
// ─── AUTH CONTEXT ATTRIBUTION ENGINE ──────────────────────────
let activeAuthContext = { trigger: 'direct' };
try {
  const cachedAuthCtx = sessionStorage.getItem('bf_auth_context');
  if (cachedAuthCtx) activeAuthContext = JSON.parse(cachedAuthCtx);
} catch (e) {}

function setAuthContext(ctx) {
  activeAuthContext = Object.assign({ trigger: 'direct' }, ctx || {});
  try {
    sessionStorage.setItem('bf_auth_context', JSON.stringify(activeAuthContext));
  } catch (e) {}
}

function getActiveAuthContext() {
  try {
    const raw = sessionStorage.getItem('bf_auth_context');
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return activeAuthContext || { trigger: 'direct' };
}
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  updateProfile,
  verifyBeforeUpdateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  unlink,
  linkWithPopup,
  confirmPasswordReset,
  verifyPasswordResetCode,
  getAdditionalUserInfo,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  collection,
  deleteDoc,
  query,
  where,
  limit,
  orderBy,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import './membership.css';

// ── Global State & Constants ──
const USERS_COLLECTION = 'dev_users';
let auth = null;
let db = null;
let messaging = null;
let currentUser = null;
let currentToken = null;
let userDocUnsubscribe = null;
const userFollowedBrandIds = new Set();
let userSavedDeals = [];
let userSavedArticles = [];
let userRecentBrands = [];
const followedBrandsMetaCache = new Map();
let lastSyncedTheme = null;

// Initialize when configurations are loaded from localized object
const localized = window.bfwt_membershipData || {};
const firebaseConfig = localized.firebaseConfig || {};

let isInitializingFirebase = false;
let isFirebaseInitialized = false;

function initFirebase() {
  if (isFirebaseInitialized || isInitializingFirebase) return;
  if (!firebaseConfig || !firebaseConfig.apiKey) {
    console.warn(
      '[Firebase] Configuration is missing. Please define FIREBASE_WEB_CONFIG in wp-config.php.'
    );
    return;
  }

  isInitializingFirebase = true;
  try {
    const app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);

    try {
      messaging = getMessaging(app);
      onMessage(messaging, (payload) => {
        console.log('[Firebase FCM] Foreground Web Push received:', payload);
        const title = payload.notification?.title || payload.data?.title || 'BudgetFitter Alert';
        const body = payload.notification?.body || payload.data?.body || '';
        let targetUrl = payload.data?.webUrl || payload.data?.link || payload.notification?.click_action || payload.data?.click_action || '';
        
        // If app deep link, convert to web URL
        if (targetUrl && targetUrl.startsWith('budgetfitter://brand/')) {
          const slug = targetUrl.replace('budgetfitter://brand/', '').replace(/\/$/, '');
          targetUrl = `/brand/${slug}/`;
        }

        if (window.BFWT_Toast) {
          window.BFWT_Toast.info(body, {
            title: title,
            duration: 10000,
            action: targetUrl ? {
              label: 'View Deal ➔',
              url: targetUrl
            } : null
          });
        }
      });
    } catch (mErr) {
      console.warn('[Firebase] Messaging initialization error:', mErr);
    }

    // Listen for Authentication state changes
    onAuthStateChanged(auth, handleAuthStateChanged);
    isFirebaseInitialized = true;
  } catch (e) {
    console.error('[Firebase] Initialization error:', e);
  } finally {
    isInitializingFirebase = false;
  }
}

/**
 * Ensures a complete, rich user document exists in Firestore and triggers backend sync.
 * Guarantees email, created_at, display_name, and newsletter preferences are recorded.
 */
async function ensureUserProfileInFirestore(user, extraData = {}, isNewUser = false) {
  if (!user || !db) return;
  try {
    const userDocRef = doc(db, USERS_COLLECTION, user.uid);
    let docExists = false;
    let existingData = null;

    try {
      const snap = await getDoc(userDocRef);
      if (snap.exists()) {
        docExists = true;
        existingData = snap.data();
        if (existingData?.account_status === 'disabled') {
          console.warn('[Firebase] Account is disabled/scheduled for deletion. Signing out.');
          await signOut(auth);
          if (window.BFWT_Toast) {
            window.BFWT_Toast.error('This account is scheduled for deletion and has been disabled.');
          }
          return;
        }
      }
    } catch (readErr) {
      console.warn('[Firebase] Profile read check warning:', readErr);
    }

    const emailVal = (user.email || extraData.email || existingData?.email || '').trim().toLowerCase();
    const displayNameVal = user.displayName || extraData.displayName || existingData?.display_name || null;
    const photoURLVal = user.photoURL || extraData.photoURL || existingData?.photo_url || null;

    const profileData = {
      email: emailVal,
      display_name: displayNameVal,
      photo_url: photoURLVal,
      email_verified: !!user.emailVerified,
      signup_source: existingData?.signup_source || extraData.signup_source || 'web',
      last_sign_in_at: serverTimestamp(),
      disabled: false,
    };

    // If new user or document didn't exist or created_at was missing, write created_at
    if (isNewUser || !docExists || !existingData?.created_at) {
      profileData.created_at = serverTimestamp();
    }

    // Default newsletter subscription to true if not explicitly set
    if (!docExists || typeof existingData?.newsletter_subscribed === 'undefined') {
      profileData.newsletter_subscribed = true;
    }

    // Notification preferences v2
    if (!docExists || !existingData?.notification_preferences_v2) {
      profileData.notification_preferences_v2 = {
        web_enabled: true,
        enabled: true,
        muted_brands: [],
        last_updated: new Date().toISOString(),
      };
    }

    Object.assign(profileData, extraData);

    // Filter out undefined keys
    Object.keys(profileData).forEach(
      (key) => profileData[key] === undefined && delete profileData[key]
    );

    await setDoc(userDocRef, profileData, { merge: true });
    console.log('[Firebase] User profile successfully recorded in Firestore for:', emailVal);

    // Call initializeNewUser Cloud Function as a secondary backend guarantee
    if (emailVal) {
      try {
        fetch('https://europe-west1-budgetfitter.cloudfunctions.net/initializeNewUser', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid: user.uid,
            email: emailVal,
            displayName: displayNameVal,
            photoURL: photoURLVal,
            emailVerified: !!user.emailVerified,
            disabled: false,
          }),
        }).catch(() => {});
      } catch (_) {}
    }
  } catch (err) {
    console.error('[Firebase] Error ensuring user profile in Firestore:', err);
  }
}

// Check if user has an existing session or if this page requires immediate auth
function shouldInitFirebaseImmediately() {
  try {
    // 1. Logged-in session cookie
    if (document.cookie.includes('wordpress_logged_in_firebase=1')) return true;
    if (document.cookie.includes('bfwt_auth_email=')) return true;
    if (document.cookie.includes('bfwt_auth_name=')) return true;

    // 2. Firebase Auth persistence in localStorage
    if (typeof localStorage !== 'undefined') {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('firebase:authUser') || key.startsWith('bfwt_auth'))) {
          return true;
        }
      }
    }

    // 3. URL parameters or hash indicating auth intent
    const urlParams = new URLSearchParams(window.location.search);
    if (
      urlParams.has('auth') ||
      urlParams.has('open') ||
      urlParams.get('open_account') === '1' ||
      urlParams.get('open_auth') === '1' ||
      urlParams.get('mode') === 'resetPassword' ||
      urlParams.has('oobCode') ||
      window.location.hash === '#login' ||
      window.location.hash === '#accountSheet' ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('bfwt_open_account_hub'))
    ) {
      return true;
    }

    // 4. Password reset form or Vault Hub in DOM
    if (
      document.getElementById('bfwt-reset-password-form') ||
      document.getElementById('bf-saved-page-member-view') ||
      window.location.pathname.startsWith('/vault')
    ) {
      return true;
    }
  } catch (err) {}

  return false;
}

if (shouldInitFirebaseImmediately()) {
  initFirebase();
} else {
  // Pre-warm on interaction or idle
  const _triggerAuthInit = () => {
    initFirebase();
  };

  // 1. Delegated listener for clicks and touches on any auth / account / follow triggers
  document.addEventListener(
    'click',
    (e) => {
      if (
        e.target.closest(
          '#bfwt-account-trigger, [data-sheet-target="accountSheet"], [data-sheet-target="sheet-saved"], #bfwt-saved-trigger, #bfwt-search-trigger, [data-sheet-target="searchSheet"], .bfwt-watchlist-icon, .bfwt-save-deal-btn, .bfwt-save-article-btn, .bf-auth-trigger, #accountSheet form'
        )
      ) {
        _triggerAuthInit();
      }
    },
    { capture: true }
  );

  // 2. Pre-warm slightly on hover/touch before click finishes
  const accountTriggerEl = document.getElementById('bfwt-account-trigger');
  if (accountTriggerEl) {
    accountTriggerEl.addEventListener('pointerenter', _triggerAuthInit, { once: true, passive: true });
    accountTriggerEl.addEventListener('touchstart', _triggerAuthInit, { once: true, passive: true });
  }

  // Purely interaction-driven for anonymous guests
}

// Helper: Apply site theme, update toggles & localStorage
function applyTheme(theme) {
  if (theme !== 'dark' && theme !== 'light') return;
  document.documentElement.setAttribute('data-color-mode', theme);
  try {
    localStorage.setItem('bfwt-color-mode', theme);
  } catch (e) {}
  document.cookie =
    'theme=' + theme + '; path=/; max-age=31536000; Secure; SameSite=Lax';

  // Sync theme checkbox states
  const checked = theme === 'dark';
  const selectors =
    '#darkModeToggle, #footerDarkModeToggle, #bfwt-theme-checkbox';
  document.querySelectorAll(selectors).forEach((el) => {
    el.checked = checked;
  });

  // Dispatch custom theme change event
  window.dispatchEvent(
    new CustomEvent('bfwt:themechange', { detail: { theme } })
  );
  if (window.BFWT_Accessibility) {
    window.BFWT_Accessibility.announce('Theme set to ' + theme + ' mode');
  }
}

// ── Authentication State Handler ──
async function handleAuthStateChanged(user) {
  currentUser = user;

  const accountTrigger = document.getElementById('bfwt-account-trigger');
  const accountTriggerLabel = document.getElementById(
    'bfwt-account-trigger-label'
  );
  const accountTriggerIcon = document.getElementById(
    'bfwt-account-trigger-icon'
  );

  const loggedInView = document.getElementById('bfwt-account-logged-in-view');
  const loggedOutView = document.getElementById('bfwt-account-logged-out-view');

  if (user) {
    // Set bypass cookie for RunCache (Nginx) and Cloudflare
    document.cookie =
      'wordpress_logged_in_firebase=1; path=/; max-age=31536000; Secure; SameSite=Lax';

    let firstName = user.displayName
      ? user.displayName.split(' ')[0]
      : 'Account';
    if (firstName.length > 8) {
      firstName = firstName.substring(0, 8) + '…';
    }
    document.cookie =
      'bfwt_auth_name=' +
      encodeURIComponent(firstName) +
      '; path=/; max-age=31536000; Secure; SameSite=Lax';

    if (user.displayName) {
      document.cookie =
        'bfwt_auth_fullname=' +
        encodeURIComponent(user.displayName) +
        '; path=/; max-age=31536000; Secure; SameSite=Lax';
    }
    if (user.email) {
      document.cookie =
        'bfwt_auth_email=' +
        encodeURIComponent(user.email) +
        '; path=/; max-age=31536000; Secure; SameSite=Lax';
    }

    try {
      currentToken = await user.getIdToken();
    } catch (err) {
      console.error('Failed to get user ID token', err);
      currentToken = null;
    }

    // 1. Update Header Button trigger target to accountSheet
    if (accountTrigger) {
      accountTrigger.setAttribute('data-sheet-target', 'accountSheet');
      accountTrigger.setAttribute('aria-controls', 'accountSheet');

      if (accountTriggerLabel) {
        accountTriggerLabel.textContent = firstName;
      }
      // Keep custom signature FitterPass SVG in #bfwt-account-trigger-icon
    }

    // 2. Toggle Sheet Views
    if (loggedInView) {
      loggedInView.classList.remove('tw-hidden');
      loggedInView.classList.add('tw-flex');
    }
    if (loggedOutView) {
      loggedOutView.classList.remove('tw-flex');
      loggedOutView.classList.add('tw-hidden');
    }

    try {
      const pendingPanel = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('bfwt_open_account_panel') : null;
      if (pendingPanel) {
        sessionStorage.removeItem('bfwt_open_account_panel');
        setTimeout(() => {
          if (window.BFWT_Membership && typeof window.BFWT_Membership.openPanel === 'function') {
            window.BFWT_Membership.openPanel(pendingPanel);
          }
        }, 180);
      }
    } catch (_) {}

    // 3. Populate Account Sheet details
    const avatarEl = document.getElementById('bfwt-account-avatar');
    const nameEl = document.getElementById('bfwt-account-name');
    const emailEl = document.getElementById('bfwt-account-email');
    const nameLabelEl = document.getElementById('bfwt-account-name-label');
    const emailLabelEl = document.getElementById('bfwt-account-email-label');
    const editNameInput = document.getElementById('bfwt-edit-name-input');
    const editEmailInput = document.getElementById('bfwt-edit-email-input');
    const emailPasswordInput = document.getElementById(
      'bfwt-email-password-input'
    );
    const emailChangeToggle = document.getElementById(
      'bfwt-email-change-toggle'
    );
    const googleStatus = document.getElementById('bfwt-google-status');
    const googleLinkBtn = document.getElementById('bfwt-google-link-btn');
    const appleStatus = document.getElementById('bfwt-apple-status');
    const appleLinkBtn = document.getElementById('bfwt-apple-link-btn');
    const greetingNameEl = document.getElementById('bfwt-greeting-name');
    const greetingEmailEl = document.getElementById('bfwt-greeting-email');

    if (nameEl) nameEl.textContent = user.displayName || 'BudgetFitter User';
    if (emailEl) emailEl.textContent = user.email || '';
    if (nameLabelEl)
      {nameLabelEl.textContent = user.displayName || 'BudgetFitter User';}
    if (emailLabelEl) emailLabelEl.textContent = user.email || '';
    if (greetingNameEl) greetingNameEl.textContent = user.displayName || 'User';
    if (greetingEmailEl) greetingEmailEl.textContent = user.email || '—';

    if (editNameInput) editNameInput.value = user.displayName || '';
    if (editEmailInput) editEmailInput.value = user.email || '';
    if (emailPasswordInput) emailPasswordInput.value = '';

    const isPasswordUser = user.providerData.some(
      (p) => p.providerId === 'password'
    );
    const isGoogleUser = user.providerData.some(
      (p) => p.providerId === 'google.com'
    );
    const isAppleUser = user.providerData.some(
      (p) => p.providerId === 'apple.com'
    );

    // Update Google Account link status & button action
    if (googleStatus) {
      googleStatus.textContent = isGoogleUser ? 'Connected' : 'Not connected';
      if (isGoogleUser) {
        googleStatus.classList.remove('tw-text-secondary');
        googleStatus.classList.add('tw-text-accent', 'tw-font-medium');
      } else {
        googleStatus.classList.remove('tw-text-accent', 'tw-font-medium');
        googleStatus.classList.add('tw-text-secondary');
      }
    }
    if (googleLinkBtn) {
      googleLinkBtn.textContent = isGoogleUser ? 'Disconnect' : 'Connect';
    }

    // Update Apple Account link status & button action
    if (appleStatus) {
      appleStatus.textContent = isAppleUser ? 'Connected' : 'Not connected';
      if (isAppleUser) {
        appleStatus.classList.remove('tw-text-secondary');
        appleStatus.classList.add('tw-text-accent', 'tw-font-medium');
      } else {
        appleStatus.classList.remove('tw-text-accent', 'tw-font-medium');
        appleStatus.classList.add('tw-text-secondary');
      }
    }
    if (appleLinkBtn) {
      appleLinkBtn.textContent = isAppleUser ? 'Disconnect' : 'Connect';
    }

    // Show/hide email Change edit button
    if (emailChangeToggle) {
      if (isPasswordUser) {
        emailChangeToggle.classList.remove('tw-hidden');
      } else {
        emailChangeToggle.classList.add('tw-hidden');
      }
    }

    // Show/hide email verification warning alert banner
    const verificationAlert = document.getElementById(
      'bfwt-verification-alert'
    );
    if (verificationAlert) {
      if (isPasswordUser && !user.emailVerified) {
        verificationAlert.classList.remove('tw-hidden');
        verificationAlert.classList.add('tw-flex');
      } else {
        verificationAlert.classList.remove('tw-flex');
        verificationAlert.classList.add('tw-hidden');
      }
    }

    // Set theme checkbox state on load
    const themeCheckbox = document.getElementById('bfwt-theme-checkbox');
    if (themeCheckbox) {
      const isDark =
        document.documentElement.getAttribute('data-color-mode') === 'dark';
      themeCheckbox.checked = isDark;
    }

    // Hide inline forms & reset toggles
    const nameForm = document.getElementById('bfwt-name-edit-form');
    const nameToggle = document.getElementById('bfwt-name-edit-toggle');
    if (nameForm) {
      nameForm.classList.add('tw-hidden');
      nameForm.classList.remove('tw-flex');
    }
    if (nameToggle) nameToggle.classList.remove('tw-hidden');

    const emailForm = document.getElementById('bfwt-email-change-form');
    if (emailForm) {
      emailForm.classList.add('tw-hidden');
      emailForm.classList.remove('tw-flex');
    }
    if (emailChangeToggle && isPasswordUser) {
      emailChangeToggle.classList.remove('tw-hidden');
    }

    if (avatarEl && user.photoURL) {
      avatarEl.innerHTML = `<img src="${user.photoURL}" alt="${user.displayName || 'User'}" class="tw-w-full tw-h-full tw-object-cover tw-rounded-full">`;
    } else if (avatarEl) {
      const initials = (user.displayName || user.email || 'U')
        .charAt(0)
        .toUpperCase();
      avatarEl.innerHTML = `<span class="tw-font-bold">${initials}</span>`;
    }

    // 4. Start user document listener & In-App Notifications listener
    startUserDocSync(user.uid);
    startWatchlistSync(user.uid);
    setupInAppNotificationsListener(user);
    ensureUserProfileInFirestore(user, {}, false);
    loadFitterPassHub(user);
    initWebFitterPassActivityTracking(user);

    // 5. Auto-sync or prompt Web Push token on login / load
    if ('Notification' in window && (Notification.permission === 'granted' || Notification.permission === 'default')) {
      setTimeout(() => {
        syncWebPushTokenOnLoad(user);
      }, 1000);
    }

    // 5. Toggle Newsletter & Saved Hub page views if on those pages
    const guestView = document.getElementById('bfwt-newsletter-guest-view');
    const memberView = document.getElementById('bfwt-newsletter-member-view');
    const memberEmail = document.getElementById('bfwt-newsletter-member-email');
    if (guestView) guestView.classList.add('tw-hidden');
    if (memberView) memberView.classList.remove('tw-hidden');
    if (memberEmail) memberEmail.textContent = user.email || '';

    const savedPageGuest = document.getElementById('bf-saved-page-guest-view');
    const savedPageMember = document.getElementById('bf-saved-page-member-view');
    if (savedPageGuest) {
      savedPageGuest.classList.add('tw-hidden');
      savedPageGuest.classList.remove('tw-block');
    }
    if (savedPageMember) {
      savedPageMember.classList.remove('tw-hidden');
      savedPageMember.classList.add('tw-block');
    }
  } else {
    // Thoroughly clear all session and profile cookies across domain variations
    const clearAuthCookie = (cName) => {
      document.cookie = cName + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax';
      const host = window.location.hostname;
      const parts = host.split('.');
      if (parts.length >= 2) {
        const rootDom = '.' + parts.slice(-2).join('.');
        document.cookie = cName + '=; path=/; domain=' + rootDom + '; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax';
      }
    };

    clearAuthCookie('wordpress_logged_in_firebase');
    clearAuthCookie('bfwt_auth_name');
    clearAuthCookie('bfwt_auth_fullname');
    clearAuthCookie('bfwt_auth_email');
    clearAuthCookie('bfwt_auth_uid');
    clearAuthCookie('bfwt_favorite_brands');

    currentToken = null;
    stopUserDocSync();
    stopWatchlistSync();
    setupInAppNotificationsListener(null);

    // Reset verification warning alert
    const verificationAlert = document.getElementById(
      'bfwt-verification-alert'
    );
    if (verificationAlert) {
      verificationAlert.classList.remove('tw-flex');
      verificationAlert.classList.add('tw-hidden');
    }

    // Reset Connected Accounts status
    const googleStatus = document.getElementById('bfwt-google-status');
    const googleLinkBtn = document.getElementById('bfwt-google-link-btn');
    if (googleStatus) {
      googleStatus.textContent = '—';
      googleStatus.classList.remove('tw-text-accent', 'tw-font-medium');
      googleStatus.classList.add('tw-text-secondary');
    }
    if (googleLinkBtn) {
      googleLinkBtn.textContent = 'Connect';
    }

    // Reset Header Button trigger target to accountSheet
    if (accountTrigger) {
      accountTrigger.setAttribute('data-sheet-target', 'accountSheet');
      accountTrigger.setAttribute('aria-controls', 'accountSheet');

      if (accountTriggerLabel) accountTriggerLabel.textContent = 'Sign up';
      // Keep custom signature SVG in #bfwt-account-trigger-icon
    }

    // Toggle Sheet Views
    if (loggedInView) {
      loggedInView.classList.remove('tw-flex');
      loggedInView.classList.add('tw-hidden');
    }
    if (loggedOutView) {
      loggedOutView.classList.remove('tw-hidden');
      loggedOutView.classList.add('tw-flex');
    }

    // Reset auth forms to login view only if accountSheet is not currently active
    const activeAccountSheet = document.getElementById('accountSheet');
    if (!activeAccountSheet || !activeAccountSheet.classList.contains('is-active')) {
      showAuthView('login');
    }

    // Toggle Newsletter & Saved Hub page views if on those pages
    const guestView = document.getElementById('bfwt-newsletter-guest-view');
    const memberView = document.getElementById('bfwt-newsletter-member-view');
    if (guestView) guestView.classList.remove('tw-hidden');
    if (memberView) memberView.classList.add('tw-hidden');

    const savedPageGuest = document.getElementById('bf-saved-page-guest-view');
    const savedPageMember = document.getElementById('bf-saved-page-member-view');
    if (savedPageGuest) {
      savedPageGuest.classList.remove('tw-hidden');
      savedPageGuest.classList.add('tw-block');
    }
    if (savedPageMember) {
      savedPageMember.classList.add('tw-hidden');
      savedPageMember.classList.remove('tw-block');
    }
  }

  // Dispatch global auth change event
  window.dispatchEvent(
    new CustomEvent('bfwt:authchange', {
      detail: { user, token: currentToken },
    })
  );
}

// ── Firestore User Document Sync (Real-time newsletter checkbox, theme, saved deals & articles sync) ──
function isSavedDealExpired(expStr) {
  if (!expStr || typeof expStr !== 'string' || !expStr.trim()) return false;
  const trimmed = expStr.trim();
  const parsed = new Date(trimmed.length <= 10 ? `${trimmed}T23:59:59` : trimmed);
  if (isNaN(parsed.getTime())) return false;
  return parsed.getTime() < Date.now();
}

function escapeHtmlSafe(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function updateThemeModeButtonsUI(modePref) {
  const normalized = (modePref === 'dark' || modePref === 'light' || modePref === 'system') ? modePref : 'system';
  document.querySelectorAll('.bfwt-theme-mode-btn').forEach((btn) => {
    const btnMode = btn.getAttribute('data-theme-pref');
    const isActive = btnMode === normalized;
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    if (isActive) {
      btn.classList.add('tw-border-accent', 'tw-bg-accent/[0.08]', 'tw-text-accent');
      btn.classList.remove('tw-border-border-subtle', 'tw-bg-surface', 'tw-text-secondary');
    } else {
      btn.classList.remove('tw-border-accent', 'tw-bg-accent/[0.08]', 'tw-text-accent');
      btn.classList.add('tw-border-border-subtle', 'tw-bg-surface', 'tw-text-secondary');
    }
  });
}

const hydratedDealIds = new Set();
const savedDealCardHtmlCache = new Map();
let isHydratingSavedDeals = false;

async function hydrateIncompleteSavedDeals() {
  if (isHydratingSavedDeals) return;
  const needCardHtml = Boolean(
    document.getElementById('bfwt-saved-page-deals-list') ||
    document.getElementById('bfwt-header-saved-deals-list')
  );
  const incompleteIds = userSavedDeals
    .map((d) => {
      const id = Number(d?.id || 0);
      if (id <= 0) return 0;
      if (!hydratedDealIds.has(id)) return id;
      if (needCardHtml && !savedDealCardHtmlCache.has(id)) return id;
      return 0;
    })
    .filter((id) => id > 0);

  if (incompleteIds.length === 0) return;

  isHydratingSavedDeals = true;
  incompleteIds.forEach((id) => hydratedDealIds.add(id));

  try {
    const res = await fetch(`/wp-json/app-api/v1/saved-hydration?deal_ids=${encodeURIComponent(incompleteIds.slice(0, 50).join(','))}`);
    if (!res.ok) return;
    const json = await res.json();
    const serverDeals = Array.isArray(json?.deals) ? json.deals : [];
    const mapById = new Map();
    serverDeals.forEach((sd) => {
      if (sd && sd.id) {
        const numId = Number(sd.id);
        mapById.set(numId, sd);
        if (sd.card_html) {
          savedDealCardHtmlCache.set(numId, sd.card_html);
        }
      }
    });

    let firestoreChanged = false;
    let uiNeedsRefresh = serverDeals.length > 0;

    // Also prune any requested deal IDs that no longer exist on the live server (trashed/expired)
    const beforeCount = userSavedDeals.length;
    userSavedDeals = userSavedDeals
      .filter((d) => {
        const id = Number(d?.id || 0);
        if (incompleteIds.includes(id) && !mapById.has(id)) {
          firestoreChanged = true;
          uiNeedsRefresh = true;
          return false;
        }
        return true;
      })
      .map((d) => {
        const id = Number(d?.id || 0);
        const sd = mapById.get(id);
        if (!sd) return d;
        const oldTitle = d.title || '';
        const oldBrand = d.brand_name || '';
        if (oldTitle !== sd.title || oldBrand !== sd.brand_name || !oldTitle || oldTitle === 'Special Offer') {
          firestoreChanged = true;
        }
        return {
          ...d,
          id,
          title: sd.title || d.title,
          description: sd.description || d.description || '',
          type: sd.type || d.type || 'deal',
          code: sd.code || d.code || null,
          referral_link: sd.referral_link || d.referral_link || null,
          expiration_date: sd.expiration_date || d.expiration_date || null,
          brand_id: sd.brand_id || d.brand_id || null,
          brand_name: sd.brand_name || d.brand_name || null,
          brand_slug: sd.brand_slug || d.brand_slug || null,
          brand_logo: sd.brand_logo || d.brand_logo || null,
        };
      });

    if (userSavedDeals.length !== beforeCount) {
      firestoreChanged = true;
    }

    if (uiNeedsRefresh) {
      syncSavedDealsUI();
    }
    if (firestoreChanged) {
      const activeUser =
        (typeof currentUser !== 'undefined' && currentUser) ||
        (typeof auth !== 'undefined' && auth ? auth.currentUser : null);
      if (activeUser && db) {
        setDoc(
          doc(db, USERS_COLLECTION, activeUser.uid),
          { saved_deals: userSavedDeals },
          { merge: true }
        ).catch(() => {});
      }
    }
  } catch (_) {
  } finally {
    isHydratingSavedDeals = false;
  }
}

let isHydratingSavedArticles = false;
const hydratedArticleKeys = new Set();
const savedArticleCardHtmlCache = new Map();

function confirmUnsaveModal({
  title = 'Remove from Your Vault?',
  message = 'Are you sure you want to remove this item from your Vault?',
  confirmText = 'Remove from Vault',
  cancelText = 'Keep in Vault',
  icon = 'remove_saved',
}) {
  return new Promise((resolve) => {
    const existing = document.getElementById('bfwt-unsave-confirm-modal');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'bfwt-unsave-confirm-modal';
    overlay.className = 'tw-fixed tw-inset-0 tw-z-[100000] tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/55 tw-backdrop-blur-md';
    overlay.innerHTML = `
      <div class="tw-w-full tw-max-w-sm tw-rounded-[22px] tw-bg-paper/92 dark:tw-bg-[#16191E]/92 tw-backdrop-blur-2xl tw-backdrop-saturate-[180%] tw-border tw-border-line/70 dark:tw-border-white/[0.14] tw-shadow-[0_24px_56px_rgba(0,0,0,0.38)] tw-p-5 sm:tw-p-6 tw-text-left" role="dialog" aria-modal="true">
        <div class="tw-flex tw-items-center tw-gap-3.5 tw-mb-3.5">
          <span class="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-red-500/10 tw-border tw-border-red-500/25 tw-text-red-500 tw-flex tw-items-center tw-justify-center tw-shrink-0">
            <span class="material-symbols-rounded tw-text-[20px]">${escapeHtmlSafe(icon)}</span>
          </span>
          <h3 class="tw-font-heading tw-text-base sm:tw-text-lg tw-font-bold tw-text-ink tw-m-0">${escapeHtmlSafe(title)}</h3>
        </div>
        <p class="tw-font-base tw-text-xs sm:tw-text-[13.5px] tw-text-secondary tw-leading-relaxed tw-m-0 tw-mb-5">${escapeHtmlSafe(message)}</p>
        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2.5">
          <button type="button" data-confirm-action="cancel" class="tw-px-4 tw-py-2.5 tw-rounded-xl tw-border tw-border-line/70 dark:tw-border-white/[0.12] tw-bg-surface/80 hover:tw-bg-ink/[0.06] tw-text-xs sm:tw-text-[13px] tw-font-heading tw-font-bold tw-text-secondary hover:tw-text-ink tw-cursor-pointer tw-transition-colors">${escapeHtmlSafe(cancelText)}</button>
          <button type="button" data-confirm-action="confirm" class="tw-px-4 tw-py-2.5 tw-rounded-xl tw-border tw-border-red-500/35 tw-bg-red-500/12 hover:tw-bg-red-500 tw-text-xs sm:tw-text-[13px] tw-font-heading tw-font-bold tw-text-red-500 hover:tw-text-white tw-cursor-pointer tw-transition-colors">${escapeHtmlSafe(confirmText)}</button>
        </div>
      </div>
    `;

    const cleanup = (result) => {
      overlay.remove();
      resolve(result);
    };

    overlay.addEventListener('click', (ev) => {
      const btn = ev.target.closest('[data-confirm-action]');
      if (btn) {
        cleanup(btn.getAttribute('data-confirm-action') === 'confirm');
      } else if (ev.target === overlay) {
        cleanup(false);
      }
    });

    document.body.appendChild(overlay);
  });
}

async function hydrateSavedArticles() {
  if (isHydratingSavedArticles || !Array.isArray(userSavedArticles) || userSavedArticles.length === 0) return;

  const unchecked = userSavedArticles.filter((a) => {
    const idKey = Number(a?.id) > 0 ? `id:${Number(a.id)}` : '';
    const slugKey = a?.slug ? `slug:${a.slug}` : '';
    const key = idKey || slugKey;
    const hasCardHtml = (idKey && savedArticleCardHtmlCache.has(idKey)) || (slugKey && savedArticleCardHtmlCache.has(slugKey));
    return key && (!hydratedArticleKeys.has(key) || !hasCardHtml);
  });
  if (unchecked.length === 0) return;

  isHydratingSavedArticles = true;
  const ids = [];
  const slugs = [];
  unchecked.forEach((a) => {
    const key = a?.id ? `id:${a.id}` : a?.slug ? `slug:${a.slug}` : '';
    if (key) hydratedArticleKeys.add(key);
    if (Number(a?.id) > 0) ids.push(Number(a.id));
    if (a?.slug) slugs.push(String(a.slug));
  });

  try {
    const params = new URLSearchParams();
    if (ids.length > 0) params.set('article_ids', ids.slice(0, 100).join(','));
    if (slugs.length > 0) params.set('article_slugs', slugs.slice(0, 100).join(','));
    const res = await fetch(`/wp-json/app-api/v1/saved-hydration?${params.toString()}`);
    if (!res.ok) return;
    const json = await res.json();
    const liveArticles = Array.isArray(json?.articles) ? json.articles : [];

    const byId = new Map();
    const bySlug = new Map();
    liveArticles.forEach((art) => {
      if (art && Number(art.id) > 0) {
        byId.set(Number(art.id), art);
        if (art.card_html) savedArticleCardHtmlCache.set(`id:${Number(art.id)}`, art.card_html);
      }
      if (art && art.slug) {
        bySlug.set(String(art.slug), art);
        if (art.card_html) savedArticleCardHtmlCache.set(`slug:${String(art.slug)}`, art.card_html);
      }
    });

    let changed = false;
    const beforeLen = userSavedArticles.length;
    userSavedArticles = userSavedArticles
      .filter((a) => {
        const id = Number(a?.id || 0);
        const slug = String(a?.slug || '');
        const wasChecked = (id > 0 && ids.includes(id)) || (slug && slugs.includes(slug));
        if (!wasChecked) return true;
        const found = (id > 0 && byId.has(id)) || (slug && bySlug.has(slug));
        if (!found) {
          changed = true;
          return false;
        }
        return true;
      })
      .map((a) => {
        const id = Number(a?.id || 0);
        const slug = String(a?.slug || '');
        const live = (id > 0 && byId.get(id)) || (slug && bySlug.get(slug));
        if (!live) return a;
        if (!a.image_url || !a.featuredImageUrl || a.title !== live.title) {
          changed = true;
        }
        return {
          ...a,
          id: live.id || id,
          slug: live.slug || slug,
          type: live.type || a.type || 'news',
          badge_label: live.badge_label || (live.type === 'review' ? 'Review' : 'News'),
          title: live.title || a.title,
          excerpt: live.excerpt || a.excerpt || '',
          image_url: live.image_url || a.image_url || a.featuredImageUrl || '',
          featuredImageUrl: live.image_url || a.featuredImageUrl || a.image_url || null,
          category_name: live.category_name || a.category_name || a.categoryName || '',
          categoryName: live.category_name || a.categoryName || a.category_name || null,
          read_time: live.read_time || a.read_time || '3 min read',
          readingTimeMinutes: live.readingTimeMinutes || a.readingTimeMinutes || 3,
          date_text: live.date_text || a.date_text || '',
          date_iso: live.date_iso || a.date_iso || a.publishedAt || '',
          publishedAt: live.date_iso || a.publishedAt || a.date_iso || '',
        };
      });

    if (userSavedArticles.length !== beforeLen) {
      changed = true;
    }
    syncSavedArticlesUI();
    if (changed) {
      const activeUser =
        (typeof currentUser !== 'undefined' && currentUser) ||
        (typeof auth !== 'undefined' && auth ? auth.currentUser : null);
      if (activeUser && db) {
        setDoc(
          doc(db, USERS_COLLECTION, activeUser.uid),
          { saved_articles: userSavedArticles },
          { merge: true }
        ).catch(() => {});
      }
    }
  } catch (_) {
  } finally {
    isHydratingSavedArticles = false;
  }
}

function updateSaveDealButtonsOnDOM(savedIds) {
  document.querySelectorAll('.bfwt-save-deal-btn').forEach((btn) => {
    const dealId = Number(btn.getAttribute('data-deal-id') || 0);
    const isSaved = savedIds.has(dealId);
    const iconEl = btn.querySelector('.bfwt-save-deal-icon') || btn.querySelector('.material-symbols-rounded');
    btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    btn.setAttribute('title', isSaved ? 'Remove from Saved Deals' : 'Save Deal for Later');
    if (isSaved) {
      btn.classList.add('!tw-border-accent/40', '!tw-bg-accent/10', '!tw-text-accent');
      if (iconEl) {
        iconEl.textContent = 'saved_item';
        if (window.bfIcon) {
          iconEl.removeAttribute('data-bf-icon');
          iconEl.innerHTML = '';
          iconEl.textContent = 'saved_item';
        }
      }
    } else {
      btn.classList.remove('!tw-border-accent/40', '!tw-bg-accent/10', '!tw-text-accent');
      if (iconEl) {
        iconEl.textContent = 'save_item';
        if (window.bfIcon) {
          iconEl.removeAttribute('data-bf-icon');
          iconEl.innerHTML = '';
          iconEl.textContent = 'save_item';
        }
      }
    }
  });
}

const activeLazyObservers = new WeakMap();

function mountProgressiveList(containerEl, items, renderItemFn, pageSize = 12, onBatchRendered = null) {
  if (!containerEl) return;
  const prevObs = activeLazyObservers.get(containerEl);
  if (prevObs) {
    prevObs.disconnect();
    activeLazyObservers.delete(containerEl);
  }
  containerEl.innerHTML = '';
  let cursor = 0;
  let isBatchLoading = false;

  const sentinel = document.createElement('div');
  sentinel.className = 'tw-col-span-full tw-w-full tw-min-h-[16px] tw-py-4 tw-flex tw-justify-center';

  const renderLoadMoreBtn = () => {
    if (cursor >= items.length) {
      sentinel.innerHTML = '';
      return;
    }
    sentinel.innerHTML = `
      <button type="button" class="bfwt-load-more-btn" data-bfwt-progressive-more>
        <span>Load More</span>
        <span class="bfwt-load-more-btn__count">${Math.min(cursor, items.length)} / ${items.length}</span>
      </button>
    `;
    const btn = sentinel.querySelector('[data-bfwt-progressive-more]');
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        appendNextBatch(true);
      });
    }
  };

  const appendNextBatch = (withSignatureEffect = false) => {
    if (isBatchLoading) return;
    const slice = items.slice(cursor, cursor + pageSize);
    if (slice.length === 0) {
      if (sentinel.parentNode) sentinel.remove();
      return;
    }
    const commitSlice = () => {
      cursor += slice.length;
      const html = slice.map(renderItemFn).join('');
      if (sentinel.parentNode === containerEl) {
        sentinel.insertAdjacentHTML('beforebegin', html);
      } else {
        containerEl.insertAdjacentHTML('beforeend', html);
      }
      isBatchLoading = false;
      if (withSignatureEffect && window.BFWT_EdgeAura) {
        window.BFWT_EdgeAura.hide(240);
      }
      if (typeof onBatchRendered === 'function') {
        onBatchRendered();
      }
      if (cursor >= items.length) {
        if (sentinel.parentNode) sentinel.remove();
        const obs = activeLazyObservers.get(containerEl);
        if (obs) {
          obs.disconnect();
          activeLazyObservers.delete(containerEl);
        }
      } else {
        renderLoadMoreBtn();
      }
    };

    if (withSignatureEffect) {
      isBatchLoading = true;
      if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.show();
      if (window.BFWT_SignatureLoaderHtml) {
        sentinel.innerHTML = window.BFWT_SignatureLoaderHtml('Loading more');
      }
      setTimeout(commitSlice, 380);
    } else {
      commitSlice();
    }
  };

  containerEl.appendChild(sentinel);
  appendNextBatch(false);
}

function syncSavedDealsUI() {
  const savedIds = new Set(
    userSavedDeals.map((d) => Number(d.id)).filter((id) => id > 0)
  );

  // Always validate & hydrate saved deals so header Vault badge and /vault/ page stay 100% in sync
  hydrateIncompleteSavedDeals();

  // 1. Update all deal card bookmark buttons on page
  updateSaveDealButtonsOnDOM(savedIds);

  // 2. Update badge counts in Header Saved Sheet & Full /saved/ Page
  ['bfwt-saved-deals-count', 'bfwt-header-saved-deals-count', 'bfwt-saved-page-deals-count'].forEach((badgeId) => {
    const countBadge = document.getElementById(badgeId);
    if (countBadge) {
      countBadge.textContent = String(userSavedDeals.length);
      if (badgeId === 'bfwt-saved-deals-count') {
        if (userSavedDeals.length > 0) {
          countBadge.classList.remove('tw-hidden');
        } else {
          countBadge.classList.add('tw-hidden');
        }
      }
    }
  });

  // 3. Render canonical Deal Cards inside Header Saved Sheet (preview max 4) & Full /saved/ Page (all)
  const emptyEl = document.getElementById('bfwt-saved-deals-empty');
  const headerListEl = document.getElementById('bfwt-header-saved-deals-list');
  const pageListEl = document.getElementById('bfwt-saved-page-deals-list');

  if (!headerListEl && !pageListEl) return;

  if (userSavedDeals.length === 0) {
    if (emptyEl) emptyEl.classList.remove('tw-hidden');
    const emptyHtml = `
      <div class="tw-text-center tw-py-9 tw-px-5 tw-rounded-2xl tw-border tw-border-dashed tw-border-border-subtle tw-bg-surface/40">
        <span class="tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-rounded-2xl tw-bg-ink/[0.04] dark:tw-bg-white/[0.05] tw-border tw-border-line/50 tw-flex tw-items-center tw-justify-center tw-text-secondary">
          <span class="material-symbols-rounded tw-text-[24px]">vault_deals</span>
        </span>
        <p class="tw-text-sm sm:tw-text-base tw-font-heading tw-font-bold tw-text-primary tw-mb-1">No Saved Deals in Your Vault</p>
        <p class="tw-text-xs sm:tw-text-[13px] tw-text-secondary tw-max-w-md tw-mx-auto tw-mb-4 tw-leading-relaxed">Tap the Vault bookmark icon on any voucher code or deal card across BudgetFitter to keep it ready here for checkout.</p>
        <a href="/brand/" class="bf-button bf-button-secondary !tw-h-9 !tw-px-4 !tw-text-xs tw-no-underline">Browse Live Perks</a>
      </div>
    `;
    [headerListEl, pageListEl].filter(Boolean).forEach((el) => {
      el.classList.remove('tw-hidden');
      el.innerHTML = emptyHtml;
    });
    return;
  }

  if (emptyEl) emptyEl.classList.add('tw-hidden');

  const renderDealCardItem = (deal) => {
    const id = Number(deal.id || 0);
    const canonicalCardHtml = savedDealCardHtmlCache.get(id);
    if (canonicalCardHtml) {
      return canonicalCardHtml;
    }
    return `
      <div class="tw-p-5 tw-rounded-card tw-border tw-border-line/40 tw-bg-paper tw-animate-pulse tw-flex tw-flex-col tw-gap-3">
        <div class="tw-h-5 tw-w-28 tw-bg-ink/10 tw-rounded-full"></div>
        <div class="tw-h-6 tw-w-3/4 tw-bg-ink/10 tw-rounded"></div>
        <div class="tw-h-4 tw-w-1/2 tw-bg-ink/5 tw-rounded"></div>
      </div>
    `;
  };

  if (headerListEl) {
    const previewLimit = 4;
    headerListEl.classList.remove('tw-hidden');
    headerListEl.innerHTML = userSavedDeals.slice(0, previewLimit).map(renderDealCardItem).join('');
  }

  if (pageListEl) {
    pageListEl.classList.remove('tw-hidden');
    mountProgressiveList(pageListEl, userSavedDeals, renderDealCardItem, 8, () => {
      updateSaveDealButtonsOnDOM(savedIds);
    });
  } else {
    updateSaveDealButtonsOnDOM(savedIds);
  }
}

function syncSavedArticlesUI() {
  const savedKeys = new Set(
    userSavedArticles.map((a) => `${a.type || 'news'}:${a.id || a.slug}`)
  );
  const savedSlugs = new Set(
    userSavedArticles.map((a) => String(a.slug || '')).filter(Boolean)
  );

  // 1. Update article save buttons on page
  document.querySelectorAll('.bfwt-save-article-btn').forEach((btn) => {
    const id = Number(btn.getAttribute('data-article-id') || 0);
    const slug = btn.getAttribute('data-article-slug') || '';
    const type = btn.getAttribute('data-article-type') || 'news';
    const isSaved = savedKeys.has(`${type}:${id}`) || (slug && savedSlugs.has(slug));
    const iconEl = btn.querySelector('.bfwt-save-article-icon');
    const labelEl = btn.querySelector('.bfwt-save-article-label');

    btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    if (isSaved) {
      btn.classList.add('!tw-border-accent/40', '!tw-bg-accent/10', '!tw-text-accent');
      if (iconEl) {
        iconEl.removeAttribute('data-bf-icon');
        iconEl.innerHTML = '';
        iconEl.textContent = 'saved_item';
      }
      if (labelEl) labelEl.textContent = 'Saved in Vault';
      btn.setAttribute('title', 'Remove from your Vault');
    } else {
      btn.classList.remove('!tw-border-accent/40', '!tw-bg-accent/10', '!tw-text-accent');
      if (iconEl) {
        iconEl.removeAttribute('data-bf-icon');
        iconEl.innerHTML = '';
        iconEl.textContent = 'save_item';
      }
      if (labelEl) labelEl.textContent = 'Save to Vault';
      btn.setAttribute('title', 'Save to your Vault');
    }
  });

  // 2. Update badge counts in Header Saved Sheet & Full /saved/ Page
  ['bfwt-saved-articles-count', 'bfwt-header-saved-pulse-count', 'bfwt-saved-page-pulse-count'].forEach((badgeId) => {
    const countBadge = document.getElementById(badgeId);
    if (countBadge) {
      countBadge.textContent = String(userSavedArticles.length);
      if (badgeId === 'bfwt-saved-articles-count') {
        if (userSavedArticles.length > 0) {
          countBadge.classList.remove('tw-hidden');
        } else {
          countBadge.classList.add('tw-hidden');
        }
      }
    }
  });

  // 3. Render Saved Pulse list inside Header Saved Sheet (preview max 6) & Full /saved/ Page (all)
  const emptyEl = document.getElementById('bfwt-saved-articles-empty');
  const headerListEl = document.getElementById('bfwt-header-saved-pulse-list');
  const pageListEl = document.getElementById('bfwt-saved-page-pulse-list');
  const legacyListEl = document.getElementById('bfwt-saved-articles-list');

  // Hydrate live cover images and prune unpublished/deleted articles on all pages so header Vault count is exact
  hydrateSavedArticles();

  if (!headerListEl && !pageListEl && !legacyListEl) return;

  if (userSavedArticles.length === 0) {
    if (emptyEl) emptyEl.classList.remove('tw-hidden');
    const emptyHtml = `
      <div class="tw-col-span-full tw-text-center tw-py-9 tw-px-5 tw-rounded-2xl tw-border tw-border-dashed tw-border-border-subtle tw-bg-surface/40">
        <span class="tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-rounded-2xl tw-bg-ink/[0.04] dark:tw-bg-white/[0.05] tw-border tw-border-line/50 tw-flex tw-items-center tw-justify-center tw-text-secondary">
          <span class="material-symbols-rounded tw-text-[24px]">vault_guides</span>
        </span>
        <p class="tw-text-sm sm:tw-text-base tw-font-heading tw-font-bold tw-text-primary tw-mb-1">No Saved Guides in Your Vault</p>
        <p class="tw-text-xs sm:tw-text-[13px] tw-text-secondary tw-max-w-md tw-mx-auto tw-mb-4 tw-leading-relaxed">Tap "Save to Vault" on any Savings Guide or editorial feature to build your personal reading list across web and mobile.</p>
        <a href="/guides/" class="bf-button bf-button-secondary !tw-h-9 !tw-px-4 !tw-text-xs tw-no-underline">Explore Savings Guides</a>
      </div>
    `;
    [headerListEl, pageListEl, legacyListEl].filter(Boolean).forEach((el) => {
      el.classList.remove('tw-hidden');
      el.innerHTML = emptyHtml;
    });
    return;
  }

  if (emptyEl) emptyEl.classList.add('tw-hidden');


  const renderArticleCard = (item) => {
    const idKey = Number(item.id) > 0 ? `id:${Number(item.id)}` : '';
    const slugKey = item.slug ? `slug:${String(item.slug)}` : '';
    const canonicalCardHtml =
      (idKey && savedArticleCardHtmlCache.get(idKey)) ||
      (slugKey && savedArticleCardHtmlCache.get(slugKey)) ||
      item.card_html;
    if (canonicalCardHtml) {
      return canonicalCardHtml;
    }
    return `
      <div class="bfwt-grid-item">
        <div class="bfwt-skeleton-grid-card bfwt-skeleton-grid-card--vertical">
          <div class="bfwt-skeleton-grid-card__image"></div>
          <div class="bfwt-skeleton-grid-card__body">
            <div class="bfwt-skeleton-grid-card__line bfwt-skeleton-grid-card__line--title"></div>
            <div class="bfwt-skeleton-grid-card__line bfwt-skeleton-grid-card__line--text"></div>
            <div class="bfwt-skeleton-grid-card__line bfwt-skeleton-grid-card__line--short"></div>
          </div>
        </div>
      </div>
    `;
  };

  if (headerListEl) {
    const previewLimit = 6;
    headerListEl.classList.remove('tw-hidden');
    headerListEl.innerHTML = userSavedArticles.slice(0, previewLimit).map(renderArticleCard).join('');
  }

  [pageListEl, legacyListEl].filter(Boolean).forEach((el) => {
    el.classList.remove('tw-hidden');
    mountProgressiveList(el, userSavedArticles, renderArticleCard, 12);
  });
}

async function syncHeaderFollowedBrandsUI() {
  const countEl = document.getElementById('bfwt-header-followed-brands-count');
  const pageCountEl = document.getElementById('bfwt-saved-page-brands-count');
  const headerListEl = document.getElementById('bfwt-header-followed-brands-list');
  const pageListEl = document.getElementById('bfwt-saved-page-brands-list');
  const brandIds = Array.from(userFollowedBrandIds).filter(Boolean);

  // Fetch brand details (including canonical card_html) from /wp-json/app-api/v1/saved-hydration
  const missingIds = brandIds.filter((id) => {
    const cached = followedBrandsMetaCache.get(String(id));
    return !cached || !cached.serverChecked || (!cached.notFound && !cached.card_html);
  });

  if (missingIds.length > 0) {
    try {
      for (let i = 0; i < missingIds.length; i += 150) {
        const batch = missingIds.slice(i, i + 150);
        const res = await fetch(`/wp-json/app-api/v1/saved-hydration?brand_ids=${encodeURIComponent(batch.join(','))}`);
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json?.brands) ? json.brands : [];
          const returnedIds = new Set();
          items.forEach((b) => {
            if (b && b.id && b.post_type === 'brand') {
              const key = String(b.id);
              returnedIds.add(key);
              followedBrandsMetaCache.set(key, {
                ...b,
                notFound: false,
                serverChecked: true,
              });
            }
          });
          const staleBrandKeys = [];
          batch.forEach((id) => {
            const key = String(id);
            if (!returnedIds.has(key)) {
              // Mark unpublished/archived brands as notFound for current UI session,
              // but preserve the Firestore document so if republished with the same ID,
              // the user's follow relationship and notifications remain intact.
              followedBrandsMetaCache.set(key, { notFound: true, serverChecked: true });
            }
          });
        }
      }
    } catch (_) {}
  }

  // Strictly filter to published active 'brand' posts verified by the server
  const validBrandIds = brandIds.filter((id) => {
    const meta = followedBrandsMetaCache.get(String(id));
    if (!meta || !meta.serverChecked || meta.notFound) return false;
    if (meta.post_type && meta.post_type !== 'brand') return false;
    return Boolean(meta.name || meta.title);
  });

  if (countEl) countEl.textContent = String(validBrandIds.length);
  if (pageCountEl) pageCountEl.textContent = String(validBrandIds.length);

  const headerFeedCountEl = document.getElementById('bfwt-header-brand-feed-count');
  if (headerFeedCountEl) {
    const totalOffers = validBrandIds.reduce((sum, id) => {
      const m = followedBrandsMetaCache.get(String(id)) || {};
      return sum + Number(m.active_deals_count ?? m.deals_count ?? 0);
    }, 0);
    headerFeedCountEl.textContent = String(totalOffers);
  }

  const avatarsEl = document.getElementById('bfwt-header-followed-brands-avatars');
  if (avatarsEl) {
    if (validBrandIds.length === 0) {
      avatarsEl.innerHTML = '<span class="tw-font-heading tw-text-xs tw-font-bold tw-text-secondary group-hover:tw-text-ink group-hover:tw-underline">Open Followed Brands</span>';
    } else {
      const topAvatars = validBrandIds.slice(0, 5).map((id) => {
        const m = followedBrandsMetaCache.get(String(id)) || {};
        const bName = escapeHtmlSafe(m.name || m.title || 'Brand');
        const bLogo = m.logo_url || m.brand_logo_url || m.brand_logo_svg || m.icon || '';
        return bLogo
          ? `<img src="${escapeHtmlSafe(bLogo)}" alt="${bName}" class="tw-w-6 tw-h-6 tw-rounded-full tw-object-contain tw-bg-white tw-border tw-border-line/80 tw-p-0.5 tw-shrink-0" loading="lazy">`
          : `<span class="tw-w-6 tw-h-6 tw-rounded-full tw-bg-ink/[0.06] dark:tw-bg-white/[0.08] tw-border tw-border-line/70 tw-text-ink tw-text-[10px] tw-font-bold tw-flex tw-items-center tw-justify-center tw-shrink-0">${escapeHtmlSafe(bName.charAt(0))}</span>`;
      }).join('');
      const extraCount = validBrandIds.length - 5;
      const extraBadge = extraCount > 0
        ? `<span class="tw-pl-2.5 tw-font-heading tw-text-[11px] tw-font-bold tw-text-secondary">+${extraCount} more</span>`
        : `<span class="tw-pl-2.5 tw-font-heading tw-text-xs tw-font-bold tw-text-secondary group-hover:tw-text-ink group-hover:tw-underline">Manage</span>`;
      avatarsEl.innerHTML = `${topAvatars}${extraBadge}`;
    }
  }

  syncFollowedBrandsDealFeedUI(validBrandIds);

  if (!headerListEl && !pageListEl) {
    return;
  }

  if (validBrandIds.length === 0) {
    const emptyHtml = `
      <div class="tw-col-span-full tw-text-center tw-py-9 tw-px-5 tw-rounded-2xl tw-border tw-border-dashed tw-border-border-subtle tw-bg-surface/40">
        <span class="tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-rounded-2xl tw-bg-ink/[0.04] dark:tw-bg-white/[0.05] tw-border tw-border-line/50 tw-flex tw-items-center tw-justify-center tw-text-secondary">
          <span class="material-symbols-rounded tw-text-[24px]">vault_brands</span>
        </span>
        <p class="tw-text-sm sm:tw-text-base tw-font-heading tw-font-bold tw-text-primary tw-mb-1">No Followed Brands in Your Vault</p>
        <p class="tw-text-xs sm:tw-text-[13px] tw-text-secondary tw-max-w-md tw-mx-auto tw-mb-4 tw-leading-relaxed">Tap Follow on any brand page to track your favourite merchants and unlock your personalised live deal feed.</p>
        <a href="/brand/" class="bf-button bf-button-secondary !tw-h-9 !tw-px-4 !tw-text-xs tw-no-underline">Explore Brands</a>
      </div>
    `;
    if (headerListEl) headerListEl.innerHTML = emptyHtml;
    if (pageListEl) pageListEl.innerHTML = emptyHtml;
    return;
  }

  const renderBrandCard = (id) => {
    const meta = followedBrandsMetaCache.get(String(id)) || {};
    if (meta.card_html) {
      return meta.card_html;
    }
    return `
      <div class="bfwt-home-feed__skeleton">
        <div class="bfwt-home-feed__skeleton-media"></div>
        <div class="bfwt-home-feed__skeleton-body">
          <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--title"></div>
          <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--discount"></div>
          <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--intro"></div>
          <div class="bfwt-home-feed__skeleton-line bfwt-home-feed__skeleton-line--footer"></div>
        </div>
      </div>
    `;
  };

  if (headerListEl) {
    const previewLimit = 6;
    headerListEl.innerHTML = validBrandIds.slice(0, previewLimit).map(renderBrandCard).join('');
  }

  if (pageListEl) {
    mountProgressiveList(pageListEl, validBrandIds, renderBrandCard, 12);
  }
}

let lastBrandFeedCacheKey = '';
let cachedBrandFeedDeals = [];
let cachedBrandFeedTotal = 0;
let cachedBrandFeedHasMore = false;
let cachedBrandFeedNextOffset = 0;
let isFetchingBrandFeedMore = false;

async function syncFollowedBrandsDealFeedUI(validBrandIds) {
  const headerFeedCountEl = document.getElementById('bfwt-header-brand-feed-count');
  const pageFeedCountEl = document.getElementById('bfwt-saved-page-feed-count');
  const headerFeedListEl = document.getElementById('bfwt-header-brand-feed-list');
  const pageFeedListEl = document.getElementById('bfwt-saved-page-feed-list');

  if (!headerFeedCountEl && !pageFeedCountEl && !headerFeedListEl && !pageFeedListEl) {
    return;
  }

  const ids = Array.isArray(validBrandIds) ? validBrandIds.filter(Boolean) : [];
  if (ids.length === 0) {
    if (headerFeedCountEl) headerFeedCountEl.textContent = '0';
    if (pageFeedCountEl) pageFeedCountEl.textContent = '0';
    const emptyNoBrandsHtml = `
      <div class="tw-text-center tw-py-9 tw-px-5 tw-rounded-2xl tw-border tw-border-dashed tw-border-border-subtle tw-bg-surface/40">
        <span class="tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-rounded-2xl tw-bg-ink/[0.04] dark:tw-bg-white/[0.05] tw-border tw-border-line/50 tw-flex tw-items-center tw-justify-center tw-text-secondary">
          <span class="material-symbols-rounded tw-text-[24px]">vault_feed</span>
        </span>
        <p class="tw-text-sm sm:tw-text-base tw-font-heading tw-font-bold tw-text-primary tw-mb-1">Your Brands' Deal Feed Is Empty</p>
        <p class="tw-text-xs sm:tw-text-[13px] tw-text-secondary tw-max-w-md tw-mx-auto tw-mb-4 tw-leading-relaxed">Follow your favourite merchants across BudgetFitter and their active voucher codes and verified deals will stream automatically right here.</p>
        <a href="/brand/" class="bf-button bf-button-secondary !tw-h-9 !tw-px-4 !tw-text-xs tw-no-underline">Explore Brands to Follow</a>
      </div>
    `;
    if (headerFeedListEl) headerFeedListEl.innerHTML = emptyNoBrandsHtml;
    if (pageFeedListEl) pageFeedListEl.innerHTML = emptyNoBrandsHtml;
    return;
  }

  const brandIdsParam = encodeURIComponent(ids.slice(0, 150).join(','));
  const cacheKey = ids.slice().map(String).sort().join(',');

  if (cacheKey !== lastBrandFeedCacheKey || cachedBrandFeedDeals.length === 0) {
    try {
      const res = await fetch(`/wp-json/app-api/v1/saved-hydration?feed_brand_ids=${brandIdsParam}&feed_offset=0&feed_limit=10`);
      if (res.ok) {
        const json = await res.json();
        const rawBatch = Array.isArray(json?.brand_feed_deals) ? json.brand_feed_deals : [];
        cachedBrandFeedDeals = rawBatch.filter((d) => d && d.id && d.card_html);
        cachedBrandFeedTotal = Number(json?.brand_feed_total ?? cachedBrandFeedDeals.length);
        cachedBrandFeedNextOffset = rawBatch.length > 0 ? rawBatch.length : cachedBrandFeedDeals.length;
        cachedBrandFeedHasMore = Boolean(json?.brand_feed_has_more) || cachedBrandFeedNextOffset < cachedBrandFeedTotal;
        lastBrandFeedCacheKey = cacheKey;
      }
    } catch (_) {}
  }

  if (headerFeedCountEl) headerFeedCountEl.textContent = String(cachedBrandFeedTotal);
  if (pageFeedCountEl) pageFeedCountEl.textContent = String(cachedBrandFeedTotal);

  if (cachedBrandFeedDeals.length === 0) {
    const emptyDealsHtml = `
      <div class="tw-text-center tw-py-9 tw-px-5 tw-rounded-2xl tw-border tw-border-dashed tw-border-border-subtle tw-bg-surface/40">
        <span class="tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-rounded-2xl tw-bg-ink/[0.04] dark:tw-bg-white/[0.05] tw-border tw-border-line/50 tw-flex tw-items-center tw-justify-center tw-text-secondary">
          <span class="material-symbols-rounded tw-text-[24px]">vault_feed</span>
        </span>
        <p class="tw-text-sm sm:tw-text-base tw-font-heading tw-font-bold tw-text-primary tw-mb-1">No Live Offers From Your Followed Brands Right Now</p>
        <p class="tw-text-xs sm:tw-text-[13px] tw-text-secondary tw-max-w-md tw-mx-auto tw-mb-4 tw-leading-relaxed">Follow more merchants or explore all live verified perks while we monitor your followed brands for new drops.</p>
        <a href="/brand/" class="bf-button bf-button-secondary !tw-h-9 !tw-px-4 !tw-text-xs tw-no-underline">Explore Live Perks</a>
      </div>
    `;
    if (headerFeedListEl) headerFeedListEl.innerHTML = emptyDealsHtml;
    if (pageFeedListEl) pageFeedListEl.innerHTML = emptyDealsHtml;
    return;
  }

  if (pageFeedListEl) {
    const prevObs = activeLazyObservers.get(pageFeedListEl);
    if (prevObs) {
      prevObs.disconnect();
      activeLazyObservers.delete(pageFeedListEl);
    }
    pageFeedListEl.innerHTML = cachedBrandFeedDeals.map((d) => d.card_html).join('');
    updateSaveDealButtonsOnDOM();

    if (cachedBrandFeedHasMore) {
      const sentinel = document.createElement('div');
      sentinel.className = 'tw-w-full tw-py-5 tw-flex tw-justify-center tw-text-center tw-text-xs tw-text-secondary';
      pageFeedListEl.appendChild(sentinel);

      const loadMoreFeedDeals = async () => {
        if (isFetchingBrandFeedMore || !cachedBrandFeedHasMore) return;
        isFetchingBrandFeedMore = true;
        if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.show();
        sentinel.innerHTML = window.BFWT_SignatureLoaderHtml
          ? window.BFWT_SignatureLoaderHtml('Loading more offers')
          : '<div class="tw-p-4 tw-rounded-2xl tw-border tw-border-line/50 tw-bg-surface/60 tw-animate-pulse tw-h-24 tw-w-full"></div>';
        try {
          const offset = cachedBrandFeedNextOffset > 0 ? cachedBrandFeedNextOffset : cachedBrandFeedDeals.length;
          const res = await fetch(`/wp-json/app-api/v1/saved-hydration?feed_brand_ids=${brandIdsParam}&feed_offset=${offset}&feed_limit=10`);
          if (res.ok) {
            const json = await res.json();
            const rawBatch = Array.isArray(json?.brand_feed_deals) ? json.brand_feed_deals : [];
            const nextItems = rawBatch.filter((d) => d && d.id && d.card_html);
            cachedBrandFeedNextOffset = offset + (rawBatch.length > 0 ? rawBatch.length : nextItems.length);
            if (typeof json?.brand_feed_total === 'number' && json.brand_feed_total > 0) {
              cachedBrandFeedTotal = json.brand_feed_total;
            }
            const existingIds = new Set(cachedBrandFeedDeals.map((d) => Number(d.id)));
            const uniqueNext = nextItems.filter((d) => !existingIds.has(Number(d.id)));
            if (uniqueNext.length > 0) {
              cachedBrandFeedDeals.push(...uniqueNext);
              sentinel.insertAdjacentHTML('beforebegin', uniqueNext.map((d) => d.card_html).join(''));
              updateSaveDealButtonsOnDOM();
            }
            cachedBrandFeedHasMore = Boolean(json?.brand_feed_has_more) && rawBatch.length > 0 && cachedBrandFeedNextOffset < cachedBrandFeedTotal;
          } else {
            cachedBrandFeedHasMore = false;
          }
        } catch (_) {
          cachedBrandFeedHasMore = false;
        } finally {
          isFetchingBrandFeedMore = false;
          if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.hide(260);
          if (!cachedBrandFeedHasMore && sentinel.parentNode) {
            sentinel.remove();
            const obs = activeLazyObservers.get(pageFeedListEl);
            if (obs) {
              obs.disconnect();
              activeLazyObservers.delete(pageFeedListEl);
            }
          } else {
            renderFeedLoadMoreBtn();
          }
        }
      };

      const renderFeedLoadMoreBtn = () => {
        if (!cachedBrandFeedHasMore) {
          sentinel.innerHTML = '';
          return;
        }
        const shownCount = Math.min(cachedBrandFeedDeals.length, cachedBrandFeedTotal || cachedBrandFeedDeals.length);
        const totalCount = Math.max(cachedBrandFeedTotal, shownCount);
        sentinel.innerHTML = `
          <button type="button" class="bfwt-load-more-btn" data-bfwt-feed-load-more>
            <span>Load More Deals</span>
            <span class="bfwt-load-more-btn__count">${shownCount} / ${totalCount}</span>
          </button>
        `;
        const btn = sentinel.querySelector('[data-bfwt-feed-load-more]');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            loadMoreFeedDeals();
          });
        }
      };

      renderFeedLoadMoreBtn();
    }
  }
}

const RECENT_BRANDS_STORAGE_KEY = 'bfwt_recent_brands';
const MAX_RECENT_BRANDS = 8;

function extractBrandSlugFromEntry(b) {
  if (!b || typeof b !== 'object') return '';
  if (typeof b.slug === 'string' && b.slug.trim()) {
    return b.slug.trim();
  }
  const candidateUrl = b.link || b.url || '';
  if (typeof candidateUrl === 'string' && candidateUrl.trim()) {
    const m = candidateUrl.match(/\/(?:brand|listing)\/([^\/?#]+)/i);
    if (m && m[1]) return m[1].trim();
  }
  return '';
}

function compactRecentBrandEntry(b) {
  if (!b || typeof b !== 'object') return null;
  if (b.post_type === 'legacy_brand' || (typeof b.link === 'string' && b.link.includes('/listing/'))) {
    return null;
  }
  const id = b.id ? Number(b.id) : 0;
  const slug = extractBrandSlugFromEntry(b);
  const name = String(b.name || b.title || '').trim();
  if ((!id && !slug) || !name || name.toLowerCase() === 'unknown') return null;
  const logo = b.brand_logo_url || b.logo_url || b.logo || '';
  const discount = b.brand_discount ? String(b.brand_discount).trim() : '';
  return {
    id,
    title: name,
    name,
    slug,
    link: slug ? `https://budgetfitter.uk/brand/${slug}/` : '',
    post_type: 'brand',
    brand_logo_url: logo,
    logo_url: logo,
    logo,
    ...(discount ? { brand_discount: discount } : {}),
  };
}

function loadLocalRecentBrands() {
  try {
    const raw = localStorage.getItem(RECENT_BRANDS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map(compactRecentBrandEntry).filter(Boolean).slice(0, MAX_RECENT_BRANDS);
    }
  } catch (_) {}
  return [];
}

function saveLocalRecentBrands(list) {
  try {
    const compacted = (list || [])
      .map(compactRecentBrandEntry)
      .filter(Boolean)
      .slice(0, MAX_RECENT_BRANDS);
    localStorage.setItem(RECENT_BRANDS_STORAGE_KEY, JSON.stringify(compacted));
  } catch (_) {}
}

function mergeRecentBrandLists(primaryList, secondaryList) {
  const seenIds = new Set();
  const seenSlugs = new Set();
  const merged = [];
  [...(primaryList || []), ...(secondaryList || [])].forEach((raw) => {
    const b = compactRecentBrandEntry(raw);
    if (!b) return;
    const idKey = b.id ? String(b.id) : '';
    const slug = (b.slug || '').toLowerCase();
    if (!idKey && !slug) return;
    if ((idKey && seenIds.has(idKey)) || (slug && seenSlugs.has(slug))) return;
    if (idKey) seenIds.add(idKey);
    if (slug) seenSlugs.add(slug);
    merged.push(b);
  });
  return merged.slice(0, MAX_RECENT_BRANDS);
}

let isHydratingRecentBrands = false;
let pendingRecentBrandsHydration = false;

async function syncRecentBrandsUI() {
  const searchSection = document.getElementById('bfwt-search-recent-brands');
  const searchListEl = document.getElementById('bfwt-search-recent-brands-list');
  const searchInputEl = document.querySelector('.bfwt-search-input');
  const isCurrentlySearching = Boolean(searchInputEl && searchInputEl.value && searchInputEl.value.trim().length > 0);

  // 1. Hydrate any recent brand IDs not yet checked against /wp-json/app-api/v1/saved-hydration
  const uncheckedIds = userRecentBrands
    .map((b) => (b && b.id ? String(b.id) : ''))
    .filter((id) => {
      if (!id || id === '0') return false;
      const cached = followedBrandsMetaCache.get(id);
      return !cached || !cached.serverChecked;
    });

  if (uncheckedIds.length > 0) {
    if (isHydratingRecentBrands) {
      pendingRecentBrandsHydration = true;
    } else {
      isHydratingRecentBrands = true;
      try {
        const uniqueIds = Array.from(new Set(uncheckedIds)).slice(0, 50);
        const res = await fetch(`/wp-json/app-api/v1/saved-hydration?brand_ids=${encodeURIComponent(uniqueIds.join(','))}`);
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json?.brands) ? json.brands : [];
          const returnedIds = new Set();
          items.forEach((item) => {
            if (item && item.id && item.post_type === 'brand') {
              const key = String(item.id);
              returnedIds.add(key);
              followedBrandsMetaCache.set(key, {
                ...item,
                notFound: false,
                serverChecked: true,
              });
            }
          });
          uniqueIds.forEach((id) => {
            const key = String(id);
            if (!returnedIds.has(key)) {
              followedBrandsMetaCache.set(key, { notFound: true, serverChecked: true });
            }
          });
        }
      } catch (_) {
      } finally {
        isHydratingRecentBrands = false;
        if (pendingRecentBrandsHydration) {
          pendingRecentBrandsHydration = false;
          setTimeout(() => syncRecentBrandsUI(), 0);
        }
      }
    }
  }

  // 2. Enrich with server-verified brand metadata, compact heavy fields & prune invalid items
  let didMutate = userRecentBrands.length > MAX_RECENT_BRANDS;
  const cleanedList = [];
  userRecentBrands.forEach((b) => {
    if (!b || typeof b !== 'object') {
      didMutate = true;
      return;
    }
    if (b.post_type === 'legacy_brand' || (typeof b.link === 'string' && b.link.includes('/listing/'))) {
      didMutate = true;
      return;
    }
    if ('content' in b || 'faqs' in b || 'factsheet' in b || 'budgetfitter_score' in b || 'taxonomies' in b) {
      didMutate = true;
    }

    const idKey = b.id ? String(b.id) : '';
    const meta = idKey ? followedBrandsMetaCache.get(idKey) : null;
    if (meta && meta.serverChecked && meta.notFound) {
      didMutate = true;
      return;
    }

    let resolvedName = (meta && meta.name) ? meta.name : (b.name || b.title || '').trim();
    let resolvedSlug = (meta && meta.slug) ? meta.slug : extractBrandSlugFromEntry(b);
    let resolvedLogo = '';
    if (meta && meta.serverChecked) {
      resolvedLogo = meta.logo_url || meta.brand_logo_url || '';
    } else {
      resolvedLogo = b.brand_logo_url || b.logo_url || b.logo || '';
    }
    const resolvedDiscount = (meta && meta.brand_discount) ? meta.brand_discount : (b.brand_discount || '');

    if (!resolvedName || resolvedName.toLowerCase() === 'unknown' || !resolvedSlug) {
      didMutate = true;
      return;
    }

    const canonicalLink = `https://budgetfitter.uk/brand/${resolvedSlug}/`;
    if (
      b.title !== resolvedName ||
      b.name !== resolvedName ||
      b.slug !== resolvedSlug ||
      b.brand_logo_url !== resolvedLogo ||
      b.logo !== resolvedLogo
    ) {
      didMutate = true;
    }

    cleanedList.push({
      id: b.id ? Number(b.id) : (meta && meta.id ? Number(meta.id) : 0),
      title: resolvedName,
      name: resolvedName,
      slug: resolvedSlug,
      link: canonicalLink,
      post_type: 'brand',
      brand_logo_url: resolvedLogo,
      logo_url: resolvedLogo,
      logo: resolvedLogo,
      ...(resolvedDiscount ? { brand_discount: resolvedDiscount } : {}),
    });
  });

  if (didMutate) {
    userRecentBrands = cleanedList.slice(0, MAX_RECENT_BRANDS);
    saveLocalRecentBrands(userRecentBrands);
    if (auth && auth.currentUser) {
      const userRef = doc(db, USERS_COLLECTION, auth.currentUser.uid);
      setDoc(userRef, { recent_brands: userRecentBrands }, { merge: true }).catch(() => {});
    }
  }

  if (searchSection && searchListEl) {
    if (userRecentBrands.length === 0) {
      searchSection.classList.add('tw-hidden');
      searchListEl.innerHTML = '';
    } else {
      if (!isCurrentlySearching) {
        searchSection.classList.remove('tw-hidden');
      }
      searchListEl.innerHTML = userRecentBrands
        .slice(0, MAX_RECENT_BRANDS)
        .map((b) => {
          const name = escapeHtmlSafe(b.name || b.title || 'Brand');
          const slug = escapeHtmlSafe(b.slug || extractBrandSlugFromEntry(b));
          const url = slug ? `/brand/${slug}/` : '/brand/';
          const logo = b.brand_logo_url || b.logo_url || b.logo || '';
          return `
            <a href="${url}" data-no-sheet class="tw-group tw-inline-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-xl tw-border tw-border-line/50 tw-bg-paper hover:tw-border-accent hover:tw-bg-accent/[0.04] tw-transition-all tw-no-underline tw-shrink-0">
              ${
                logo
                  ? `<span class="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-white tw-border tw-border-line/60 tw-p-[1.5px] tw-shadow-sm tw-shrink-0 tw-overflow-hidden tw-inline-flex"><img src="${escapeHtmlSafe(logo)}" alt="${name}" class="tw-w-full tw-h-full tw-object-cover tw-rounded-[6px]" loading="lazy"></span>`
                  : `<span class="tw-w-6 tw-h-6 tw-rounded-md tw-bg-accent/10 tw-text-accent tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center tw-shrink-0">${escapeHtmlSafe(name.charAt(0))}</span>`
              }
              <span class="tw-text-xs tw-font-semibold tw-text-ink group-hover:tw-text-accent tw-transition-colors tw-whitespace-nowrap">${name}</span>
            </a>
          `;
        })
        .join('');
    }
  }
}

let hasRecordedCurrentPageBrandView = false;

function detectCurrentPageBrandEntry() {
  const brandFollowBtn = document.querySelector('.bfwt-brand-header__watchlist-icon');
  if (!brandFollowBtn) return null;

  const brandId = Number(brandFollowBtn.getAttribute('data-dealid') || 0);
  const brandName = (brandFollowBtn.getAttribute('data-brand-name') || '').trim();
  let brandSlug = (brandFollowBtn.getAttribute('data-brand-slug') || '').trim();
  let postType = 'brand';
  const m = window.location.pathname.match(/^\/(brand|listing)\/([^\/]+)/);
  if (m) {
    postType = m[1] === 'listing' ? 'legacy_brand' : 'brand';
    if (!brandSlug) brandSlug = m[2];
  }
  if (postType !== 'brand' || !brandId || !brandName || !brandSlug) return null;

  // Strictly select the square brand logo badge (.bfwt-brand-header__logo-badge img), NEVER the 21:9 cover image
  const logoImg = document.querySelector('.bfwt-brand-header__logo-badge img');
  const logoUrl = logoImg ? logoImg.getAttribute('src') || '' : '';

  return {
    id: brandId,
    title: brandName,
    name: brandName,
    slug: brandSlug,
    link: `https://budgetfitter.uk/brand/${brandSlug}/`,
    post_type: 'brand',
    brand_logo_url: logoUrl,
    logo_url: logoUrl,
    logo: logoUrl,
    viewed_at: new Date().toISOString(),
  };
}

function recordCurrentBrandPageVisitLocal() {
  userRecentBrands = loadLocalRecentBrands();
  const entry = detectCurrentPageBrandEntry();
  if (entry) {
    userRecentBrands = mergeRecentBrandLists([entry], userRecentBrands);
    saveLocalRecentBrands(userRecentBrands);
  }
  syncRecentBrandsUI();
}

function maybeRecordBrandPageVisit(docRef, existingRecent) {
  const localList = loadLocalRecentBrands();
  const entry = detectCurrentPageBrandEntry();
  const merged = mergeRecentBrandLists(
    entry ? [entry, ...localList] : localList,
    Array.isArray(existingRecent) ? existingRecent : []
  );
  userRecentBrands = merged;
  saveLocalRecentBrands(merged);
  syncRecentBrandsUI();
  if (docRef && (!hasRecordedCurrentPageBrandView || merged.length !== (existingRecent || []).length)) {
    hasRecordedCurrentPageBrandView = true;
    setDoc(docRef, { recent_brands: merged }, { merge: true }).catch(() => {});
  }
}

function startUserDocSync(uid) {
  stopUserDocSync(); // safety clear

  const docRef = doc(db, USERS_COLLECTION, uid);
  userDocUnsubscribe = onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const pushPrefs = data.notification_preferences_v2 || {};

        // 1. 6 Granular Notification Toggles (3x2 Compact Matrix: App <-> Web Parity)
        const browserPushGranted = ('Notification' in window) && Notification.permission === 'granted';
        const masterPushEnabled = (pushPrefs.web_enabled !== undefined ? pushPrefs.web_enabled !== false : pushPrefs.enabled !== false);

        const dealPushVal = pushPrefs.deal_push_enabled !== undefined ? !!pushPrefs.deal_push_enabled : masterPushEnabled;
        const coffeePushVal = pushPrefs.coffee_push_enabled !== undefined ? !!pushPrefs.coffee_push_enabled : (pushPrefs.editorial_push_enabled !== undefined ? !!pushPrefs.editorial_push_enabled : masterPushEnabled);
        const editorialPushVal = pushPrefs.editorial_push_enabled !== undefined ? !!pushPrefs.editorial_push_enabled : masterPushEnabled;

        const isSub = typeof data.newsletter_subscribed !== 'undefined' ? !!data.newsletter_subscribed : true;
        const dealEmailVal = pushPrefs.deal_email_enabled !== undefined ? !!pushPrefs.deal_email_enabled : isSub;
        const coffeeEmailVal = pushPrefs.coffee_email_enabled !== undefined ? !!pushPrefs.coffee_email_enabled : isSub;
        const editorialEmailVal = isSub;

        const dealPushCheckbox = document.getElementById('bfwt-deal-push-checkbox');
        if (dealPushCheckbox) dealPushCheckbox.checked = dealPushVal && browserPushGranted;

        const coffeePushCheckbox = document.getElementById('bfwt-coffee-push-checkbox');
        if (coffeePushCheckbox) coffeePushCheckbox.checked = coffeePushVal && browserPushGranted;

        const editorialPushCheckbox = document.getElementById('bfwt-editorial-push-checkbox');
        if (editorialPushCheckbox) editorialPushCheckbox.checked = editorialPushVal && browserPushGranted;

        const dealEmailCheckbox = document.getElementById('bfwt-deal-email-checkbox');
        if (dealEmailCheckbox) dealEmailCheckbox.checked = dealEmailVal;

        const coffeeEmailCheckbox = document.getElementById('bfwt-coffee-email-checkbox');
        if (coffeeEmailCheckbox) coffeeEmailCheckbox.checked = coffeeEmailVal;

        const newsletterCheckbox = document.getElementById('bfwt-newsletter-checkbox');
        if (newsletterCheckbox) newsletterCheckbox.checked = editorialEmailVal;

        const webpushCheckbox = document.getElementById('bfwt-webpush-checkbox');
        if (webpushCheckbox) {
          webpushCheckbox.checked = masterPushEnabled && browserPushGranted;
        }

        updateChannelPillButtonsUI({
          'deal-push': dealPushVal && browserPushGranted,
          'deal-email': dealEmailVal,
          'coffee-push': coffeePushVal && browserPushGranted,
          'coffee-email': coffeeEmailVal,
          'editorial-push': editorialPushVal && browserPushGranted,
          'editorial-email': editorialEmailVal,
        });

        syncCommunicationNudges(currentUser, data);

        // 2. Sync Saved Deals (deduplicate by id and automatically prune expired deals)
        if (Array.isArray(data.saved_deals)) {
          const seenDealIds = new Set();
          const rawDeals = data.saved_deals.filter(
            (d) => d && typeof d === 'object' && Number(d.id) > 0
          );
          const activeDeals = rawDeals.filter((d) => {
            const id = Number(d.id);
            if (seenDealIds.has(id)) return false;
            if (isSavedDealExpired(d.expiration_date)) return false;
            seenDealIds.add(id);
            return true;
          });
          userSavedDeals = activeDeals;
          if (activeDeals.length !== rawDeals.length) {
            setDoc(docRef, { saved_deals: activeDeals }, { merge: true }).catch(
              () => {}
            );
          }
        } else {
          userSavedDeals = [];
        }
        syncSavedDealsUI();

        // 3. Sync Saved Pulse Articles (deduplicate by id/slug)
        if (Array.isArray(data.saved_articles)) {
          const seenArtKeys = new Set();
          const rawArticles = data.saved_articles.filter(
            (a) => a && typeof a === 'object' && (a.id || a.slug)
          );
          userSavedArticles = rawArticles.filter((a) => {
            const idKey = Number(a.id) > 0 ? `id:${Number(a.id)}` : '';
            const slugKey = a.slug ? `slug:${String(a.slug)}` : '';
            if ((idKey && seenArtKeys.has(idKey)) || (slugKey && seenArtKeys.has(slugKey))) {
              return false;
            }
            if (idKey) seenArtKeys.add(idKey);
            if (slugKey) seenArtKeys.add(slugKey);
            return true;
          });
          if (userSavedArticles.length !== rawArticles.length) {
            setDoc(docRef, { saved_articles: userSavedArticles }, { merge: true }).catch(
              () => {}
            );
          }
        } else {
          userSavedArticles = [];
        }
        syncSavedArticlesUI();

        // 4. Sync Recently Viewed Brands (merge guest/local + remote, cap at 8, then hydrate UI)
        const remoteRecentBrands = Array.isArray(data.recent_brands)
          ? data.recent_brands.filter((b) => b && typeof b === 'object' && (b.id || b.slug))
          : [];
        maybeRecordBrandPageVisit(docRef, remoteRecentBrands);

        // 5. Sync 3-Mode Theme Preference (system / light / dark)
        const rawThemePref = data.theme_preference || data.theme || data.color_mode || 'system';
        updateThemeModeButtonsUI(rawThemePref);
        let dbTheme = rawThemePref;
        if (dbTheme === 'system') {
          const systemPrefersDark = window.matchMedia(
            '(prefers-color-scheme: dark)'
          ).matches;
          dbTheme = systemPrefersDark ? 'dark' : 'light';
        }
        if (dbTheme === 'dark' || dbTheme === 'light') {
          lastSyncedTheme = rawThemePref;
          const currentTheme =
            document.documentElement.getAttribute('data-color-mode');
          if (currentTheme !== dbTheme) {
            applyTheme(dbTheme);
          }
        }
      }
    },
    (error) => {
      console.error('[Firestore] User doc sync error:', error);
    }
  );
}

function stopUserDocSync() {
  if (userDocUnsubscribe) {
    userDocUnsubscribe();
    userDocUnsubscribe = null;
  }
  latestUserFirestoreData = null;
  userSavedDeals = [];
  userSavedArticles = [];
  userRecentBrands = loadLocalRecentBrands();
  syncSavedDealsUI();
  syncSavedArticlesUI();
  syncRecentBrandsUI();
  syncCommunicationNudges(null, null);
  const guestPushGranted = ('Notification' in window) && Notification.permission === 'granted';
  updateChannelPillButtonsUI({
    'deal-push': false,
    'deal-email': false,
    'coffee-push': guestPushGranted,
    'coffee-email': false,
    'editorial-push': guestPushGranted,
    'editorial-email': false,
  });
}

// ── In-App Notifications Listener (Site-Wide Realtime Toasts) ──
let inAppNotifUnsub = null;

function setupInAppNotificationsListener(user) {
  if (inAppNotifUnsub) {
    inAppNotifUnsub();
    inAppNotifUnsub = null;
  }

  if (!user || !db) return;

  try {
    const notifColRef = collection(db, USERS_COLLECTION, user.uid, 'in_app_notifications');
    const q = query(notifColRef, where('read', '==', false), orderBy('timestamp', 'desc'), limit(5));

    inAppNotifUnsub = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const notifDoc = change.doc;
          const data = notifDoc.data();

          if (window.BFWT_Toast) {
            window.BFWT_Toast.info(data.body || '', {
              title: data.title || 'New Deal Alert',
              duration: 10000,
              action: data.link ? {
                label: 'View Deal ➔',
                url: data.link
              } : null
            });
          }

          // Mark notification as read so it won't pop up again on refresh
          updateDoc(notifDoc.ref, { read: true }).catch(() => {});
        }
      });
    }, (err) => {
      console.warn('[Firebase] In-App notifications listener warning:', err);
    });
  } catch (e) {
    console.warn('[Firebase] Failed to setup in-app notifications listener:', e);
  }
}

async function _ensureWebPushPermissionAndToken() {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return null;

  try {
    await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
    const registration = await navigator.serviceWorker.ready;
    if (messaging) {
      const fcmToken = await getToken(messaging, {
        vapidKey: 'BPJIKu-K2ysB8UNAp-S9T-p453waSiwth_PLd14mtEO7By9PW5N-6yByMlFxfyw0aImPD4cwpWDufLvyiobdMSo',
        serviceWorkerRegistration: registration,
      });
      return fcmToken;
    }
  } catch (e) {
    console.warn('[Firebase] ensureWebPushPermissionAndToken error:', e);
  }
  return null;
}

// ── Auto-Sync or Prompt Web Push Token on Login / Load ──
async function syncWebPushTokenOnLoad(user) {
  if (!user || !('Notification' in window) || !('serviceWorker' in navigator)) return;
  try {
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    if (permission === 'granted') {
      const webpushCheckbox = document.getElementById('bfwt-webpush-checkbox');
      if (webpushCheckbox) webpushCheckbox.checked = true;

      let registration = null;
      try {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
        registration = await navigator.serviceWorker.ready;
        console.log('[Firebase] Service Worker registered and READY for Web Push:', registration);
      } catch (swErr) {
        console.warn('[Firebase] Service Worker registration warning:', swErr);
      }

      let fcmToken = null;
      if (messaging) {
        try {
          const options = {
            vapidKey: 'BPJIKu-K2ysB8UNAp-S9T-p453waSiwth_PLd14mtEO7By9PW5N-6yByMlFxfyw0aImPD4cwpWDufLvyiobdMSo',
          };
          if (registration) options.serviceWorkerRegistration = registration;
          fcmToken = await getToken(messaging, options);
          console.log('[Firebase] Web FCM Token acquired on load:', fcmToken);
        } catch (tErr) {
          console.warn('[Firebase] Could not fetch FCM token on load:', tErr);
        }
      }

      const userDocRef = doc(db, USERS_COLLECTION, user.uid);
      const updateObj = {
        notification_preferences_v2: {
          web_enabled: true,
          enabled: true,
          last_updated: new Date().toISOString(),
        },
      };

      if (fcmToken) {
        updateObj.fcm_tokens_web = arrayUnion(fcmToken);
      }

      await setDoc(userDocRef, updateObj, { merge: true });
      console.log('[Firebase] Web push token & preferences synced to Firestore for user:', user.email);
    }
  } catch (err) {
    console.error('syncWebPushTokenOnLoad error:', err);
  }
}

// ── Firestore Watchlist Sync (Real-time favorite brands sync) ──
function syncWatchlistButtonsState() {
  const watchlistBtns = document.querySelectorAll('.bfwt-watchlist-icon');
  watchlistBtns.forEach((btn) => {
    const brandId = btn.getAttribute('data-dealid');
    if (brandId) {
      const isFollowed = userFollowedBrandIds.has(brandId.toString());
      const iconEl = btn.querySelector('.material-symbols-rounded') || btn.querySelector('svg');
      const textEl = btn.querySelector('.bfwt-header-action-label') || btn.querySelector('span:last-child') || btn.lastElementChild;

      if (isFollowed) {
        btn.classList.remove('bf-button-outline');
        btn.classList.add(
          'bf-button-primary',
          'tw-bg-accent',
          '!tw-text-white',
          'followed'
        );
        if (iconEl && iconEl.classList.contains('material-symbols-rounded')) {
          iconEl.removeAttribute('data-bf-icon');
          iconEl.innerHTML = '';
          iconEl.textContent = 'following_brand';
        }
        if (textEl) textEl.textContent = (window.bfwtTranslations && window.bfwtTranslations.following_btn) ? window.bfwtTranslations.following_btn : 'Following';
        if (!btn._bfHoverBound) {
          btn._bfHoverBound = true;
          btn.addEventListener('pointerenter', () => {
            if (!btn.classList.contains('followed')) return;
            const ic = btn.querySelector('.material-symbols-rounded');
            if (ic) { ic.removeAttribute('data-bf-icon'); ic.innerHTML = ''; ic.textContent = 'unfollow_brand'; }
          });
          btn.addEventListener('pointerleave', () => {
            if (!btn.classList.contains('followed')) return;
            const ic = btn.querySelector('.material-symbols-rounded');
            if (ic) { ic.removeAttribute('data-bf-icon'); ic.innerHTML = ''; ic.textContent = 'following_brand'; }
          });
        }
      } else {
        btn.classList.add('bf-button-outline');
        btn.classList.remove(
          'bf-button-primary',
          'tw-bg-accent',
          '!tw-text-white',
          'followed'
        );
        if (iconEl && iconEl.classList.contains('material-symbols-rounded')) {
          iconEl.removeAttribute('data-bf-icon');
          iconEl.innerHTML = '';
          iconEl.textContent = 'follow_brand';
        }
        if (textEl) textEl.textContent = (window.bfwtTranslations && window.bfwtTranslations.follow_btn) ? window.bfwtTranslations.follow_btn : 'Follow';
      }
    }
  });
  syncHeaderFollowedBrandsUI();
}

function initWatchlistFromCookie() {
  const match = document.cookie.match(/(?:^|; )bfwt_favorite_brands=([^;]*)/);
  if (match && match[1]) {
    match[1].split(',').forEach((id) => {
      const cleanId = id.trim();
      if (cleanId) userFollowedBrandIds.add(cleanId);
    });
  }
}

// Populate from cookie immediately on load
initWatchlistFromCookie();

function updateWatchlistCookie() {
  const brandIds = Array.from(userFollowedBrandIds);
  if (brandIds.length > 0) {
    document.cookie =
      'bfwt_favorite_brands=' +
      brandIds.slice(0, 200).join(',') +
      '; path=/; max-age=31536000; Secure; SameSite=Lax';
  } else {
    document.cookie =
      'bfwt_favorite_brands=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax';
  }
}

let watchlistUnsubscribes = [];

function stopWatchlistSync(updateDOM = true) {
  if (watchlistUnsubscribes.length > 0) {
    watchlistUnsubscribes.forEach((unsub) => {
      if (typeof unsub === 'function') unsub();
    });
    watchlistUnsubscribes = [];
  }

  if (updateDOM) {
    userFollowedBrandIds.clear();
    document.cookie =
      'bfwt_favorite_brands=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax';
    syncWatchlistButtonsState();

    const wrapper = document.getElementById('bfwt-your-brands-wrapper');
    if (wrapper) {
      wrapper
        .querySelector('.bfwt-your-brands-skeletons')
        ?.classList.add('tw-hidden');
      document
        .getElementById('bfwt-your-brands-empty-state')
        ?.classList.add('tw-hidden');
      document
        .getElementById('bfwt-your-brands-guest-promo')
        ?.classList.remove('tw-hidden');
    }
  }
}

function startWatchlistSync(uid) {
  stopWatchlistSync(false); // safety clear without updating DOM to prevent layout shift

  try {
    const subcolBrandMap = new Map();

    const updateCombinedWatchlist = () => {
      // Merge maps (subcollection has priority for timestamps)
      const mergedMap = new Map();
      
      subcolBrandMap.forEach((ms, id) => {
        if (!mergedMap.has(id) || mergedMap.get(id) < ms) {
          mergedMap.set(id, ms);
        }
      });

      const docs = Array.from(mergedMap.entries()).map(([id, ms]) => ({ id, ms }));
      docs.sort((a, b) => b.ms - a.ms);

      userFollowedBrandIds.clear();
      const brandIds = [];
      docs.forEach((d) => {
        userFollowedBrandIds.add(d.id);
        brandIds.push(d.id);
      });

      if (brandIds.length > 0) {
        document.cookie =
          'bfwt_favorite_brands=' +
          brandIds.slice(0, 200).join(',') +
          '; path=/; max-age=31536000; Secure; SameSite=Lax';
      } else {
        document.cookie =
          'bfwt_favorite_brands=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax';
      }

      syncWatchlistButtonsState();

      if (document.getElementById('bfwt-your-brands-wrapper')) {
        loadYourBrandsGrid(true);
      }
    };

    const targets = [
      { col: 'dev_users', sub: 'favorite_brands' },
    ];

    targets.forEach((target) => {
      try {
        const colRef = collection(db, target.col, uid, target.sub);
        const unsub = onSnapshot(
          colRef,
          (snapshot) => {
            snapshot.docChanges().forEach((change) => {
              if (change.type === 'removed') {
                subcolBrandMap.delete(change.doc.id);
              } else {
                const data = change.doc.data() || {};
                const docKey = String(change.doc.id);
                if ((data.title || data.name) && !followedBrandsMetaCache.has(docKey)) {
                  let parsedSlug = data.slug || '';
                  if (!parsedSlug && typeof data.link === 'string') {
                    const m = data.link.match(/\/(?:brand|listing)\/([^/?#]+)/);
                    if (m && m[1]) parsedSlug = m[1];
                  }
                  followedBrandsMetaCache.set(docKey, {
                    id: Number(docKey) || docKey,
                    name: data.title || data.name,
                    title: data.title || data.name,
                    slug: parsedSlug,
                    link: data.link || (parsedSlug ? `/brand/${parsedSlug}/` : '/brand/'),
                    logo_url: data.logo_url || data.brand_logo_url || data.brand_logo_svg || '',
                    brand_discount: data.brand_discount || '',
                    active_deals_count: data.active_deals_count ?? data.deals_count ?? 0,
                    serverChecked: false,
                  });
                }
                let ms = 0;
                if (data.timestamp) {
                  if (typeof data.timestamp.toMillis === 'function') {
                    ms = data.timestamp.toMillis();
                  } else if (data.timestamp.seconds) {
                    ms = data.timestamp.seconds * 1000;
                  } else {
                    ms = new Date(data.timestamp).getTime() || 0;
                  }
                }
                if (!ms && data.client_timestamp_ms) {
                  ms = Number(data.client_timestamp_ms) || 0;
                }
                if (!subcolBrandMap.has(change.doc.id) || subcolBrandMap.get(change.doc.id) < ms) {
                  subcolBrandMap.set(change.doc.id, ms);
                }
              }
            });
            updateCombinedWatchlist();
          },
          () => {}
        );
        watchlistUnsubscribes.push(unsub);
      } catch (_) {}
    });

    // Removed legacy array listener to match app behavior and prevent zombie data
  } catch (e) {
    console.error('[Firestore] Error setting up watchlist sync:', e);
  }
}

/**
 * Handle user logout cleanly: signs out of Firebase, sets a success toast in sessionStorage,
 * and reloads the page.
 */
async function logoutHandler(e) {
  if (e && e.preventDefault) {
    e.preventDefault();
  }
  try {
    if (!auth) {
      initFirebase();
    }
    try {
      localStorage.removeItem(RECENT_BRANDS_STORAGE_KEY);
    } catch (_) {}
    userRecentBrands = [];
    if (auth) {
      await signOut(auth);
    }
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(
          'bfwt_toast_success',
          'You have successfully logged out.'
        );
      }
    } catch (_) {}
    window.location.reload();
  } catch (err) {
    console.error('Logout error:', err);
  }
}

// ── Auth Forms Actions & Events ──
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Recently Viewed Brands in Search Sheet from localStorage (works for both guests & members)
  recordCurrentBrandPageVisitLocal();

  document.addEventListener('click', (e) => {
    const clearRecentBtn = e.target.closest('#bfwt-clear-recent-brands');
    if (!clearRecentBtn) return;
    e.preventDefault();
    userRecentBrands = [];
    try {
      localStorage.removeItem(RECENT_BRANDS_STORAGE_KEY);
    } catch (_) {}
    syncRecentBrandsUI();
    const activeUser =
      (typeof currentUser !== 'undefined' && currentUser) ||
      (typeof auth !== 'undefined' && auth ? auth.currentUser : null);
    if (activeUser && db) {
      setDoc(doc(db, USERS_COLLECTION, activeUser.uid), { recent_brands: [] }, { merge: true }).catch(() => {});
    }
  });

  // Initialize yourBrandsLoadedIds from the cookie if the container is already server-rendered
  const yourBrandsWrapper = document.getElementById('bfwt-your-brands-wrapper');
  if (
    yourBrandsWrapper &&
    yourBrandsWrapper.querySelector('.bfwt-pjax-container')
  ) {
    const cookieMatch = document.cookie.match(
      /(?:^|; )bfwt_favorite_brands=([^;]*)/
    );
    if (cookieMatch) {
      yourBrandsLoadedIds = decodeURIComponent(cookieMatch[1]);
    }
  }

  // Global delegated listener for Auth triggers (handles watchlist, newsletter, and any PJAX/dynamic login buttons)
  document.addEventListener(
    'click',
    (e) => {
      const trigger = e.target.closest('[data-sheet-target="accountSheet"]');
      if (trigger && !currentUser) {
        const view = trigger.getAttribute('data-auth-view') || 'login';
        const authTrigger = trigger.getAttribute('data-auth-trigger') || '';
        clearError();
        if (authTrigger) {
          setAuthContext({ trigger: authTrigger });
          updateAuthEncouragementBanner({ trigger: authTrigger });
        } else if (trigger.id === 'bfwt-account-trigger') {
          pendingGuestAction = null;
          updateAuthEncouragementBanner(null);
        }
        showAuthView(view);
      }
    },
    { capture: true }
  );

  // Check for pending success toast messages across page reloads
  try {
    const pendingToast = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('bfwt_toast_success') : null;
    if (pendingToast) {
      sessionStorage.removeItem('bfwt_toast_success');
      setTimeout(() => {
        window.BFWT_Toast?.success(pendingToast);
      }, 150);
    }
  } catch (_) {
    // Gracefully handle storage restrictions (e.g. iOS Safari Private Browsing)
  }

  // Check if we should automatically open the Account Hub / Login Sheet or a specific Account Panel (e.g. ?open=notifications)
  let shouldOpenAuth = false;
  let requestedPanelId = null;
  try {
    const openHub = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('bfwt_open_account_hub') : null;
    const urlParams = new URLSearchParams(window.location.search);
    const openTarget = urlParams.get('open');
    if (openTarget === 'notifications') {
      requestedPanelId = 'account-panel-notifications';
    } else if (openTarget === 'privacy' || openTarget === 'security') {
      requestedPanelId = 'account-panel-privacy';
    } else if (openTarget === 'account' || openTarget === 'details') {
      requestedPanelId = 'account-panel-details';
    }
    if (requestedPanelId && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('bfwt_open_account_panel', requestedPanelId);
    }
    shouldOpenAuth = openHub || Boolean(requestedPanelId) || urlParams.get('auth') === 'login' || urlParams.get('open_account') === '1' || urlParams.get('open_auth') === '1' || window.location.hash === '#login' || window.location.hash === '#accountSheet';

    if (shouldOpenAuth && typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('bfwt_open_account_hub');
    }
  } catch (_) {
    const urlParams = new URLSearchParams(window.location.search);
    shouldOpenAuth = urlParams.has('open') || urlParams.get('auth') === 'login' || urlParams.get('open_account') === '1' || urlParams.get('open_auth') === '1' || window.location.hash === '#login' || window.location.hash === '#accountSheet';
  }

  if (shouldOpenAuth) {
    setTimeout(() => {
      if (requestedPanelId && currentUser && window.BFWT_Membership && typeof window.BFWT_Membership.openPanel === 'function') {
        try { sessionStorage.removeItem('bfwt_open_account_panel'); } catch (_) {}
        window.BFWT_Membership.openPanel(requestedPanelId);
      } else {
        openAuthModal('login');
      }
    }, 350);
  }

  const accountSheet = document.getElementById('accountSheet');
  const accountTrigger = document.getElementById('bfwt-account-trigger');

  if (!accountSheet) return;

  // Reset errors and show login view when trigger is clicked
  if (accountTrigger) {
    accountTrigger.addEventListener('click', () => {
      if (!currentUser) {
        clearError();
        showAuthView('login');
      }
    });
  }

  // Auth View Switching (inside account sheet)
  const toForgot = document.getElementById('bfwt-to-forgot');
  const toRegister = document.getElementById('bfwt-to-register');
  const toLogin = document.getElementById('bfwt-to-login');
  const forgotToLogin = document.getElementById('bfwt-forgot-to-login');

  if (toForgot)
    {toForgot.addEventListener('click', () => showAuthView('forgot'));}
  if (toRegister)
    {toRegister.addEventListener('click', () => showAuthView('register'));}
  if (toLogin) toLogin.addEventListener('click', () => showAuthView('login'));
  if (forgotToLogin)
    {forgotToLogin.addEventListener('click', () => showAuthView('login'));}

  // Form Submit Listeners
  const loginForm = document.getElementById('bfwt-login-form');
  const registerForm = document.getElementById('bfwt-register-form');
  const forgotForm = document.getElementById('bfwt-forgot-form');

  // Helper to allow Enter key submission and trigger requestSubmit
  const setupEnterKeySubmit = (form) => {
    if (!form) return;
    const inputs = form.querySelectorAll('input');
    inputs.forEach((input) => {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            const submitBtn = form.querySelector('button[type="submit"]');
            if (submitBtn) {
              submitBtn.click();
            }
          }
        }
      });
    });
  };

  if (loginForm) {
    setupEnterKeySubmit(loginForm);
    loginForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      initFirebase();
      clearError();
      const email = document.getElementById('bfwt-login-email').value;
      const password = document.getElementById('bfwt-login-password').value;
      try {
        const creds = await signInWithEmailAndPassword(auth, email, password);
        const actionToast = await executePendingGuestAction(creds.user);
        sessionStorage.setItem(
          'bfwt_toast_success',
          actionToast || 'You have successfully logged in.'
        );
        if (!actionToast) {
          sessionStorage.setItem('bfwt_open_account_hub', 'true');
        }
        window.location.reload();
      } catch (err) {
        showError(translateAuthError(err.code));
      }
    });
  }

  if (registerForm) {
    setupEnterKeySubmit(registerForm);
    registerForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      initFirebase();
      clearError();
      const rawNameInput = document.getElementById('bfwt-register-name');
      const name = rawNameInput ? rawNameInput.value.trim() : '';
      const email = document.getElementById('bfwt-register-email').value;
      const password = document.getElementById('bfwt-register-password').value;

      const validation = validateName(name, true);
      if (!validation.valid) {
        showError(validation.message);
        return;
      }

      try {
        const creds = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        if (name) {
          await updateProfile(creds.user, { displayName: name });
          try {
            const tok = await creds.user.getIdToken();
            await fetch('/wp-json/app-api/v1/fitterpass/profile', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tok}`,
              },
              body: JSON.stringify({ custom_handle: name }),
            });
          } catch (_) {}
        }

        const savedAuthCtx = getActiveAuthContext();
        // Write full user profile to Firestore & trigger backend sync
        await ensureUserProfileInFirestore(
          creds.user,
          {
            email: email.trim().toLowerCase(),
            displayName: name,
            signup_source: 'web',
            signup_trigger: savedAuthCtx.trigger || 'direct',
            signup_brand_slug: savedAuthCtx.brand_slug || '',
            signup_brand_name: savedAuthCtx.brand_name || '',
            newsletter_subscribed: document.getElementById('bfwt-register-newsletter') ? document.getElementById('bfwt-register-newsletter').checked : true,
          },
          true
        );

        if (typeof window.bfTrack === 'function') {
          window.bfTrack('sign_up', {
            event_category: 'engagement',
            method: 'email',
            signup_trigger: savedAuthCtx.trigger || 'direct',
            brand_slug: savedAuthCtx.brand_slug || '',
            brand_name: savedAuthCtx.brand_name || '',
          });
        }

        // Force sync update to trigger display update
        handleAuthStateChanged(auth.currentUser);

        // Send custom email verification via WordPress API
        try {
          const idToken = await creds.user.getIdToken();
          await fetch('/wp-json/app-api/v1/send-app-verification', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              email: email,
              source: 'web',
            }),
          });
        } catch (verifyErr) {
          console.error(
            'Failed to trigger custom email verification on sign up:',
            verifyErr
          );
        }

        const actionToast = await executePendingGuestAction(creds.user);
        sessionStorage.setItem(
          'bfwt_toast_success',
          actionToast || 'Your account has been created successfully. Welcome to BudgetFitter!'
        );
        if (!actionToast) {
          sessionStorage.setItem('bfwt_open_account_hub', 'true');
        }
        window.location.reload();
      } catch (err) {
        showError(translateAuthError(err.code));
      }
    });
  }

  if (forgotForm) {
    setupEnterKeySubmit(forgotForm);
    forgotForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      clearError();
      const email = document.getElementById('bfwt-forgot-email').value;
      try {
        const res = await fetch('/wp-json/app-api/v1/send-app-password-reset', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': localized.nonce,
          },
          body: JSON.stringify({
            email: email,
            source: 'web',
          }),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to send password reset email.');
        }

        showSuccess('Password reset link has been sent to your email address.');
      } catch (err) {
        showError(
          err.message ||
            'Failed to send password reset email. Please try again.'
        );
      }
    });
  }

  // Google Auth Click (Login & Register)
  const googleAuthButtons = document.querySelectorAll(
    '#bfwt-google-login, #bfwt-google-register'
  );
  googleAuthButtons.forEach((btn) => {
    btn.addEventListener('click', async () => {
      initFirebase();
      clearError();
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      try {
        const result = await signInWithPopup(auth, provider);
        if (result.user) {
          const additionalUserInfo = getAdditionalUserInfo(result);
          const isNew = additionalUserInfo ? !!additionalUserInfo.isNewUser : false;
          const savedAuthCtx = getActiveAuthContext();
          await ensureUserProfileInFirestore(
            result.user,
            {
              signup_source: 'web',
              signup_trigger: savedAuthCtx.trigger || 'direct',
              signup_brand_slug: savedAuthCtx.brand_slug || '',
              signup_brand_name: savedAuthCtx.brand_name || '',
            },
            isNew
          );

          if (isNew && typeof window.bfTrack === 'function') {
            window.bfTrack('sign_up', {
              event_category: 'engagement',
              method: 'google',
              signup_trigger: savedAuthCtx.trigger || 'direct',
              brand_slug: savedAuthCtx.brand_slug || '',
              brand_name: savedAuthCtx.brand_name || '',
            });
          }
          const actionToast = await executePendingGuestAction(result.user);
          sessionStorage.setItem(
            'bfwt_toast_success',
            actionToast || 'You have successfully logged in.'
          );
          if (!actionToast) {
            sessionStorage.setItem('bfwt_open_account_hub', 'true');
          }
          window.location.reload();
        }
      } catch (err) {
        showError(translateAuthError(err.code));
      }
    });
  });

  // Apple Auth Click (Login & Register)
  const appleAuthButtons = document.querySelectorAll(
    '#bfwt-apple-login, #bfwt-apple-register'
  );
  appleAuthButtons.forEach((btn) => {
    btn.addEventListener('click', async () => {
      initFirebase();
      clearError();
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      try {
        const result = await signInWithPopup(auth, provider);
        if (result.user) {
          const additionalUserInfo = getAdditionalUserInfo(result);
          const isNew = additionalUserInfo ? !!additionalUserInfo.isNewUser : false;
          await ensureUserProfileInFirestore(
            result.user,
            { signup_source: 'web' },
            isNew
          );
          const actionToast = await executePendingGuestAction(result.user);
          sessionStorage.setItem(
            'bfwt_toast_success',
            actionToast || 'You have successfully logged in.'
          );
          if (!actionToast) {
            sessionStorage.setItem('bfwt_open_account_hub', 'true');
          }
          window.location.reload();
        }
      } catch (err) {
        showError(translateAuthError(err.code));
      }
    });
  });

  // ── Logged In Settings Split-Pane Navigation & Events ──

  // 1. Sidebar Tab Switching
  const tabButtons = accountSheet.querySelectorAll('.bfwt-account-tab-btn');
  const panels = accountSheet.querySelectorAll('.bfwt-account-panel');
  const sidebar = accountSheet.querySelector('.bfwt-account-sidebar-el');
  const contentPane = accountSheet.querySelector(
    '.bfwt-account-content-pane-el'
  );

  tabButtons.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = btn.getAttribute('data-target-panel');

      // Update active tab styles
      tabButtons.forEach((b) => {
        b.classList.remove(
          'is-active',
          'tw-bg-ink/[0.06]',
          'dark:tw-bg-white/[0.08]',
          'tw-text-ink',
          'tw-border-line/80',
          'tw-bg-accent/[0.08]',
          'tw-text-accent',
          'tw-border-accent/40'
        );
        b.classList.add(
          'tw-bg-transparent',
          'tw-text-secondary',
          'tw-border-transparent'
        );
      });
      btn.classList.add(
        'is-active',
        'tw-bg-ink/[0.06]',
        'dark:tw-bg-white/[0.08]',
        'tw-text-ink',
        'tw-border-line/80'
      );
      btn.classList.remove(
        'tw-bg-transparent',
        'tw-text-secondary',
        'tw-border-transparent'
      );

      // Toggle visible settings panel
      panels.forEach((p) => p.classList.add('tw-hidden'));
      const activePanel = document.getElementById(targetId);
      if (activePanel) {
        activePanel.classList.remove('tw-hidden');
      }

      // On mobile: scroll active horizontal pill into view & update sheet height
      if (window.innerWidth < 768) {
        try {
          btn.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
          });
        } catch (_) {}
      }
      if (
        window.sheetManager &&
        typeof window.sheetManager.updateMobileHeight === 'function'
      ) {
        window.sheetManager.updateMobileHeight();
      }
    });
  });

  // 3. Inline Name Edit Form Toggle
  const nameEditToggle = document.getElementById('bfwt-name-edit-toggle');
  const nameEditForm = document.getElementById('bfwt-name-edit-form');
  const cancelNameBtn = document.getElementById('bfwt-cancel-name-btn');

  if (nameEditToggle && nameEditForm) {
    nameEditToggle.addEventListener('click', (e) => {
      e.preventDefault();
      nameEditToggle.classList.add('tw-hidden');
      nameEditForm.classList.remove('tw-hidden');
      nameEditForm.classList.add('tw-flex');
      const editNameInput = document.getElementById('bfwt-edit-name-input');
      if (editNameInput && currentUser) {
        editNameInput.value = currentUser.displayName || '';
      }
    });
  }

  if (cancelNameBtn && nameEditForm && nameEditToggle) {
    cancelNameBtn.addEventListener('click', (e) => {
      e.preventDefault();
      nameEditForm.classList.add('tw-hidden');
      nameEditForm.classList.remove('tw-flex');
      nameEditToggle.classList.remove('tw-hidden');
    });
  }

  // 4. Inline Email Change Form Toggle
  const emailChangeToggleBtn = document.getElementById(
    'bfwt-email-change-toggle'
  );
  const emailChangeForm = document.getElementById('bfwt-email-change-form');
  const cancelEmailBtn = document.getElementById('bfwt-cancel-email-btn');

  if (emailChangeToggleBtn && emailChangeForm) {
    emailChangeToggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      emailChangeToggleBtn.classList.add('tw-hidden');
      emailChangeForm.classList.remove('tw-hidden');
      emailChangeForm.classList.add('tw-flex');
      const editEmailInput = document.getElementById('bfwt-edit-email-input');
      const emailPasswordInput = document.getElementById(
        'bfwt-email-password-input'
      );
      if (editEmailInput && currentUser) {
        editEmailInput.value = currentUser.email || '';
      }
      if (emailPasswordInput) {
        emailPasswordInput.value = '';
      }
    });
  }

  if (cancelEmailBtn && emailChangeForm && emailChangeToggleBtn) {
    cancelEmailBtn.addEventListener('click', (e) => {
      e.preventDefault();
      emailChangeForm.classList.add('tw-hidden');
      emailChangeForm.classList.remove('tw-flex');
      emailChangeToggleBtn.classList.remove('tw-hidden');
    });
  }

  // 5. Save Display Name
  if (nameEditForm) {
    nameEditForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!currentUser) return;

      const newName = document
        .getElementById('bfwt-edit-name-input')
        .value.trim();
      const validation = validateName(newName, true);
      if (!validation.valid) {
        if (window.BFWT_Toast) {
          window.BFWT_Toast.error(validation.message);
        } else {
          alert(validation.message);
        }
        return;
      }

      const saveNameBtn = document.getElementById('bfwt-save-name-btn');
      const originalBtnHtml = saveNameBtn.innerHTML;

      try {
        saveNameBtn.disabled = true;
        saveNameBtn.innerHTML = '<span>Saving...</span>';

        // Update Firebase Auth profile
        await updateProfile(currentUser, { displayName: newName });

        // Update Firestore doc
        const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
        await setDoc(
          userDocRef,
          { display_name: newName, custom_handle: newName },
          { merge: true }
        );

        // Sync custom_handle to FitterPass API
        const idToken = await currentUser.getIdToken();
        const fpRes = await fetch('/wp-json/app-api/v1/fitterpass/profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ custom_handle: newName }),
        });
        if (fpRes.ok) {
          const fpJson = await fpRes.json();
          if (fpJson && fpJson.profile) {
            renderFitterPassHub({
              profile: fpJson.profile,
              leaderboard: fpJson.leaderboard || [],
            });
          }
        }

        // Update UI
        await handleAuthStateChanged(auth.currentUser);

        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(
            newName
              ? `Public handle updated to "${newName}".`
              : 'Public handle cleared (showing initials / Member).'
          );
        }
      } catch (err) {
        console.error('Failed to update display name:', err);
        const errorMsg = translateAuthError(err.code);
        if (window.BFWT_Toast) {
          window.BFWT_Toast.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        saveNameBtn.disabled = false;
        saveNameBtn.innerHTML = originalBtnHtml;
      }
    });
  }

  // 6. Save Email Change with Reauthentication and Verification
  if (emailChangeForm) {
    emailChangeForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!currentUser) return;

      const newEmail = document
        .getElementById('bfwt-edit-email-input')
        .value.trim()
        .toLowerCase();
      const password = document.getElementById(
        'bfwt-email-password-input'
      ).value;
      const saveEmailBtn = document.getElementById('bfwt-save-email-btn');
      const originalBtnHtml = saveEmailBtn.innerHTML;

      if (newEmail === currentUser.email?.toLowerCase()) {
        if (window.BFWT_Toast) {
          window.BFWT_Toast.success('No changes to save.');
        }
        return;
      }

      try {
        saveEmailBtn.disabled = true;
        saveEmailBtn.innerHTML = '<span>Updating...</span>';

        // 1. Reauthenticate first
        const credential = EmailAuthProvider.credential(
          currentUser.email,
          password
        );
        await reauthenticateWithCredential(currentUser, credential);

        // 2. Send email update verification link
        await verifyBeforeUpdateEmail(currentUser, newEmail, { url: 'https://budgetfitter.uk/', handleCodeInApp: false });

        // Collapsing email form and showing toggle again
        emailChangeForm.classList.add('tw-hidden');
        emailChangeForm.classList.remove('tw-flex');
        if (emailChangeToggleBtn) {
          emailChangeToggleBtn.classList.remove('tw-hidden');
        }

        // Clear password input field
        const emailPasswordInput = document.getElementById(
          'bfwt-email-password-input'
        );
        if (emailPasswordInput) {
          emailPasswordInput.value = '';
        }

        const successMsg =
          'A verification link has been sent to your new email address. Please check your inbox to complete the change.';
        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(successMsg);
        } else {
          alert(successMsg);
        }
      } catch (err) {
        console.error('Failed to initiate email change:', err);
        const errorMsg = translateAuthError(err.code);
        if (window.BFWT_Toast) {
          window.BFWT_Toast.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        saveEmailBtn.disabled = false;
        saveEmailBtn.innerHTML = originalBtnHtml;
      }
    });
  }

  // 7. Google Account Connect / Disconnect Handler
  const googleLinkBtn = document.getElementById('bfwt-google-link-btn');
  if (googleLinkBtn) {
    googleLinkBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!currentUser) return;

      const isGoogleUser = currentUser.providerData.some(
        (p) => p.providerId === 'google.com'
      );
      const hasOtherProvider = currentUser.providerData.some(
        (p) => p.providerId !== 'google.com'
      );

      googleLinkBtn.disabled = true;
      googleLinkBtn.textContent = isGoogleUser
        ? 'Disconnecting...'
        : 'Connecting...';

      try {
        if (isGoogleUser) {
          // Disconnect (unlink)
          if (!hasOtherProvider) {
            alert(
              'For security reasons, you cannot disconnect your Google account because you have no other sign-in method. Set a password or connect another account first.'
            );
            return;
          }

          const confirmed = confirm(
            'Are you sure you want to disconnect your Google account?'
          );
          if (!confirmed) return;

          await unlink(currentUser, 'google.com');

          if (window.BFWT_Toast) {
            window.BFWT_Toast.success(
              'Google account disconnected successfully.'
            );
          } else {
            alert('Google account disconnected successfully.');
          }
        } else {
          // Connect (link)
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          await linkWithPopup(currentUser, provider);

          if (window.BFWT_Toast) {
            window.BFWT_Toast.success('Google account connected successfully.');
          } else {
            alert('Google account connected successfully.');
          }
        }

        // Refresh UI
        await handleAuthStateChanged(auth.currentUser);
      } catch (err) {
        console.error('Failed to toggle Google link:', err);
        const errorMsg = translateAuthError(err.code);
        if (window.BFWT_Toast) {
          window.BFWT_Toast.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        googleLinkBtn.disabled = false;
        const activeGoogle = currentUser
          ? currentUser.providerData.some((p) => p.providerId === 'google.com')
          : false;
        googleLinkBtn.textContent = activeGoogle ? 'Disconnect' : 'Connect';
      }
    });
  }

  // Toggle Apple Link/Unlink
  const appleLinkBtn = document.getElementById('bfwt-apple-link-btn');
  if (appleLinkBtn) {
    appleLinkBtn.addEventListener('click', async () => {
      if (!currentUser) return;

      const isAppleUser = currentUser.providerData.some(
        (p) => p.providerId === 'apple.com'
      );
      const hasOtherProvider = currentUser.providerData.some(
        (p) => p.providerId !== 'apple.com'
      );

      appleLinkBtn.disabled = true;
      appleLinkBtn.textContent = isAppleUser
        ? 'Disconnecting...'
        : 'Connecting...';

      try {
        if (isAppleUser) {
          // Disconnect (unlink)
          if (!hasOtherProvider) {
            alert(
              'For security reasons, you cannot disconnect your Apple account because you have no other sign-in method. Set a password or connect another account first.'
            );
            return;
          }

          const confirmed = confirm(
            'Are you sure you want to disconnect your Apple account?'
          );
          if (!confirmed) return;

          await unlink(currentUser, 'apple.com');

          if (window.BFWT_Toast) {
            window.BFWT_Toast.success(
              'Apple account disconnected successfully.'
            );
          } else {
            alert('Apple account disconnected successfully.');
          }
        } else {
          // Connect (link)
          const provider = new OAuthProvider('apple.com');
          provider.addScope('email');
          provider.addScope('name');
          await linkWithPopup(currentUser, provider);

          if (window.BFWT_Toast) {
            window.BFWT_Toast.success('Apple account connected successfully.');
          } else {
            alert('Apple account connected successfully.');
          }
        }

        // Refresh UI
        await handleAuthStateChanged(auth.currentUser);
      } catch (err) {
        console.error('Failed to toggle Apple link:', err);
        const errorMsg = translateAuthError(err.code);
        if (window.BFWT_Toast) {
          window.BFWT_Toast.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        appleLinkBtn.disabled = false;
        const activeApple = currentUser
          ? currentUser.providerData.some((p) => p.providerId === 'apple.com')
          : false;
        appleLinkBtn.textContent = activeApple ? 'Disconnect' : 'Connect';
      }
    });
  }

  // 8. Forgot Password Shortcut inside Current Password field
  const emailPasswordForgot = document.getElementById(
    'bfwt-email-password-forgot'
  );
  if (emailPasswordForgot) {
    emailPasswordForgot.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!currentUser || !currentUser.email) return;

      const originalText = emailPasswordForgot.textContent;
      try {
        emailPasswordForgot.disabled = true;
        emailPasswordForgot.textContent = 'Sending...';

        const idToken = await currentUser.getIdToken();
        const res = await fetch('/wp-json/app-api/v1/send-app-password-reset', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            email: currentUser.email,
            source: 'web',
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to send reset email.');
        }

        const successMsg =
          'Password reset email sent. Please check your inbox.';
        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(successMsg);
        } else {
          alert(successMsg);
        }
      } catch (err) {
        console.error('Failed to send reset email in reauth:', err);
        alert(err.message || 'Failed to send reset email. Please try again.');
      } finally {
        emailPasswordForgot.disabled = false;
        emailPasswordForgot.textContent = originalText;
      }
    });
  }

  // 9. Send Password Reset Email (Privacy & Security Panel)
  const sendResetBtn = document.getElementById('bfwt-send-reset-btn');
  if (sendResetBtn) {
    sendResetBtn.addEventListener('click', async () => {
      if (!currentUser || !currentUser.email) return;

      const originalBtnHtml = sendResetBtn.innerHTML;

      try {
        sendResetBtn.disabled = true;
        sendResetBtn.innerHTML = '<span>Sending...</span>';

        const idToken = await currentUser.getIdToken();
        const res = await fetch('/wp-json/app-api/v1/send-app-password-reset', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            email: currentUser.email,
            source: 'web',
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to send reset email.');
        }

        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(
            'Password reset email sent. Please check your inbox.'
          );
        } else {
          alert('Password reset email sent. Please check your inbox.');
        }
      } catch (err) {
        console.error('Failed to send password reset email:', err);
        alert(err.message || 'Failed to send reset email. Please try again.');
      } finally {
        sendResetBtn.disabled = false;
        sendResetBtn.innerHTML = originalBtnHtml;
      }
    });
  }

  // 5. Granular Notification Toggles (4-Toggle Parity with Mobile App)
  syncHeaderFollowedBrandsUI();
  syncSavedDealsUI();
  syncSavedArticlesUI();
  syncRecentBrandsUI();

  const dealPushCheckbox = document.getElementById('bfwt-deal-push-checkbox');
  if (dealPushCheckbox) {
    dealPushCheckbox.addEventListener('change', async () => {
      if (!currentUser) return;
      try {
        if (dealPushCheckbox.checked && 'Notification' in window && Notification.permission === 'default') {
          await _ensureWebPushPermissionAndToken();
        }
        const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
        await setDoc(
          userDocRef,
          {
            notification_preferences_v2: {
              deal_push_enabled: dealPushCheckbox.checked,
              web_enabled: dealPushCheckbox.checked,
              enabled: dealPushCheckbox.checked,
              last_updated: new Date().toISOString(),
            },
          },
          { merge: true }
        );
        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(
            dealPushCheckbox.checked
              ? 'Followed brand deal push alerts enabled.'
              : 'Followed brand deal push alerts muted.'
          );
        }
      } catch (err) {
        console.error('Failed to update deal_push_enabled:', err);
        dealPushCheckbox.checked = !dealPushCheckbox.checked;
      }
    });
  }

  const editorialPushCheckbox = document.getElementById('bfwt-editorial-push-checkbox');
  if (editorialPushCheckbox) {
    editorialPushCheckbox.addEventListener('change', async () => {
      if (!currentUser) return;
      try {
        if (editorialPushCheckbox.checked && 'Notification' in window && Notification.permission === 'default') {
          await _ensureWebPushPermissionAndToken();
        }
        const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
        await setDoc(
          userDocRef,
          {
            notification_preferences_v2: {
              editorial_push_enabled: editorialPushCheckbox.checked,
              last_updated: new Date().toISOString(),
            },
          },
          { merge: true }
        );
        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(
            editorialPushCheckbox.checked
              ? 'Daily Coffee and Pulse push alerts enabled.'
              : 'Daily Coffee and Pulse push alerts muted.'
          );
        }
      } catch (err) {
        console.error('Failed to update editorial_push_enabled:', err);
        editorialPushCheckbox.checked = !editorialPushCheckbox.checked;
      }
    });
  }

  const dealEmailCheckbox = document.getElementById('bfwt-deal-email-checkbox');
  if (dealEmailCheckbox) {
    dealEmailCheckbox.addEventListener('change', async () => {
      if (!currentUser) return;
      try {
        const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
        await setDoc(
          userDocRef,
          {
            notification_preferences_v2: {
              deal_email_enabled: dealEmailCheckbox.checked,
              last_updated: new Date().toISOString(),
            },
          },
          { merge: true }
        );
        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(
            dealEmailCheckbox.checked
              ? 'Deal alert emails enabled.'
              : 'Deal alert emails disabled.'
          );
        }
      } catch (err) {
        console.error('Failed to update deal_email_enabled:', err);
        dealEmailCheckbox.checked = !dealEmailCheckbox.checked;
      }
    });
  }

  const newsletterCheckbox = document.getElementById(
    'bfwt-newsletter-checkbox'
  );
  if (newsletterCheckbox) {
    newsletterCheckbox.addEventListener('change', async () => {
      if (!currentUser) return;

      try {
        const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
        await setDoc(
          userDocRef,
          {
            newsletter_subscribed: newsletterCheckbox.checked,
            notification_preferences_v2: {
              newsletter_enabled: newsletterCheckbox.checked,
              last_updated: new Date().toISOString(),
            },
          },
          { merge: true }
        );

        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(
            newsletterCheckbox.checked
              ? 'Subscribed to the weekly newsletter.'
              : 'Unsubscribed from the weekly newsletter.'
          );
        }
      } catch (err) {
        console.error('Failed to update newsletter preference:', err);
        // Revert checkbox state on error
        newsletterCheckbox.checked = !newsletterCheckbox.checked;
      }
    });
  }

  // 3-Mode Theme Selector Buttons (System / Light / Dark)
  document.querySelectorAll('.bfwt-theme-mode-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const pref = btn.getAttribute('data-theme-pref') || 'system';
      updateThemeModeButtonsUI(pref);
      const resolvedTheme =
        pref === 'system'
          ? window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light'
          : pref;
      lastSyncedTheme = pref;
      applyTheme(resolvedTheme);

      if (currentUser) {
        try {
          const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
          await setDoc(
            userDocRef,
            {
              theme_preference: pref,
              theme: pref,
              color_mode: resolvedTheme,
            },
            { merge: true }
          );
          if (window.BFWT_Toast) {
            window.BFWT_Toast.success(`Theme preference set to ${pref}.`);
          }
        } catch (err) {
          console.error('Failed to sync 3-mode theme preference:', err);
        }
      }
    });
  });

  // 6. Web Push Notifications Toggle
  const webpushCheckbox = document.getElementById('bfwt-webpush-checkbox');
  if (webpushCheckbox) {
    webpushCheckbox.addEventListener('change', async () => {
      if (!currentUser) return;

      if (webpushCheckbox.checked) {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
          alert('Web Push notifications are not supported in this browser.');
          webpushCheckbox.checked = false;
          return;
        }

        try {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            alert('Browser notification permission was denied. Please allow notifications in browser settings.');
            webpushCheckbox.checked = false;
            return;
          }

          let registration = null;
          try {
            await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
            registration = await navigator.serviceWorker.ready;
            console.log('[Firebase] Service Worker registered and READY for Web Push:', registration);
          } catch (swErr) {
            console.warn('[Firebase] Service Worker registration warning:', swErr);
          }

          let fcmToken = null;
          if (messaging) {
            try {
              const options = {
                vapidKey: 'BPJIKu-K2ysB8UNAp-S9T-p453waSiwth_PLd14mtEO7By9PW5N-6yByMlFxfyw0aImPD4cwpWDufLvyiobdMSo',
              };
              if (registration) {
                options.serviceWorkerRegistration = registration;
              }
              fcmToken = await getToken(messaging, options);
              console.log('[Firebase] Web FCM Token acquired on toggle:', fcmToken);
            } catch (tErr) {
              console.error('[Firebase] Could not fetch FCM token on toggle:', tErr);
              if (window.BFWT_Toast) {
                window.BFWT_Toast.error('FCM Token Fetch Error: ' + (tErr.message || tErr));
              }
            }
          } else {
            console.error('[Firebase] Messaging instance is null!');
            if (window.BFWT_Toast) {
              window.BFWT_Toast.error('Firebase Messaging is not initialized on site!');
            }
          }

          const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
          const updateObj = {
            notification_preferences_v2: {
              web_enabled: true,
              enabled: true,
              last_updated: new Date().toISOString(),
            },
          };

          if (fcmToken) {
            updateObj.fcm_tokens_web = arrayUnion(fcmToken);
          }

          await setDoc(userDocRef, updateObj, { merge: true });

          if (window.BFWT_Toast) {
            window.BFWT_Toast.success('Browser push notifications enabled! 🔔');
          }
        } catch (err) {
          console.error('Failed to enable Web Push:', err);
          webpushCheckbox.checked = false;
          alert('Could not enable Web Push: ' + (err.message || err));
        }
      } else {
        try {
          const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
          await setDoc(
            userDocRef,
            {
              notification_preferences_v2: {
                web_enabled: false,
                last_updated: new Date().toISOString(),
              },
            },
            { merge: true }
          );

          if (window.BFWT_Toast) {
            window.BFWT_Toast.success('Browser push notifications disabled.');
          }
        } catch (err) {
          console.error('Failed to disable Web Push:', err);
          webpushCheckbox.checked = true;
        }
      }
    });
  }

  // Email Verification Resend Click Handler
  const resendVerificationBtn = document.getElementById(
    'bfwt-resend-verification-btn'
  );
  if (resendVerificationBtn) {
    resendVerificationBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!currentUser) return;

      const originalText = resendVerificationBtn.textContent;
      try {
        resendVerificationBtn.disabled = true;
        resendVerificationBtn.textContent = 'Sending...';

        const idToken = await currentUser.getIdToken();
        const res = await fetch('/wp-json/app-api/v1/send-app-verification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            email: currentUser.email,
            source: 'web',
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to send verification email.');
        }

        const successMsg =
          'Verification link has been sent to your email address. Please check your inbox.';
        if (window.BFWT_Toast) {
          window.BFWT_Toast.success(successMsg);
        } else {
          alert(successMsg);
        }
      } catch (err) {
        console.error('Failed to send verification email:', err);
        alert(
          err.message || 'Failed to send verification email. Please try again.'
        );
      } finally {
        resendVerificationBtn.disabled = false;
        resendVerificationBtn.textContent = originalText;
      }
    });
  }

  // Eco-Efficiency Theme Preference Change Handler
  const themeCheckbox = document.getElementById('bfwt-theme-checkbox');
  if (themeCheckbox) {
    themeCheckbox.addEventListener('change', () => {
      const theme = themeCheckbox.checked ? 'dark' : 'light';
      applyTheme(theme);
    });
  }

  // Global Theme Preference Change Sync to Firestore
  window.addEventListener('bfwt:themechange', async (e) => {
    const theme = e.detail ? e.detail.theme : null;
    if (
      (theme === 'dark' || theme === 'light') &&
      currentUser &&
      theme !== lastSyncedTheme
    ) {
      try {
        lastSyncedTheme = theme;
        const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
        await setDoc(
          userDocRef,
          {
            theme_preference: theme,
            theme: theme,
            color_mode: theme,
          },
          { merge: true }
        );

        if (window.BFWT_Toast) {
          window.BFWT_Toast.success('Theme preference synced.');
        }
      } catch (err) {
        console.error('Failed to sync theme preference to Firestore:', err);
      }
    }
  });

  // 6. Delete Account Request (Danger Zone)
  const deleteAccountBtn = document.getElementById('bfwt-delete-account-btn');
  const deleteBackBtn = document.getElementById('bfwt-delete-back-btn');
  const confirmDeleteBtn = document.getElementById('bfwt-confirm-delete-btn');
  const cancelDeleteBtn = document.getElementById('bfwt-cancel-delete-btn');

  if (deleteAccountBtn) {
    deleteAccountBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (!currentUser) return;

      // Switch to confirmation wizard panel
      panels.forEach((p) => p.classList.add('tw-hidden'));
      const deletePanel = document.getElementById(
        'account-panel-delete-confirm'
      );
      if (deletePanel) {
        deletePanel.classList.remove('tw-hidden');
      }

      // Check if user is signed in with Google or Apple to show warning
      const isGoogle = currentUser.providerData.some(
        (p) => p.providerId === 'google.com'
      );
      const isApple = currentUser.providerData.some(
        (p) => p.providerId === 'apple.com'
      );
      const socialWarning = document.getElementById(
        'bfwt-delete-social-warning'
      );
      if (socialWarning) {
        if (isGoogle || isApple) {
          socialWarning.classList.remove('tw-hidden');
          socialWarning.classList.add('tw-flex');
          const warningText = socialWarning.querySelector('p');
          if (warningText) {
            let text =
              'Note: You may also need to manually revoke access from your ';
            if (isGoogle && isApple) {
              text += 'Google and Apple account settings ';
            } else if (isGoogle) {
              text += 'Google account settings ';
            } else {
              text += 'Apple account settings ';
            }
            text += 'to fully disconnect BudgetFitter.';
            warningText.textContent = text;
          }
        } else {
          socialWarning.classList.remove('tw-flex');
          socialWarning.classList.add('tw-hidden');
        }
      }

      // Reset newsletter checkbox to unchecked by default (matches app)
      const deleteKeepNewsletter = document.getElementById(
        'bfwt-delete-keep-newsletter'
      );
      if (deleteKeepNewsletter) {
        deleteKeepNewsletter.checked = false;
      }
    });
  }

  const showPrivacyPanel = (e) => {
    if (e) e.preventDefault();
    panels.forEach((p) => p.classList.add('tw-hidden'));
    const privacyPanel = document.getElementById('account-panel-privacy');
    if (privacyPanel) {
      privacyPanel.classList.remove('tw-hidden');
    }
  };

  if (deleteBackBtn) {
    deleteBackBtn.addEventListener('click', showPrivacyPanel);
  }

  if (cancelDeleteBtn) {
    cancelDeleteBtn.addEventListener('click', showPrivacyPanel);
  }

  if (confirmDeleteBtn) {
    confirmDeleteBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!currentUser) return;

      try {
        confirmDeleteBtn.disabled = true;
        confirmDeleteBtn.textContent = 'Deleting...';

        const deleteKeepNewsletter = document.getElementById(
          'bfwt-delete-keep-newsletter'
        );
        const keepNewsletter = deleteKeepNewsletter
          ? deleteKeepNewsletter.checked
          : false;

        // Write to dev_account_deletion_requests collection (triggers background Cloud Function)
        const deletionRequestDocRef = doc(
          db,
          'dev_account_deletion_requests',
          currentUser.uid
        );
        await setDoc(deletionRequestDocRef, {
          requested_at: serverTimestamp(),
          status: 'pending',
          keep_newsletter: keepNewsletter,
        });

        // Sign out the user and clear session cookies cleanly
        await signOut(auth);

        try {
          sessionStorage.setItem(
            'bfwt_toast_success',
            'Your account deletion request has been scheduled (30-day grace period). You have been signed out.'
          );
        } catch (_) {}

        if (window.sheetManager) {
          window.sheetManager.closeAll();
        }
        window.location.reload();
      } catch (err) {
        console.error('Failed to request account deletion:', err);
        alert('Failed to request account deletion. Please try again.');
        confirmDeleteBtn.disabled = false;
        confirmDeleteBtn.textContent = 'Delete My Account';
      }
    });
  }

  // 7. Logout Event Bindings (mobile footer + desktop sidebar)
  const logoutBtn = document.getElementById('bfwt-account-logout');
  if (logoutBtn) logoutBtn.addEventListener('click', logoutHandler);

  const logoutSidebarBtn = document.getElementById(
    'bfwt-account-logout-sidebar'
  );
  if (logoutSidebarBtn)
    {logoutSidebarBtn.addEventListener('click', logoutHandler);}

  // ── Watchlist Icon Click Interceptor (Redirect to Auth sheet or toggle Follow) ──
  document.body.addEventListener('click', async (e) => {
    if (!(e.target instanceof Element)) return;

    const watchlistIcon = e.target.closest('.bfwt-watchlist-icon');
    if (watchlistIcon) {
      // Check if it's the brand follow button (or not on a deal row)
      const isBrandFollow =
        watchlistIcon.classList.contains('bfwt-brand-header__watchlist-icon') ||
        !watchlistIcon.closest('.bfwt-deal-row-inner');

      const activeUser = (typeof currentUser !== 'undefined' && currentUser) || (typeof auth !== 'undefined' && auth ? auth.currentUser : null);

      if (!activeUser) {
        e.preventDefault();
        e.stopPropagation();
        const brandId = watchlistIcon.getAttribute('data-dealid') || '';
        const brandName = watchlistIcon.getAttribute('data-brand-name') || '';
        const brandSlug = watchlistIcon.getAttribute('data-brand-slug') || '';
        pendingGuestAction = {
          type: 'follow_brand',
          brandId,
          brandName,
          brandSlug,
        };
        // Not logged in: open account sheet in register mode with Watchlist encouragement context
        openAuthModal('register', {
          trigger: 'brand_watchlist',
          brand_name: brandName,
          brand_slug: brandSlug,
        });
      } else if (isBrandFollow) {
        e.preventDefault();
        e.stopPropagation();

        const brandId = watchlistIcon.getAttribute('data-dealid');
        const brandName =
          watchlistIcon.getAttribute('data-brand-name') || 'Brand';
        if (!brandId) return;

        try {
          watchlistIcon.style.pointerEvents = 'none'; // prevent double clicks

          const isFollowed = userFollowedBrandIds.has(brandId.toString()) || watchlistIcon.classList.contains('followed') || watchlistIcon.classList.contains('tw-bg-accent');

          if (isFollowed) {
            const confirmed = await confirmUnsaveModal({
              title: `Unfollow ${brandName}?`,
              message: `Are you sure you want to remove ${brandName} from Followed Brands in your Vault?`,
              confirmText: 'Unfollow Brand',
              cancelText: 'Keep Following',
              icon: 'unfollow_brand',
            });
            if (!confirmed) return;
            userFollowedBrandIds.delete(brandId.toString());
            updateWatchlistCookie();
            syncWatchlistButtonsState();
            try { window.postMessage({ type: 'BFWT_FOLLOW_UPDATED', brandId: brandId.toString(), action: 'unfollow' }, '*'); } catch (_) {}

            await Promise.allSettled([
              deleteDoc(doc(db, USERS_COLLECTION, activeUser.uid, 'favorite_brands', brandId.toString())),
              updateDoc(doc(db, USERS_COLLECTION, activeUser.uid), { favorite_brands: arrayRemove(brandId.toString(), Number(brandId)) })
            ]);
            if (window.BFWT_Toast) {
              window.BFWT_Toast.info(
                `Removed <strong>${brandName}</strong> from Followed Brands in your Vault.`,
                { icon: 'unfollow_brand' }
              );
            }
          } else {
            userFollowedBrandIds.add(brandId.toString());
            updateWatchlistCookie();
            syncWatchlistButtonsState();
            try { window.postMessage({ type: 'BFWT_FOLLOW_UPDATED', brandId: brandId.toString(), action: 'follow' }, '*'); } catch (_) {}

            let brandData = {};
            try {
              const response = await fetch(`/wp-json/app-api/v1/brands/${brandId}`);
              if (response.ok) {
                brandData = await response.json();
              }
            } catch (_) {}

            // Prepare brand payload for Firestore
            const brandDataForFirestore = {
              id: Number(brandId),
              ...brandData,
              timestamp: serverTimestamp(),
            };

            await Promise.allSettled([
              setDoc(doc(db, USERS_COLLECTION, activeUser.uid, 'favorite_brands', brandId.toString()), brandDataForFirestore, { merge: true }),
              updateDoc(doc(db, USERS_COLLECTION, activeUser.uid), { favorite_brands: arrayUnion(brandId.toString()) })
            ]);

            if (window.BFWT_Toast) {
              window.BFWT_Toast.success(
                `Added <strong>${brandName}</strong> to Followed Brands in your Vault.`,
                { icon: 'following_brand' }
              );
            }
            recordWebFitterPassEvent('brand_follow', String(brandId));
          }
        } catch (err) {
          console.error('Watchlist toggle error:', err);
          if (window.BFWT_Toast) {
            window.BFWT_Toast.error(err?.message || 'An error occurred. Please try again.');
          }
        } finally {
          watchlistIcon.style.pointerEvents = '';
        }
      }
    }

    // ── Save Deal Button Click (on Deal Cards) ──
        const dealCtaEl = e.target.closest('.bfwt-cta-button, .bfwt-code-container');
    if (dealCtaEl && currentUser) {
      const rowEl = dealCtaEl.closest('.bfwt-deal-row-inner, .bfwt-deal-row, .bfwt-deal-card');
      const saveBtn = rowEl ? rowEl.querySelector('.bfwt-save-deal-btn') : null;
      const bId = (rowEl && (rowEl.getAttribute('data-brand-id') || rowEl.getAttribute('data-brand_id') || rowEl.getAttribute('data-brand-slug'))) || (saveBtn && (saveBtn.getAttribute('data-brand-id') || saveBtn.getAttribute('data-brand-slug') || saveBtn.getAttribute('data-deal-id'))) || '';
      if (bId) {
        recordWebFitterPassEvent('deal_click', String(bId));
      }
    }
    const saveDealBtn = e.target.closest('.bfwt-save-deal-btn');
    if (saveDealBtn) {
      e.preventDefault();
      e.stopPropagation();

      const activeUser =
        (typeof currentUser !== 'undefined' && currentUser) ||
        (typeof auth !== 'undefined' && auth ? auth.currentUser : null);

      const dealId = Number(saveDealBtn.getAttribute('data-deal-id') || 0);
      if (!dealId) return;

      const brandName = saveDealBtn.getAttribute('data-brand-name') || '';
      const brandSlug = saveDealBtn.getAttribute('data-brand-slug') || '';

      if (!activeUser) {
        const dealPayload = buildDealPayloadFromButton(saveDealBtn, dealId, brandName, brandSlug);
        pendingGuestAction = {
          type: 'save_deal',
          dealId,
          brandName,
          brandSlug,
          dealPayload,
        };
        openAuthModal('register', {
          trigger: 'save_deal',
          brand_name: brandName,
          brand_slug: brandSlug,
          deal_title: dealPayload.title,
        });
        return;
      }

      try {
        saveDealBtn.style.pointerEvents = 'none';
        const existingIndex = userSavedDeals.findIndex(
          (d) => Number(d.id) === dealId
        );

        if (existingIndex >= 0) {
          const confirmed = await confirmUnsaveModal({
            title: 'Remove from Your Vault?',
            message: 'Are you sure you want to remove this offer from Saved Deals in your Vault?',
            confirmText: 'Remove from Vault',
            cancelText: 'Keep in Vault',
            icon: 'remove_saved',
          });
          if (!confirmed) return;
          userSavedDeals.splice(existingIndex, 1);
          syncSavedDealsUI();
          await setDoc(
            doc(db, USERS_COLLECTION, activeUser.uid),
            { saved_deals: userSavedDeals },
            { merge: true }
          );
          if (window.BFWT_Toast) {
            window.BFWT_Toast.info('Removed from Saved Deals in your Vault.', { icon: 'remove_saved' });
          }
        } else {
          const dealRow = saveDealBtn.closest('.bfwt-deal-row-inner, .bfwt-deal-row');
          const dealCard = saveDealBtn.closest('.bfwt-deal-card');
          const domTitle = dealRow?.querySelector('.bfwt-short-title')?.textContent?.trim() || '';
          const domDesc = dealRow?.querySelector('.bfwt-deal-description')?.textContent?.trim() || '';
          const resolvedCode =
            saveDealBtn.getAttribute('data-deal-code') ||
            (dealRow ? (dealRow.getAttribute('data-deal-code') || dealRow.getAttribute('data-promo')) : '') ||
            null;
          const dealPayload = {
            id: dealId,
            title:
              saveDealBtn.getAttribute('data-deal-title') ||
              (dealRow ? (dealRow.getAttribute('data-deal-title') || dealRow.getAttribute('data-shorttitle')) : '') ||
              domTitle ||
              'Verified Offer',
            description:
              saveDealBtn.getAttribute('data-deal-desc') ||
              (dealRow ? dealRow.getAttribute('data-deal-desc') : '') ||
              domDesc ||
              '',
            type:
              saveDealBtn.getAttribute('data-deal-type') ||
              (dealCard ? dealCard.getAttribute('data-deal-type') : '') ||
              (dealRow ? dealRow.getAttribute('data-deal-type') : '') ||
              (resolvedCode ? 'code' : 'deal'),
            code: resolvedCode,
            referral_link:
              saveDealBtn.getAttribute('data-referral-link') ||
              (dealRow ? (dealRow.getAttribute('data-referral-link') || dealRow.getAttribute('data-ctalink')) : null) ||
              null,
            expiration_date:
              saveDealBtn.getAttribute('data-expiration-date') ||
              (dealRow ? dealRow.getAttribute('data-expiration-date') : null) ||
              null,
            brand_id:
              Number(
                saveDealBtn.getAttribute('data-brand-id') ||
                (dealRow ? (dealRow.getAttribute('data-brand-id') || dealRow.getAttribute('data-brand_id')) : 0) ||
                0
              ) || null,
            brand_name:
              brandName ||
              (dealRow ? dealRow.getAttribute('data-brand-name') : '') ||
              null,
            brand_slug:
              brandSlug ||
              (dealRow ? dealRow.getAttribute('data-brand-slug') : '') ||
              null,
            brand_logo:
              saveDealBtn.getAttribute('data-brand-logo') ||
              (dealRow ? dealRow.getAttribute('data-brand-logo') : null) ||
              null,
            saved_at: new Date().toISOString(),
          };

          userSavedDeals.unshift(dealPayload);
          if (userSavedDeals.length > 100) {
            userSavedDeals = userSavedDeals.slice(0, 100);
          }
          syncSavedDealsUI();
          await setDoc(
            doc(db, USERS_COLLECTION, activeUser.uid),
            { saved_deals: userSavedDeals },
            { merge: true }
          );
          if (window.BFWT_Toast) {
            window.BFWT_Toast.success(
              'Added to Saved Deals in your Vault.',
              { icon: 'vault_deals' }
            );
          }
        }
      } catch (err) {
        console.error('Save deal toggle error:', err);
      } finally {
        saveDealBtn.style.pointerEvents = '';
      }
      return;
    }

    // ── Remove Saved Deal Button (inside Account Hub) ──
    const removeSavedDealBtn = e.target.closest('.bfwt-remove-saved-deal');
    if (removeSavedDealBtn) {
      e.preventDefault();
      const activeUser =
        (typeof currentUser !== 'undefined' && currentUser) ||
        (typeof auth !== 'undefined' && auth ? auth.currentUser : null);
      if (!activeUser) return;

      const dealId = Number(
        removeSavedDealBtn.getAttribute('data-deal-id') || 0
      );
      if (!dealId) return;

      userSavedDeals = userSavedDeals.filter((d) => Number(d.id) !== dealId);
      syncSavedDealsUI();
      try {
        await setDoc(
          doc(db, USERS_COLLECTION, activeUser.uid),
          { saved_deals: userSavedDeals },
          { merge: true }
        );
        if (window.BFWT_Toast) {
          window.BFWT_Toast.info('Removed from Saved Deals in your Vault.', { icon: 'remove_saved' });
        }
      } catch (err) {
        console.error('Remove saved deal error:', err);
      }
      return;
    }

    // ── Save Article Button Click (on News & Reviews Single Post) ──
    const saveArticleBtn = e.target.closest('.bfwt-save-article-btn');
    if (saveArticleBtn) {
      e.preventDefault();
      e.stopPropagation();

      const activeUser =
        (typeof currentUser !== 'undefined' && currentUser) ||
        (typeof auth !== 'undefined' && auth ? auth.currentUser : null);

      if (!activeUser) {
        const articlePayload = buildArticlePayloadFromButton(saveArticleBtn);
        pendingGuestAction = {
          type: 'save_article',
          articlePayload,
        };
        openAuthModal('register', {
          trigger: 'save_article',
          article_title: articlePayload.title,
        });
        return;
      }

      const id = Number(saveArticleBtn.getAttribute('data-article-id') || 0);
      const slug = saveArticleBtn.getAttribute('data-article-slug') || '';
      const type = saveArticleBtn.getAttribute('data-article-type') || 'news';
      if (!id && !slug) return;

      try {
        saveArticleBtn.style.pointerEvents = 'none';
        const existingIndex = userSavedArticles.findIndex(
          (a) =>
            (id && Number(a.id) === id && (a.type || 'news') === type) ||
            (slug && a.slug === slug)
        );

        if (existingIndex >= 0) {
          const confirmed = await confirmUnsaveModal({
            title: 'Remove from Your Vault?',
            message: 'Are you sure you want to remove this guide from Saved Guides in your Vault?',
            confirmText: 'Remove from Vault',
            cancelText: 'Keep in Vault',
            icon: 'remove_saved',
          });
          if (!confirmed) return;
          userSavedArticles.splice(existingIndex, 1);
          syncSavedArticlesUI();
          await setDoc(
            doc(db, USERS_COLLECTION, activeUser.uid),
            { saved_articles: userSavedArticles },
            { merge: true }
          );
          if (window.BFWT_Toast) {
            window.BFWT_Toast.info('Removed from Saved Guides in your Vault.', { icon: 'remove_saved' });
          }
        } else {
          const rawImg = saveArticleBtn.getAttribute('data-article-image') || '';
          const rawCat = saveArticleBtn.getAttribute('data-article-category') || (type === 'review' ? 'Review' : 'News');
          const rawMins = Number(saveArticleBtn.getAttribute('data-article-read-time') || 3);
          const rawDate = saveArticleBtn.getAttribute('data-article-date') || new Date().toISOString();
          const articlePayload = {
            id: id,
            slug: slug,
            title:
              saveArticleBtn.getAttribute('data-article-title') ||
              document.title,
            excerpt: saveArticleBtn.getAttribute('data-article-excerpt') || '',
            image_url: rawImg,
            featuredImageUrl: rawImg || null,
            date_iso: rawDate,
            publishedAt: rawDate,
            read_time: `${rawMins} min read`,
            readingTimeMinutes: rawMins,
            category_name: rawCat,
            categoryName: rawCat,
            badge_label: type === 'review' ? 'Review' : 'News',
            type: type === 'review' ? 'review' : 'news',
            savedAt: new Date().toISOString(),
          };

          userSavedArticles.unshift(articlePayload);
          if (userSavedArticles.length > 100) {
            userSavedArticles = userSavedArticles.slice(0, 100);
          }
          syncSavedArticlesUI();
          await setDoc(
            doc(db, USERS_COLLECTION, activeUser.uid),
            { saved_articles: userSavedArticles },
            { merge: true }
          );
          if (window.BFWT_Toast) {
            window.BFWT_Toast.success('Added to Saved Guides in your Vault.', { icon: 'vault_guides' });
          }
        }
      } catch (err) {
        console.error('Save article toggle error:', err);
      } finally {
        saveArticleBtn.style.pointerEvents = '';
      }
      return;
    }

    // ── Remove Saved Article Button (inside Account Hub) ──
    const removeSavedArticleBtn = e.target.closest(
      '.bfwt-remove-saved-article'
    );
    if (removeSavedArticleBtn) {
      e.preventDefault();
      const activeUser =
        (typeof currentUser !== 'undefined' && currentUser) ||
        (typeof auth !== 'undefined' && auth ? auth.currentUser : null);
      if (!activeUser) return;

      const id = Number(
        removeSavedArticleBtn.getAttribute('data-article-id') || 0
      );
      const slug =
        removeSavedArticleBtn.getAttribute('data-article-slug') || '';
      const type =
        removeSavedArticleBtn.getAttribute('data-article-type') || 'news';

      userSavedArticles = userSavedArticles.filter(
        (a) =>
          !(
            (id && Number(a.id) === id && (a.type || 'news') === type) ||
            (slug && a.slug === slug)
          )
      );
      syncSavedArticlesUI();
      try {
        await setDoc(
          doc(db, USERS_COLLECTION, activeUser.uid),
          { saved_articles: userSavedArticles },
          { merge: true }
        );
        if (window.BFWT_Toast) {
          window.BFWT_Toast.info('Removed from Saved Guides in your Vault.', { icon: 'remove_saved' });
        }
      } catch (err) {
        console.error('Remove saved article error:', err);
      }
      return;
    }
  });
});

// External Extension Sync Handler
async function handleExtensionToggleFollow(brandId, action) {
  const activeUser = (typeof currentUser !== 'undefined' && currentUser) || (typeof auth !== 'undefined' && auth ? auth.currentUser : null);
  if (!brandId || !activeUser) {
    console.warn('BFWT_TOGGLE_FOLLOW ignored: missing brandId or activeUser', { brandId, activeUser });
    return;
  }

  try {
    if (action === 'unfollow') {
      await Promise.allSettled([
        deleteDoc(doc(db, USERS_COLLECTION, activeUser.uid, 'favorite_brands', brandId.toString())),
        updateDoc(doc(db, USERS_COLLECTION, activeUser.uid), { favorite_brands: arrayRemove(brandId.toString(), Number(brandId)) })
      ]);
      if (window.BFWT_Toast) {
        window.BFWT_Toast.info('Removed from Followed Brands in your Vault.', { icon: 'unfollow_brand' });
      }
    } else {
      let brandData = {};
      try {
        const response = await fetch(`/wp-json/app-api/v1/brands/${brandId}`);
        if (response.ok) {
          brandData = await response.json();
        }
      } catch (_) {}

      const payload = {
        id: Number(brandId),
        ...brandData,
        timestamp: serverTimestamp(),
      };

      await Promise.allSettled([
        setDoc(doc(db, USERS_COLLECTION, activeUser.uid, 'favorite_brands', brandId.toString()), payload, { merge: true }),
        updateDoc(doc(db, USERS_COLLECTION, activeUser.uid), { favorite_brands: arrayUnion(brandId.toString()) })
      ]);

      if (window.BFWT_Toast) {
        window.BFWT_Toast.success('Added to Followed Brands in your Vault.', { icon: 'following_brand' });
      }
    }
  } catch (err) {
    console.error('handleExtensionToggleFollow error:', err);
  }
}

window.addEventListener('message', (e) => {
  if (e.data && e.data.type === 'BFWT_TOGGLE_FOLLOW') {
    handleExtensionToggleFollow(e.data.brandId, e.data.action);
  }
});

document.addEventListener('bfwt:toggle-follow', (e) => {
  if (e.detail && e.detail.brandId) {
    handleExtensionToggleFollow(e.detail.brandId, e.detail.action);
  }
});

let pendingGuestAction = null;

function buildDealPayloadFromButton(saveDealBtn, dealId, brandName, brandSlug) {
  const dealRow = saveDealBtn.closest('.bfwt-deal-row-inner, .bfwt-deal-row');
  const dealCard = saveDealBtn.closest('.bfwt-deal-card');
  const domTitle = dealRow?.querySelector('.bfwt-short-title')?.textContent?.trim() || '';
  const domDesc = dealRow?.querySelector('.bfwt-deal-description')?.textContent?.trim() || '';
  const resolvedCode =
    saveDealBtn.getAttribute('data-deal-code') ||
    (dealRow ? (dealRow.getAttribute('data-deal-code') || dealRow.getAttribute('data-promo')) : '') ||
    null;
  return {
    id: dealId,
    title:
      saveDealBtn.getAttribute('data-deal-title') ||
      (dealRow ? (dealRow.getAttribute('data-deal-title') || dealRow.getAttribute('data-shorttitle')) : '') ||
      domTitle ||
      'Verified Offer',
    description:
      saveDealBtn.getAttribute('data-deal-desc') ||
      (dealRow ? dealRow.getAttribute('data-deal-desc') : '') ||
      domDesc ||
      '',
    type:
      saveDealBtn.getAttribute('data-deal-type') ||
      (dealCard ? dealCard.getAttribute('data-deal-type') : '') ||
      (dealRow ? dealRow.getAttribute('data-deal-type') : '') ||
      (resolvedCode ? 'code' : 'deal'),
    code: resolvedCode,
    referral_link:
      saveDealBtn.getAttribute('data-referral-link') ||
      (dealRow ? (dealRow.getAttribute('data-referral-link') || dealRow.getAttribute('data-ctalink')) : null) ||
      null,
    expiration_date:
      saveDealBtn.getAttribute('data-expiration-date') ||
      (dealRow ? dealRow.getAttribute('data-expiration-date') : null) ||
      null,
    brand_id:
      Number(
        saveDealBtn.getAttribute('data-brand-id') ||
        (dealRow ? (dealRow.getAttribute('data-brand-id') || dealRow.getAttribute('data-brand_id')) : 0) ||
        0
      ) || null,
    brand_name:
      brandName ||
      (dealRow ? dealRow.getAttribute('data-brand-name') : '') ||
      null,
    brand_slug:
      brandSlug ||
      (dealRow ? dealRow.getAttribute('data-brand-slug') : '') ||
      null,
    brand_logo:
      saveDealBtn.getAttribute('data-brand-logo') ||
      (dealRow ? dealRow.getAttribute('data-brand-logo') : null) ||
      null,
    saved_at: new Date().toISOString(),
  };
}

function buildArticlePayloadFromButton(saveArticleBtn) {
  const id = Number(saveArticleBtn.getAttribute('data-article-id') || 0);
  const slug = saveArticleBtn.getAttribute('data-article-slug') || '';
  const type = saveArticleBtn.getAttribute('data-article-type') || 'news';
  const rawImg = saveArticleBtn.getAttribute('data-article-image') || '';
  const rawCat = saveArticleBtn.getAttribute('data-article-category') || (type === 'review' ? 'Review' : 'News');
  const rawMins = Number(saveArticleBtn.getAttribute('data-article-read-time') || 3);
  const rawDate = saveArticleBtn.getAttribute('data-article-date') || new Date().toISOString();
  return {
    id,
    slug,
    title: saveArticleBtn.getAttribute('data-article-title') || document.title,
    excerpt: saveArticleBtn.getAttribute('data-article-excerpt') || '',
    image_url: rawImg,
    featuredImageUrl: rawImg || null,
    date_iso: rawDate,
    publishedAt: rawDate,
    read_time: `${rawMins} min read`,
    readingTimeMinutes: rawMins,
    category_name: rawCat,
    categoryName: rawCat,
    badge_label: type === 'review' ? 'Review' : 'News',
    type: type === 'review' ? 'review' : 'news',
    savedAt: new Date().toISOString(),
  };
}

async function executePendingGuestAction(user) {
  if (!user || !pendingGuestAction || !db) return null;
  const action = pendingGuestAction;
  pendingGuestAction = null;

  try {
    if (action.type === 'follow_brand' && action.brandId) {
      const brandId = String(action.brandId);
      const brandName = action.brandName || 'this brand';
      userFollowedBrandIds.add(brandId);
      updateWatchlistCookie();
      let brandData = {};
      try {
        const response = await fetch(`/wp-json/app-api/v1/brands/${brandId}`);
        if (response.ok) {
          brandData = await response.json();
        }
      } catch (_) {}
      await Promise.allSettled([
        setDoc(doc(db, USERS_COLLECTION, user.uid, 'favorite_brands', brandId), {
          id: Number(brandId),
          ...brandData,
          timestamp: serverTimestamp(),
        }, { merge: true }),
        setDoc(doc(db, USERS_COLLECTION, user.uid), { favorite_brands: arrayUnion(brandId) }, { merge: true }),
      ]);
      return `Welcome! Added ${brandName} to Followed Brands in your Vault.`;
    }

    if (action.type === 'save_deal' && action.dealPayload && action.dealPayload.id) {
      await setDoc(
        doc(db, USERS_COLLECTION, user.uid),
        { saved_deals: arrayUnion(action.dealPayload) },
        { merge: true }
      );
      return 'Welcome! Added to Saved Deals in your Vault.';
    }

    if (action.type === 'save_article' && action.articlePayload && (action.articlePayload.id || action.articlePayload.slug)) {
      await setDoc(
        doc(db, USERS_COLLECTION, user.uid),
        { saved_articles: arrayUnion(action.articlePayload) },
        { merge: true }
      );
      return 'Welcome! Added to Saved Guides in your Vault.';
    }

    if (action.type === 'claim_deal_lock') {
      const targetBrand = action.brandName ? `${action.brandName} ` : '';
      return `Welcome! ${targetBrand}Members-Only Perk unlocked — click the offer to reveal your code.`;
    }
  } catch (err) {
    console.warn('executePendingGuestAction error:', err);
  }
  return null;
}

function setSvgIconKey(containerEl, attrName, activeKey) {
  if (!containerEl) return;
  const spans = containerEl.querySelectorAll(`[${attrName}]`);
  if (!spans.length) return;
  let matched = false;
  spans.forEach((sp) => {
    if (sp.getAttribute(attrName) === activeKey) {
      sp.classList.remove('tw-hidden');
      matched = true;
    } else {
      sp.classList.add('tw-hidden');
    }
  });
  if (!matched && spans[0]) {
    spans[0].classList.remove('tw-hidden');
  }
}

function updateAuthEncouragementBanner(context) {
  const banner = document.getElementById('bfwt-auth-context-banner');
  const bannerIcon = document.getElementById('bfwt-auth-context-icon');
  const bannerEyebrow = document.getElementById('bfwt-auth-context-eyebrow');
  const bannerTitle = document.getElementById('bfwt-auth-context-title');
  const bannerDesc = document.getElementById('bfwt-auth-context-desc');
  const bannerPills = document.getElementById('bfwt-auth-context-pills');

  const promoIcon = document.getElementById('bfwt-auth-promo-icon');
  const promoEyebrow = document.getElementById('bfwt-auth-promo-eyebrow');
  const promoTitle = document.getElementById('bfwt-auth-promo-title');
  const promoDesc = document.getElementById('bfwt-auth-promo-desc');
  const loginSubmitLabel = document.getElementById('bfwt-login-submit-label');
  const registerSubmitLabel = document.getElementById('bfwt-register-submit-label');

  const trigger = context?.trigger || '';
  const stripMd = (s) => String(s || '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*\*/g, '').trim();
  const brandName = stripMd(context?.brand_name);
  const itemTitle = stripMd(context?.item_title || context?.article_title);

  let recentBrandNames = [];
  try {
    const recentList = Array.isArray(userRecentBrands) && userRecentBrands.length
      ? userRecentBrands
      : loadLocalRecentBrands();
    recentBrandNames = recentList
      .map((b) => (b?.title || b?.name || '').trim())
      .filter(Boolean)
      .slice(0, 2);
  } catch (_) {}
  const hasRecent = recentBrandNames.length > 0;
  const recentText = recentBrandNames.join(' & ');

  let iconKey = 'auto_awesome';
  let eyebrow = 'FREE UK MEMBERSHIP · PERSONAL VAULT';
  let title = hasRecent
    ? `Track ${recentBrandNames[0]} & Stop Paying the Lazy Tax`
    : 'Stop Paying the Lazy Tax';
  let desc = hasRecent
    ? `Sign in or create a free account to follow ${recentText}, bookmark verified voucher codes in your Personal Vault, and earn monthly FitterPass Circle rewards.`
    : 'Follow UK brands with BudgetFitter Score (BFS), bookmark verified voucher codes in your Personal Vault, and join FitterPass Circle—synced across Web & App.';
  let pills = ['Personal Vault', 'FitterPass Circle', '100% Free'];
  let loginCta = 'Sign In';
  let registerCta = 'Create Free Account';
  let activePillar = 'brands';

  if (trigger === 'brand_watchlist') {
    iconKey = 'favorite';
    eyebrow = brandName ? 'PERSONAL VAULT · FOLLOW BRAND' : 'PERSONAL VAULT · BRAND TRACKING';
    title = brandName ? `Follow ${brandName} in Your Vault` : 'Follow Your Favourite Brands';
    desc = brandName
      ? `We will automatically add ${brandName} to your Personal Vault as soon as you continue, unlocking Your Brands' Deals and instant drop alerts.`
      : 'Follow your favourite UK merchants, monitor their BudgetFitter Score (BFS), and unlock your live Your Brands deal feed.';
    pills = ['Auto-Follow Ready', 'Instant Drop Alerts', '100% Free'];
    loginCta = brandName ? 'Sign In & Follow' : 'Sign In & Continue';
    registerCta = brandName ? 'Join Free & Follow' : 'Create Free Account';
    activePillar = 'brands';
  } else if (trigger === 'save_deal') {
    iconKey = 'bookmark';
    eyebrow = 'PERSONAL VAULT · BOOKMARK OFFER';
    title = brandName ? `Save This ${brandName} Perk` : 'Save This Verified Perk';
    desc = itemTitle
      ? `We will bookmark "${itemTitle}" in your Personal Vault right after you continue—auto-checked against expiry across Web & App.`
      : (brandName
          ? `Bookmark this ${brandName} offer in your Personal Vault—automatically revalidated against expiry and ready at checkout.`
          : 'Bookmark live voucher codes and flash offers in your Personal Vault so they are ready whenever you check out.');
    pills = ['Auto-Save Ready', 'Live Expiry Check', 'Web & App Sync'];
    loginCta = 'Sign In & Save Perk';
    registerCta = 'Join Free & Save Perk';
    activePillar = 'deals';
  } else if (trigger === 'save_article' || trigger === 'saved_library_pulse') {
    iconKey = 'menu_book';
    eyebrow = 'EDITORIAL VAULT · DAILY COFFEE & GUIDES';
    title = 'Save to Daily Coffee & Guides';
    desc = itemTitle
      ? `We will bookmark "${itemTitle}" inside your Personal Vault immediately after you continue.`
      : 'Bookmark weekday Daily Coffee briefings, UK consumer rights dispatches, and independent Editorial Guides across Web & Mobile App.';
    pills = ['Daily Coffee', 'Editorial Guides', 'Web & App Sync'];
    loginCta = 'Sign In & Save Guide';
    registerCta = 'Join Free & Save Guide';
    activePillar = 'guides';
  } else if (trigger === 'claim_deal_lock') {
    iconKey = 'lock_open';
    eyebrow = 'MEMBERS-ONLY PERK · INSTANT UNLOCK';
    title = brandName ? `Unlock ${brandName} Member Perk` : 'Unlock Members-Only Perk';
    desc = itemTitle
      ? `Reveal "${itemTitle}" immediately—100% free UK membership, no payment card required.`
      : 'This verified offer is reserved for BudgetFitter members. Sign in or create a free account to reveal the code immediately.';
    pills = ['Instant Code Reveal', 'Zero Cost', 'Verified UK Perk'];
    loginCta = 'Sign In & Unlock Perk';
    registerCta = 'Join Free & Unlock Perk';
    activePillar = 'deals';
    pendingGuestAction = {
      type: 'claim_deal_lock',
      brandName: brandName,
      itemTitle: itemTitle,
    };
  } else if (trigger === 'saved_library_brands') {
    iconKey = 'favorite';
    eyebrow = 'PERSONAL VAULT · FOLLOWED BRANDS';
    title = hasRecent ? `Track ${recentBrandNames[0]} in Your Vault` : 'Unlock Followed Brands';
    desc = 'Follow UK merchants with BudgetFitter Score (BFS), monitor active voucher counts, and receive instant price-drop alerts.';
    pills = ['BFS Tracking', 'Live Brand Feed', '100% Free'];
    loginCta = 'Sign In to Your Vault';
    registerCta = 'Create Free Vault Account';
    activePillar = 'brands';
  } else if (trigger === 'saved_library_brand_feed') {
    iconKey = 'vault';
    eyebrow = "LIVE DEAL STREAM · YOUR BRANDS' DEALS";
    title = "Stream Your Brands' Deals";
    desc = 'A zero-noise live feed of verified voucher codes and flash offers from every merchant you follow.';
    pills = ['Zero Noise Feed', 'Verified UK Codes', 'Instant Alerts'];
    loginCta = 'Sign In to Live Feed';
    registerCta = 'Join Free & Unlock Feed';
    activePillar = 'brands';
  } else if (trigger === 'saved_library_deals' || trigger === 'saved_hub') {
    iconKey = 'vault';
    eyebrow = 'PERSONAL VAULT · SAVED DEALS';
    title = 'Sync Your Saved Deals Vault';
    desc = 'Bookmark verified voucher codes on Web or Mobile App—automatically checked against expiry and ready at checkout.';
    pills = ['Auto Expiry Pruning', 'Web & App Sync', '100% Free'];
    loginCta = 'Sign In to Your Vault';
    registerCta = 'Create Free Vault Account';
    activePillar = 'deals';
  } else if (trigger === 'club_tab_gate') {
    iconKey = 'workspace_premium';
    eyebrow = 'FITTERPASS CIRCLE · MONTHLY STANDINGS';
    title = 'Claim Your Free FitterPass';
    desc = 'Earn points automatically when you read Daily Coffee, explore brand perks, and share tips to enter the monthly £20 Digital Gift Card standings.';
    pills = ['£20 Monthly Draw', 'Custom Handle', '100% Free'];
    loginCta = 'Sign In to FitterPass';
    registerCta = 'Claim Free FitterPass';
    activePillar = 'fitterpass';
  } else if (trigger === 'article_discussion' || trigger === 'article_comment_helpful') {
    iconKey = 'forum';
    eyebrow = 'FITTERPASS CIRCLE · READER DISCUSSION';
    title = 'Post with FitterPass Circle';
    desc = 'Sign in for 1-click verified comments, custom community avatar & handle, and +25 monthly FitterPass points on approved tips.';
    pills = ['+25 FitterPass Pts', 'Verified Badge', '100% Free'];
    loginCta = 'Sign In & Continue';
    registerCta = 'Join Free & Continue';
    activePillar = 'fitterpass';
  } else if (trigger === 'notification_matrix_brand_push') {
    iconKey = 'notifications_active';
    eyebrow = 'FOLLOWED BRAND ALERTS · INSTANT PUSH';
    title = 'Get Followed Brand Push Alerts';
    desc = 'Sign in or create a free account to follow your favourite UK brands in your Personal Vault and receive instant push notifications when they drop a new voucher code.';
    pills = ['Instant Push', 'Followed Brands', 'Zero Spam'];
    loginCta = 'Sign In & Enable Brand Push';
    registerCta = 'Join Free & Enable Brand Push';
    activePillar = 'brands';
  } else if (trigger === 'notification_matrix_email') {
    iconKey = 'mail';
    eyebrow = 'EMAIL BRIEFINGS · ZERO-NOISE MATRIX';
    title = 'Unlock Email Briefings & Alerts';
    desc = 'Sign in or create a free account to receive Followed Brand deal alerts, your weekday morning Daily Coffee briefing, and major seasonal drops in your inbox.';
    pills = ['Inbox Briefings', 'Zero Spam', '1-Tap Control'];
    loginCta = 'Sign In & Enable Emails';
    registerCta = 'Join Free & Enable Emails';
    activePillar = 'guides';
  }

  setSvgIconKey(promoIcon, 'data-promo-icon', iconKey);
  if (promoEyebrow) promoEyebrow.textContent = eyebrow;
  if (promoTitle) promoTitle.textContent = title;
  if (promoDesc) promoDesc.textContent = desc;

  setSvgIconKey(bannerIcon, 'data-auth-icon', iconKey);
  if (bannerEyebrow) bannerEyebrow.textContent = eyebrow;
  if (bannerTitle) bannerTitle.textContent = title;
  if (bannerDesc) bannerDesc.textContent = desc;
  if (bannerPills) {
    pills.forEach((pillText, idx) => {
      const pillEl = bannerPills.querySelector(`[data-auth-pill="${idx}"] .pill-label`);
      if (pillEl) pillEl.textContent = pillText;
    });
  }
  if (banner) {
    banner.classList.remove('tw-hidden');
    banner.classList.add('tw-flex');
  }

  if (loginSubmitLabel) loginSubmitLabel.textContent = loginCta;
  if (registerSubmitLabel) registerSubmitLabel.textContent = registerCta;

  const benefitList = document.getElementById('bfwt-auth-benefit-list');
  if (benefitList) {
    const items = Array.from(benefitList.querySelectorAll('[data-benefit-pillar]'));
    items.forEach((item) => {
      const isMatch = item.getAttribute('data-benefit-pillar') === activePillar;
      const badge = item.querySelector('[data-benefit-badge]');
      if (isMatch) {
        item.classList.add('tw-border-accent/50', 'tw-bg-accent/[0.04]');
        if (badge) badge.classList.remove('tw-hidden');
      } else {
        item.classList.remove('tw-border-accent/50', 'tw-bg-accent/[0.04]');
        if (badge) badge.classList.add('tw-hidden');
      }
    });
    const matchedItem = items.find((i) => i.getAttribute('data-benefit-pillar') === activePillar);
    if (matchedItem && benefitList.firstElementChild !== matchedItem) {
      benefitList.insertBefore(matchedItem, benefitList.firstElementChild);
    }
  }
}

// Helper: Open Auth (Sheet) with attribution context
function openAuthModal(viewName = 'login', context = null) {
  if (context) {
    setAuthContext(context);
  }
  updateAuthEncouragementBanner(context);
  initFirebase();
  clearError();
  showAuthView(viewName);

  const attemptOpen = () => {
    const sheet = document.getElementById('accountSheet');
    let trigger = document.getElementById('bfwt-account-trigger');
    if (!trigger) {
      trigger = document.querySelector('[data-sheet-target="accountSheet"]');
    }
    if (window.sheetManager && sheet) {
      window.sheetManager.open(sheet, trigger);
    } else if (trigger) {
      trigger.click();
    }
  };

  if (window.sheetManager) {
    attemptOpen();
  } else {
    setTimeout(attemptOpen, 150);
  }
}

// Helper: Close Auth (Sheet)
function closeAuthModal() {
  if (window.sheetManager) {
    window.sheetManager.closeAll();
  }
}

// Helper: Switch Auth Modal View
function showAuthView(viewName) {
  const titles = {
    login: 'Log In',
    register: 'Create Account',
    forgot: 'Reset Password',
    success: 'Check Email',
  };

  const titleEl = document.getElementById('bfwt-auth-title');
  if (titleEl) titleEl.textContent = titles[viewName] || 'BudgetFitter';

  // Synchronise input fields between views before switching
  const loginEmail = document.getElementById('bfwt-login-email');
  const loginPassword = document.getElementById('bfwt-login-password');
  const registerEmail = document.getElementById('bfwt-register-email');
  const registerPassword = document.getElementById('bfwt-register-password');
  const forgotEmail = document.getElementById('bfwt-forgot-email');

  if (viewName === 'register') {
    if (loginEmail && registerEmail && loginEmail.value) {
      registerEmail.value = loginEmail.value;
    }
    if (loginPassword && registerPassword && loginPassword.value) {
      registerPassword.value = loginPassword.value;
    }
  } else if (viewName === 'login') {
    if (registerEmail && loginEmail && registerEmail.value) {
      loginEmail.value = registerEmail.value;
    }
    if (registerPassword && loginPassword && registerPassword.value) {
      loginPassword.value = registerPassword.value;
    }
    if (forgotEmail && loginEmail && forgotEmail.value) {
      loginEmail.value = forgotEmail.value;
    }
  } else if (viewName === 'forgot') {
    if (loginEmail && forgotEmail && loginEmail.value) {
      forgotEmail.value = loginEmail.value;
    } else if (registerEmail && forgotEmail && registerEmail.value) {
      forgotEmail.value = registerEmail.value;
    }
  }

  document.querySelectorAll('.bfwt-auth-view').forEach((view) => {
    view.classList.add('tw-hidden');
  });

  const targetView =
    document.getElementById(`bfwt-${viewName}-form`) ||
    document.getElementById(`bfwt-auth-${viewName}`);
  targetView?.classList.remove('tw-hidden');
}

// Helper: Validate optional Display Name / Handle (2-30 chars when provided, privacy-safe)
function validateName(name, isOptional = true) {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    return isOptional
      ? { valid: true }
      : { valid: false, message: 'Please enter a display name or handle.' };
  }
  if (trimmed.length < 2 || trimmed.length > 30) {
    return {
      valid: false,
      message: 'Display Name / Handle must be between 2 and 30 characters.',
    };
  }
  if (trimmed.includes('@') && trimmed.includes('.') && !trimmed.includes(' ')) {
    return {
      valid: false,
      message: 'For your privacy, please do not use an email address as your public handle.',
    };
  }
  const handleRegex = /^[\p{L}\p{N}\s._\-']+$/u;
  if (!handleRegex.test(trimmed)) {
    return {
      valid: false,
      message:
        'Display Name / Handle can only contain letters, numbers, spaces, dots, hyphens, and underscores.',
    };
  }
  return { valid: true };
}

// ── FitterPass Web Hub Engine ──
let lastFitterPassHubData = null;
let webPageActivityTracked = false;

async function loadFitterPassHub(user) {
  const activeUser = user || currentUser;
  if (!activeUser) return;
  try {
    const idToken = await activeUser.getIdToken();
    const res = await fetch(`/wp-json/app-api/v1/fitterpass/hub?_t=${Date.now()}`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    if (!res.ok) return;
    const data = await res.json();
    lastFitterPassHubData = data;
    renderFitterPassHub(data);
  } catch (err) {
    console.warn('[FitterPass] Failed to load hub:', err);
  }
}

function renderFitterPassHub(data) {
  if (!data) return;
  const profile = data.profile || {};
  const season = data.season || lastFitterPassHubData?.season || {};
  const leaderboard = Array.isArray(data.leaderboard)
    ? data.leaderboard
    : lastFitterPassHubData?.leaderboard || [];

  const ptsMonthly = Number(profile.points_monthly || 0);
  const rank = Number(profile.rank || 0);
  const streak = Number(profile.streak_days || 0);
  const lvlNum = Number(profile.level?.level_number || 1);
  const lvlTitle = profile.level?.title || 'Curious Saver';
  const publicName = profile.public_name || profile.display_name || 'Member';
  const customHandle = profile.custom_handle || '';

  // Sidebar badge
  const sidebarPts = document.getElementById('bfwt-sidebar-fp-pts');
  if (sidebarPts) sidebarPts.textContent = `${ptsMonthly} pts`;

  // Summary card on Your Account panel
  const cardLevel = document.getElementById('bfwt-fp-card-level');
  if (cardLevel) cardLevel.textContent = `Level ${lvlNum} · ${lvlTitle}`;

  const cardMonthly = document.getElementById('bfwt-fp-card-monthly');
  if (cardMonthly) cardMonthly.textContent = `${ptsMonthly} pts`;

  const cardRank = document.getElementById('bfwt-fp-card-rank');
  if (cardRank) cardRank.textContent = rank > 0 ? `#${rank}` : '—';

  const cardStreak = document.getElementById('bfwt-fp-card-streak');
  if (cardStreak) cardStreak.textContent = `${streak}d`;

  const rewardEnabled = season.reward_enabled !== false;
  const cardRewardLine = document.getElementById('bfwt-fp-card-reward-line');
  if (cardRewardLine) {
    if (rewardEnabled && season.reward_headline) {
      const sponsorTxt = season.sponsor_name
        ? ` · Sponsored by ${season.sponsor_name}`
        : ' · Delivered via email at month-end';
      cardRewardLine.innerHTML = `Monthly #1 Reward: <strong class="tw-text-ink">${season.reward_headline}</strong>${sponsorTxt}`;
      cardRewardLine.classList.remove('tw-hidden');
    } else {
      cardRewardLine.textContent = 'Monthly Member Standings & Activity Points';
    }
  }

  // Signature Avatar preview & picker
  const avPreview = document.getElementById('bfwt-account-avatar-preview');
  const avNameEl = document.getElementById('bfwt-account-avatar-name');
  const avToggle = document.getElementById('bfwt-avatar-picker-toggle');
  const avGrid = document.getElementById('bfwt-avatar-picker-grid');
  if (avPreview && profile.avatar_svg) {
    avPreview.innerHTML = profile.avatar_svg;
  }
  if (avNameEl && profile.avatar_name) {
    avNameEl.textContent = profile.avatar_name;
  }
  if (avToggle && avGrid && !avToggle.dataset.bound) {
    avToggle.dataset.bound = '1';
    avToggle.addEventListener('click', () => {
      const isHidden = avGrid.classList.contains('tw-hidden');
      if (isHidden) {
        avGrid.classList.remove('tw-hidden');
        avGrid.classList.add('tw-grid');
        avToggle.textContent = 'Close';
      } else {
        avGrid.classList.add('tw-hidden');
        avGrid.classList.remove('tw-grid');
        avToggle.textContent = 'Change Avatar';
      }
    });
  }
  if (avGrid && Array.isArray(data.avatars)) {
    const currentAvId = profile.avatar_id || 'fox';
    avGrid.innerHTML = data.avatars
      .map((av) => {
        const selected = av.id === currentAvId;
        return `
          <button type="button" data-select-avatar="${av.id}" class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-p-2 tw-rounded-xl tw-border ${selected ? 'tw-border-ink dark:tw-border-white tw-bg-ink/[0.06] dark:tw-bg-white/[0.10]' : 'tw-border-line/40 hover:tw-border-line tw-bg-paper'} tw-cursor-pointer tw-transition-all" title="${av.name}">
            <div class="tw-w-10 tw-h-10 tw-rounded-full tw-overflow-hidden">${av.svg || ''}</div>
            <span class="tw-text-[9.5px] tw-font-heading tw-font-bold tw-text-ink tw-truncate tw-w-full tw-text-center">${av.name}</span>
            ${av.is_sponsored && av.sponsor_name ? `<span class="tw-text-[8px] tw-font-bold tw-text-accent tw-uppercase">${av.sponsor_name}</span>` : ''}
          </button>
        `;
      })
      .join('');

    avGrid.querySelectorAll('[data-select-avatar]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const chosenId = btn.getAttribute('data-select-avatar');
        if (!chosenId || !currentUser) return;
        try {
          const idToken = await currentUser.getIdToken();
          const res = await fetch('/wp-json/app-api/v1/fitterpass/profile', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ avatar_id: chosenId }),
          });
          const j = await res.json();
          if (res.ok && j.success) {
            window.BFWT_Toast?.success('Signature avatar updated!');
            loadFitterPassHub(currentUser);
            avGrid.classList.add('tw-hidden');
            avGrid.classList.remove('tw-grid');
            if (avToggle) avToggle.textContent = 'Change Avatar';
          }
        } catch (err) {
          console.warn('[FitterPass] Avatar update failed:', err);
        }
      });
    });
  }

  // Profile Details preview
  const nameLabel = document.getElementById('bfwt-account-name-label');
  if (nameLabel) {
    nameLabel.textContent = customHandle || publicName || 'Member';
  }
  const pubPreview = document.getElementById('bfwt-account-public-preview');
  if (pubPreview) {
    const lbSelfName =
      publicName === 'Member' ? 'Member (You to yourself)' : `${publicName} (You)`;
    pubPreview.innerHTML = `Leaderboard display: <strong class="tw-text-ink">${lbSelfName}</strong>`;
  }

  // Open FitterPass Hub button inside summary card
  const openHubBtn = document.getElementById('bfwt-open-fitterpass-panel-btn');
  if (openHubBtn && !openHubBtn.dataset.bound) {
    openHubBtn.dataset.bound = '1';
    openHubBtn.addEventListener('click', () => {
      const tabBtn = document.querySelector(
        '.bfwt-account-tab-btn[data-target-panel="account-panel-fitterpass"]'
      );
      if (tabBtn) tabBtn.click();
    });
  }

  // Season Hero Card & Remote Reward Toggle (reward_enabled)
  const rewardCardEl = document.getElementById('bfwt-fp-reward-card');
  if (rewardCardEl) {
    if (rewardEnabled) {
      rewardCardEl.classList.remove('tw-hidden');
      rewardCardEl.classList.add('tw-flex');
    } else {
      rewardCardEl.classList.add('tw-hidden');
      rewardCardEl.classList.remove('tw-flex');
    }
  }
  const standingsSubEl = document.getElementById('bfwt-fp-standings-subtitle');
  if (standingsSubEl) {
    standingsSubEl.textContent = rewardEnabled
      ? 'Top active members this month. The #1 eligible member receives the reward by email.'
      : 'Top active members in FitterPass Circle this month.';
  }
  const seasonIntro = document.getElementById('bfwt-fp-season-intro');
  if (seasonIntro) {
    seasonIntro.textContent = rewardEnabled
      ? season.intro || 'We reward active FitterPass members every month. Collect points by using BudgetFitter and finish #1 at month-end to claim this month’s reward.'
      : season.no_reward_intro || 'Collect FitterPass points by exploring verified perks, reading Daily Coffee, and sharing tips to climb the monthly FitterPass Circle standings.';
  }

  const seasonTitle = document.getElementById('bfwt-fp-season-title');
  if (seasonTitle && season.title) seasonTitle.textContent = season.title;

  const seasonDays = document.getElementById('bfwt-fp-season-days');
  if (seasonDays && season.days_remaining !== undefined) {
    seasonDays.textContent = `${season.days_remaining}d left`;
  }

  const rewardHead = document.getElementById('bfwt-fp-reward-headline');
  if (rewardHead && season.reward_headline) {
    rewardHead.textContent = season.reward_headline;
  }

  const rewardSub = document.getElementById('bfwt-fp-reward-subtitle');
  if (rewardSub && season.reward_subtitle) {
    rewardSub.textContent = season.reward_subtitle;
  }

  const sponsorBadge = document.getElementById('bfwt-fp-sponsor-badge');
  if (sponsorBadge) {
    if (season.sponsor_name) {
      sponsorBadge.textContent = `Sponsored by ${season.sponsor_name}`;
      sponsorBadge.classList.remove('tw-hidden');
    } else {
      sponsorBadge.classList.add('tw-hidden');
    }
  }

  const qualifyPts = Number(season.qualify_points || 200);
  const qualifyPct = Math.min(100, Math.round((ptsMonthly / Math.max(1, qualifyPts)) * 100));
  const qualifyStatus = document.getElementById('bfwt-fp-qualify-status');
  if (qualifyStatus) {
    qualifyStatus.textContent =
      ptsMonthly >= qualifyPts
        ? `Your Monthly Points: ${ptsMonthly} pts (Eligible)`
        : `Your Monthly Points: ${ptsMonthly} / ${qualifyPts} pts`;
  }
  const levelNext = document.getElementById('bfwt-fp-level-next');
  if (levelNext) {
    levelNext.textContent = rank > 0 ? `Rank #${rank}` : `${streak}d streak`;
  }
  const qualifyBar = document.getElementById('bfwt-fp-qualify-bar');
  if (qualifyBar) {
    qualifyBar.style.width = `${qualifyPct}%`;
  }

  // Server-driven ways to earn & target-specific quests (links shown ONLY when link_target is set)
  const missionsEl = document.getElementById('bfwt-fp-missions-list');
  if (missionsEl && Array.isArray(profile.missions)) {
    missionsEl.innerHTML = profile.missions
      .map((m) => {
        const done = Boolean(m.completed);
        const prog = Number(m.progress || 0);
        const targ = Number(m.target || 1);
        const pts = Number(m.points || 0);
        const lType = (m.link_type || '').trim();
        const lTarget = (m.link_target || '').trim();
        let href = '';
        if (lType === 'brand' && lTarget) {
          href = `/brand/${lTarget.replace(/^\/+|\/+$/g, '')}/`;
        } else if ((lType === 'review' || lType === 'guide') && lTarget) {
          href = `/guides/${lTarget.replace(/^\/+|\/+$/g, '')}/`;
        } else if (lType === 'digest' && lTarget) {
          href = `/digest/${lTarget.replace(/^\/+|\/+$/g, '')}/`;
        } else if ((lType === 'url' || lType === 'app' || lType === 'app_download' || lType === 'external') && lTarget) {
          href = lTarget;
        } else if (lType === 'coffee') {
          href = '/coffee/';
        }
        const isExtQuest = lType === 'url' || lType === 'app' || lType === 'app_download' || lType === 'external';
        const ctaLabel = (m.cta_label || 'Open').trim();

        return `
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-p-3.5 tw-bg-surface tw-border ${done ? 'tw-border-line/80 tw-bg-ink/[0.04] dark:tw-bg-white/[0.05]' : 'tw-border-line/40'} tw-rounded-card">
            <div class="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0 tw-flex-1">
              <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <span class="tw-text-xs tw-font-bold tw-text-ink">${m.title}</span>
                <span class="tw-px-1.5 tw-py-0.5 tw-rounded-md tw-bg-amber-500/12 tw-text-amber-600 dark:tw-text-amber-400 tw-text-[10.5px] tw-font-bold">+${pts} pts</span>
              </div>
              <p class="tw-text-[11px] tw-text-secondary tw-m-0 tw-leading-snug">${m.subtitle || ''}</p>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
              <span class="tw-text-[11px] tw-font-bold ${done ? 'tw-text-ink' : 'tw-text-secondary'}">${prog}/${targ}</span>
              ${
                !done && href
                  ? `<a href="${href}" data-fp-mission-id="${m.id || ''}" data-fp-link-type="${lType}" ${isExtQuest ? 'target="_blank" rel="noopener"' : ''} class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-lg tw-bg-ink/[0.06] dark:tw-bg-white/[0.08] hover:tw-bg-ink hover:tw-text-paper tw-text-ink tw-text-[10px] tw-font-bold tw-no-underline tw-transition-all"><span>${ctaLabel}</span><span class="material-symbols-rounded tw-text-[12px]">arrow_forward</span></a>`
                  : ''
              }
            </div>
          </div>
        `;
      })
      .join('');

    missionsEl.querySelectorAll('a[data-fp-mission-id]').forEach((aEl) => {
      aEl.addEventListener('click', () => {
        const mId = aEl.getAttribute('data-fp-mission-id') || '';
        const lType = aEl.getAttribute('data-fp-link-type') || '';
        if (mId && (mId.startsWith('quest_') || lType === 'url' || lType === 'app' || lType === 'app_download' || lType === 'external')) {
          recordWebFitterPassEvent('quest_click', mId);
        }
      });
    });
  }

  // Leaderboard list
  const lbEl = document.getElementById('bfwt-fp-leaderboard-list');
  if (lbEl) {
    if (leaderboard.length === 0) {
      lbEl.innerHTML =
        '<div class="tw-text-xs tw-text-secondary tw-py-2">Be the first member on the board this month.</div>';
    } else {
      lbEl.innerHTML = leaderboard
        .map((row) => {
          const isMe = Boolean(row.is_current_user);
          const isLeader = Boolean(row.is_prize_leader) && rewardEnabled;
          const isExempt = Boolean(row.is_exempt);
          return `
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-xl ${isMe ? 'tw-bg-ink/[0.06] dark:tw-bg-white/[0.08] tw-border tw-border-line/80' : 'tw-bg-paper tw-border tw-border-line/30'}">
              <div class="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0">
                <span class="tw-w-6 tw-text-center tw-font-heading tw-font-bold tw-text-xs ${row.rank === 1 ? 'tw-text-amber-600 dark:tw-text-amber-400' : 'tw-text-secondary'}">#${row.rank}</span>
                ${row.avatar_svg ? `<div class="tw-w-7 tw-h-7 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-border tw-border-line/40">${row.avatar_svg}</div>` : ''}
                <div class="tw-flex tw-flex-col tw-min-w-0">
                  <div class="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                    <span class="tw-text-xs tw-font-bold tw-text-ink tw-truncate">${row.display_name}</span>
                    ${isLeader ? '<span class="tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-amber-500/15 tw-text-amber-600 dark:tw-text-amber-400 tw-text-[9px] tw-font-bold tw-uppercase"><span class="material-symbols-rounded tw-text-[11px]">emoji_events</span><span>#1 Leader</span></span>' : ''}
                    ${isExempt && row.exempt_label ? `<span class="tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-amber-500/15 tw-text-amber-600 dark:tw-text-amber-400 tw-text-[9px] tw-font-bold">${row.exempt_label}</span>` : ''}
                  </div>
                  ${row.streak_days > 0 ? `<span class="tw-text-[10px] tw-text-secondary">${row.streak_days}d streak</span>` : ''}
                </div>
              </div>
              <span class="tw-font-heading tw-font-bold tw-text-xs tw-text-ink tw-shrink-0">${row.points_monthly} pts</span>
            </div>
          `;
        })
        .join('');
    }
  }
}

async function recordWebFitterPassEvent(actionType, targetId, readToken = '') {
  if (!currentUser) return;
  try {
    const idToken = await currentUser.getIdToken();
    const res = await fetch('/wp-json/app-api/v1/fitterpass/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        action_type: actionType,
        target_id: targetId || '',
        read_token: readToken || '',
      }),
    });
    if (!res.ok) return;
    const json = await res.json();
    if (json && json.profile) {
      renderFitterPassHub(json);
    }
    if (json && json.awarded && Number(json.points_added) > 0 && window.BFWT_Toast) {
      window.BFWT_Toast.success(
        `<strong>+${json.points_added} FitterPass Points</strong> — ${json.message || 'Activity recorded.'}`
      );
    }
  } catch (err) {
    console.warn('[FitterPass] Event error:', err);
  }
}

async function initWebFitterPassActivityTracking(user) {
  if (webPageActivityTracked || !user) return;
  const path = window.location.pathname || '';
  let actionType = '';
  let targetId = '';

  if (path.startsWith('/coffee')) {
    actionType = 'daily_coffee';
    targetId = new Date().toISOString().slice(0, 10);
  } else if (path.startsWith('/brand/') || path.startsWith('/listing/')) {
    const parts = path.split('/').filter(Boolean);
    if (parts.length >= 2) {
      actionType = 'perk_view';
      targetId = parts[1];
    }
  }

  if (!actionType || !targetId) return;
  webPageActivityTracked = true;

  try {
    const idToken = await user.getIdToken();
    const tokRes = await fetch('/wp-json/app-api/v1/fitterpass/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        action_type: actionType,
        target_id: targetId,
      }),
    });
    if (!tokRes.ok) return;
    const tokJson = await tokRes.json();
    if (!tokJson || !tokJson.read_token) return;

    const minDwellMs = Math.max(4000, Number(tokJson.min_dwell_seconds || 4) * 1000 + 500);
    setTimeout(() => {
      if (document.visibilityState === 'visible') {
        recordWebFitterPassEvent(actionType, tokJson.target_id || targetId, tokJson.read_token);
      }
    }, minDwellMs);
  } catch (_) {}
}

// Helpers: Error display
function showError(msg) {
  const errEl = document.getElementById('bfwt-auth-error');
  if (errEl) {
    errEl.querySelector('.error-message').textContent = msg;
    errEl.classList.replace('tw-hidden', 'tw-flex');
  }
}

// Helpers: Clear errors
function clearError() {
  const errEl = document.getElementById('bfwt-auth-error');
  if (errEl) {
    errEl.classList.replace('tw-flex', 'tw-hidden');
  }
}

// Helpers: Success display
function showSuccess(msg) {
  showAuthView('success');
  const successEl = document.getElementById('bfwt-auth-success');
  if (successEl) {
    successEl.querySelector('.success-message').textContent = msg;
  }
}

// Translate Firebase Error Codes
function translateAuthError(code) {
  switch (code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email address or password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account already exists with this email address.';
    case 'auth/weak-password':
      return 'Password is too weak. Please choose at least 6 characters.';
    case 'auth/popup-closed-by-user':
      return 'Sign in was cancelled. Please try again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

// ── Watchlist Feed Dynamic Loader functions ──
let yourBrandsLoadedIds = '';

async function loadYourBrandsGrid(force = false) {
  const wrapper = document.getElementById('bfwt-your-brands-wrapper');
  if (!wrapper) return;

  const idsArray = Array.from(userFollowedBrandIds);
  const idsString = idsArray.join(',');

  if (idsArray.length === 0) {
    wrapper
      .querySelector('.bfwt-your-brands-skeletons')
      ?.classList.add('tw-hidden');
    document
      .getElementById('bfwt-your-brands-guest-promo')
      ?.classList.add('tw-hidden');
    document
      .getElementById('bfwt-your-brands-empty-state')
      ?.classList.remove('tw-hidden');
    yourBrandsLoadedIds = '';
    return;
  }

  if (!force && yourBrandsLoadedIds === idsString) {
    return;
  }

  wrapper
    .querySelector('.bfwt-your-brands-skeletons')
    ?.classList.remove('tw-hidden');
  document
    .getElementById('bfwt-your-brands-guest-promo')
    ?.classList.add('tw-hidden');
  document
    .getElementById('bfwt-your-brands-empty-state')
    ?.classList.add('tw-hidden');

  const currentParams = new URLSearchParams(window.location.search);
  currentParams.set('post__in', idsString);
  const url = `/collection/your-brands/?${currentParams.toString()}`;
  try {
    const response = await fetch(url, {
      headers: { 'X-PJAX': '1' },
    });
    if (!response.ok) throw new Error('Network response was not ok');
    const html = await response.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newContainer = doc.querySelector('.bfwt-pjax-container');
    if (newContainer) {
      wrapper.innerHTML = newContainer.outerHTML;
      yourBrandsLoadedIds = idsString;

      // Re-bind standard PJAX handlers to the dynamically inserted container
      const insertedContainer = wrapper.querySelector('.bfwt-pjax-container');
      if (
        insertedContainer &&
        typeof window.bfwtInitPjaxContainer === 'function'
      ) {
        window.bfwtInitPjaxContainer(insertedContainer);
      }

      syncWatchlistButtonsState();
    } else {
      console.error('Grid container not found in response');
    }
  } catch (err) {
    console.error('Failed to load followed brands grid:', err);
  }
}

// Listen for standard PJAX loads and sync the watchlist & saved buttons state when navigation occurs
document.addEventListener('bfwt:pjax:load', (e) => {
  if (e.detail && e.detail.containerId === 'discover-bar') {
    syncWatchlistButtonsState();
  }
  syncSavedDealsUI();
  syncSavedArticlesUI();
});

// ── Password Reset Form Handler (Verify Custom Landing Page) ──
document.addEventListener('DOMContentLoaded', () => {
  initPasswordResetForm();
});
window.addEventListener('load', initPasswordResetForm);

async function initPasswordResetForm() {
  const resetPasswordForm = document.getElementById('bfwt-reset-password-form');
  if (!resetPasswordForm || resetPasswordForm.dataset.initialized === 'true')
    {return;}
  resetPasswordForm.dataset.initialized = 'true';

  initFirebase();

  const oobCode = document.getElementById('bfwt-reset-oobcode').value;
  const errorDiv = document.getElementById('bfwt-reset-error');
  const successDiv = document.getElementById('bfwt-reset-success');
  const submitBtn = document.getElementById('bfwt-reset-submit-btn');

  // Pre-validate the reset code on load
  try {
    let checkCount = 0;
    while (!auth && checkCount < 20) {
      await new Promise((r) => setTimeout(r, 100));
      checkCount++;
    }
    if (!auth) throw new Error('Firebase Auth failed to initialize.');

    const email = await verifyPasswordResetCode(auth, oobCode);
    const emailLabel = document.getElementById('bfwt-reset-email-display');
    if (emailLabel) {
      emailLabel.textContent = `for ${email}`;
    }
  } catch (err) {
    console.error('Invalid or expired password reset code:', err);
    errorDiv.textContent =
      'This password reset link is invalid, expired, or has already been used.';
    errorDiv.classList.remove('tw-hidden');
    submitBtn.disabled = true;
    document
      .querySelectorAll('.bfwt-reset-field-group')
      .forEach((el) => el.classList.add('tw-hidden'));
    return;
  }

  resetPasswordForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorDiv.classList.add('tw-hidden');
    successDiv.classList.add('tw-hidden');

    const password = document.getElementById('bfwt-reset-password').value;
    const confirmPassword = document.getElementById(
      'bfwt-reset-password-confirm'
    ).value;

    if (password !== confirmPassword) {
      errorDiv.textContent = 'Passwords do not match.';
      errorDiv.classList.remove('tw-hidden');
      return;
    }

    try {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span>Updating...</span>';

      await confirmPasswordReset(auth, oobCode, password);

      successDiv.innerHTML =
        'Your password has been successfully reset. <a href="/" id="bfwt-continue-login-link" style="color: #04875D; font-weight: 600; text-decoration: underline; margin-left: 4px;">Continue to Login</a>';
      successDiv.classList.remove('tw-hidden');
      
      const continueLink = document.getElementById('bfwt-continue-login-link');
      if (continueLink) {
        continueLink.addEventListener('click', () => {
          try {
            sessionStorage.setItem('bfwt_open_account_hub', 'true');
          } catch (e) {}
        });
      }

      // Hide the input fields
      document
        .querySelectorAll('.bfwt-reset-field-group')
        .forEach((el) => el.classList.add('tw-hidden'));

      resetPasswordForm.reset();

      // Replace the button with a navigation button
      const continueBtn = submitBtn.cloneNode(true);
      continueBtn.innerHTML = '<span>CONTINUE TO LOGIN</span>';
      continueBtn.disabled = false;
      submitBtn.parentNode.replaceChild(continueBtn, submitBtn);

      continueBtn.addEventListener('click', (e) => {
        e.preventDefault();
        try {
          sessionStorage.setItem('bfwt_open_account_hub', 'true');
        } catch (e) {}
        window.location.href = '/';
      });
    } catch (err) {
      console.error('Password reset failed:', err);
      errorDiv.textContent =
        translateAuthError(err.code) ||
        'Failed to reset password. The link may have expired.';
      errorDiv.classList.remove('tw-hidden');
      submitBtn.disabled = false;
      submitBtn.innerHTML = '<span>UPDATE PASSWORD</span>';
    }
  });
}

// Export Global API
window.BFWT_Membership = {
  getCurrentUser: () => currentUser,
  getCurrentToken: () => currentToken,
  openAuthModal: openAuthModal,
  closeAuthModal: closeAuthModal,
  logout: logoutHandler,
  openPanel: (panelId) => {
    const sheet = document.getElementById('accountSheet');
    if (sheet && window.sheetManager) {
      window.sheetManager.open(sheet);
      const btn = document.querySelector(
        `.bfwt-account-tab-btn[data-target-panel="${panelId}"]`
      );
      if (btn) btn.click();
    }
  },
};


let latestUserFirestoreData = null;

function updateChannelPillButtonsUI(stateMap) {
  Object.entries(stateMap || {}).forEach(([key, isEnabled]) => {
    const isPush = key.endsWith('-push');
    document.querySelectorAll(`[data-bfwt-channel-pill="${key}"]`).forEach((btn) => {
      btn.setAttribute('aria-pressed', isEnabled ? 'true' : 'false');
      if (isEnabled) {
        btn.classList.add('tw-border-accent/45', 'tw-bg-accent/15', 'tw-text-accent');
        btn.classList.remove('tw-border-line/60', 'tw-bg-ink/[0.03]', 'tw-text-secondary');
      } else {
        btn.classList.remove('tw-border-accent/45', 'tw-bg-accent/15', 'tw-text-accent');
        btn.classList.add('tw-border-line/60', 'tw-bg-ink/[0.03]', 'tw-text-secondary');
      }
      const iconEl = btn.querySelector('.bfwt-pill-icon') || btn.querySelector('.material-symbols-rounded');
      if (iconEl) {
        iconEl.textContent = isPush
          ? (isEnabled ? 'notifications_active' : 'notifications_off')
          : (isEnabled ? 'mark_email_read' : 'mail_off');
      }
    });
  });
}

function syncCommunicationNudges(user, userData) {
  if (userData) {
    latestUserFirestoreData = userData;
  }
  const data = userData || latestUserFirestoreData || {};
  const slots = document.querySelectorAll('[data-bfwt-comm-nudge-slot]');
  if (!slots.length) return;

  if (!user) {
    slots.forEach((slot) => {
      slot.innerHTML = '';
      slot.classList.add('tw-hidden');
    });
    return;
  }

  const isPasswordUser = Array.isArray(user.providerData) && user.providerData.some((p) => p.providerId === 'password');
  const isUnverified = isPasswordUser && !user.emailVerified;
  const pushPrefs = data.notification_preferences_v2 || {};
  const browserPermGranted = ('Notification' in window) && Notification.permission === 'granted';
  const browserPermDenied = ('Notification' in window) && Notification.permission === 'denied';
  const masterPush = pushPrefs.web_enabled !== undefined ? pushPrefs.web_enabled !== false : pushPrefs.enabled !== false;

  const dealPush = browserPermGranted && masterPush && pushPrefs.deal_push_enabled !== false;
  const coffeePush = browserPermGranted && masterPush && (pushPrefs.coffee_push_enabled !== undefined ? pushPrefs.coffee_push_enabled !== false : pushPrefs.editorial_push_enabled !== false);
  const editorialPush = browserPermGranted && masterPush && pushPrefs.editorial_push_enabled !== false;
  const hasPushGap = !browserPermGranted || !dealPush || !coffeePush || !editorialPush;

  const isSub = data.newsletter_subscribed !== false;
  const dealEmail = pushPrefs.deal_email_enabled !== false;
  const coffeeEmail = pushPrefs.coffee_email_enabled !== false;
  const editorialEmail = isSub;
  const hasEmailGap = !dealEmail || !coffeeEmail || !editorialEmail;

  const pushDismissed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('bfwt_nudge_push_dismissed') === '1';
  const emailDismissed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('bfwt_nudge_email_dismissed') === '1';

  slots.forEach((slot) => {
    const slotType = slot.getAttribute('data-bfwt-comm-nudge-slot') || 'account';
    const isPersistent = slot.getAttribute('data-persistent-nudge') === '1';
    const preferBrandNudge = slotType === 'vault' || slotType === 'your-brands';

    // In Vault, show unverified email alert if unverified (Account panel already has #bfwt-verification-alert)
    if (slotType === 'vault' && isUnverified) {
      slot.classList.remove('tw-hidden');
      slot.innerHTML = `
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3.5 tw-p-4 tw-bg-amber-500/10 tw-border tw-border-amber-500/30 tw-rounded-2xl tw-text-amber-700 dark:tw-text-amber-400">
          <div class="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
            <span class="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-amber-500/15 tw-border tw-border-amber-500/30 tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <span class="material-symbols-rounded tw-text-[19px] tw-text-amber-500">mark_email_unread</span>
            </span>
            <div class="tw-flex tw-flex-col tw-gap-0.5">
              <strong class="tw-font-heading tw-text-xs sm:tw-text-[13px] tw-font-bold tw-text-ink">Verify Your Email Address</strong>
              <span class="tw-text-[11.5px] tw-text-secondary">Confirm your inbox to activate instant deal alerts &amp; cross-device Vault sync.</span>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
            <button type="button" data-bfwt-nudge-action="check-verify" class="tw-px-3 tw-py-1.5 tw-rounded-full tw-bg-transparent hover:tw-bg-amber-500/15 tw-border tw-border-amber-500/30 tw-text-[11px] tw-font-heading tw-font-bold tw-text-amber-600 dark:tw-text-amber-400 tw-cursor-pointer tw-transition-colors">I've Verified</button>
            <button type="button" data-bfwt-nudge-action="resend-verify" class="tw-px-3.5 tw-py-1.5 tw-rounded-full tw-bg-amber-500 hover:tw-bg-amber-400 tw-border-0 tw-text-[11px] tw-font-heading tw-font-bold tw-text-black tw-cursor-pointer tw-transition-colors">Resend Link</button>
          </div>
        </div>
      `;
      return;
    }

    if (hasPushGap && (isPersistent || !pushDismissed) && ('Notification' in window) && !browserPermDenied) {
      let pushTitle = 'Turn On Instant Push Alerts';
      let pushSubtitle = 'Get notified when followed brands drop new offers or Daily Coffee goes live.';
      let ctaLabel = 'Turn On';

      if (!browserPermGranted) {
        pushTitle = preferBrandNudge ? 'Turn On Followed Brand Alerts' : 'Enable Browser Push Alerts';
        pushSubtitle = preferBrandNudge
          ? 'Allow browser notifications so we can alert you when your followed brands drop offers.'
          : 'Allow notifications for followed brand deals, Daily Coffee & major drops.';
        ctaLabel = 'Allow';
      } else {
        const offCount = (!dealPush ? 1 : 0) + (!coffeePush ? 1 : 0) + (!editorialPush ? 1 : 0);
        if (offCount === 1 && !dealPush) {
          pushTitle = 'Followed Brand Push Is Off';
          pushSubtitle = 'Turn on Followed Brand Deals push to get instant alerts when your saved brands drop new codes.';
        } else if (offCount === 1 && !coffeePush) {
          pushTitle = 'Daily Coffee Push Is Off';
          pushSubtitle = 'Turn on Daily Coffee push for your weekday morning UK savings briefing.';
        } else if (offCount === 1 && !editorialPush) {
          pushTitle = 'Major Drops Push Is Off';
          pushSubtitle = 'Turn on Digests & Major Drops push for seasonal events and top UK roundups.';
        } else {
          pushTitle = 'Push Alerts Are Paused';
          pushSubtitle = 'Re-enable all push channels so you never miss followed brand drops or Daily Coffee.';
          ctaLabel = 'Enable All';
        }
      }

      slot.classList.remove('tw-hidden');
      slot.innerHTML = `
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3.5 tw-p-4 tw-bg-accent/[0.06] tw-border tw-border-accent/25 tw-rounded-2xl">
          <div class="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
            <span class="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-accent/15 tw-border tw-border-accent/30 tw-text-accent tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <span class="material-symbols-rounded tw-text-[19px]">notifications_active</span>
            </span>
            <div class="tw-flex tw-flex-col tw-gap-0.5">
              <strong class="tw-font-heading tw-text-xs sm:tw-text-[13px] tw-font-bold tw-text-ink">${pushTitle}</strong>
              <span class="tw-text-[11.5px] tw-text-secondary">${pushSubtitle}</span>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
            <button type="button" data-bfwt-nudge-action="enable-push" class="tw-px-3.5 tw-py-1.5 tw-rounded-full tw-bg-accent hover:tw-bg-accent/90 tw-border-0 tw-text-[11px] tw-font-heading tw-font-bold tw-text-cream tw-cursor-pointer tw-transition-colors">${ctaLabel}</button>
            ${isPersistent ? '' : `<button type="button" data-bfwt-nudge-action="dismiss-push" aria-label="Dismiss" class="tw-w-7 tw-h-7 tw-rounded-full tw-bg-transparent hover:tw-bg-ink/[0.06] tw-border-0 tw-text-secondary hover:tw-text-ink tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors"><span class="material-symbols-rounded tw-text-[16px]">close</span></button>`}
          </div>
        </div>
      `;
      return;
    }

    if (hasEmailGap && (isPersistent || !emailDismissed)) {
      const offCount = (!dealEmail ? 1 : 0) + (!coffeeEmail ? 1 : 0) + (!editorialEmail ? 1 : 0);
      let emailTitle = 'Email Briefings Are Paused';
      let emailSubtitle = 'Turn on followed brand deal emails, Daily Coffee & weekly digests in one click.';
      if (offCount === 1 && !dealEmail) {
        emailTitle = 'Followed Brand Emails Are Off';
        emailSubtitle = 'Enable email alerts for new offers and discount codes from brands you follow.';
      } else if (offCount === 1 && !coffeeEmail) {
        emailTitle = 'Daily Coffee Email Is Off';
        emailSubtitle = 'Receive your weekday morning UK consumer news and savings briefing by email.';
      } else if (offCount === 1 && !editorialEmail) {
        emailTitle = 'Weekly Digest Emails Are Off';
        emailSubtitle = 'Turn on weekly savings roundups and major seasonal event alerts in your inbox.';
      }

      slot.classList.remove('tw-hidden');
      slot.innerHTML = `
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3.5 tw-p-4 tw-bg-accent/[0.06] tw-border tw-border-accent/25 tw-rounded-2xl">
          <div class="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
            <span class="tw-w-9 tw-h-9 tw-rounded-xl tw-bg-accent/15 tw-border tw-border-accent/30 tw-text-accent tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <span class="material-symbols-rounded tw-text-[19px]">mail</span>
            </span>
            <div class="tw-flex tw-flex-col tw-gap-0.5">
              <strong class="tw-font-heading tw-text-xs sm:tw-text-[13px] tw-font-bold tw-text-ink">${emailTitle}</strong>
              <span class="tw-text-[11.5px] tw-text-secondary">${emailSubtitle}</span>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
            <button type="button" data-bfwt-nudge-action="enable-email" class="tw-px-3.5 tw-py-1.5 tw-rounded-full tw-bg-accent hover:tw-bg-accent/90 tw-border-0 tw-text-[11px] tw-font-heading tw-font-bold tw-text-cream tw-cursor-pointer tw-transition-colors">Turn On</button>
            ${isPersistent ? '' : `<button type="button" data-bfwt-nudge-action="dismiss-email" aria-label="Dismiss" class="tw-w-7 tw-h-7 tw-rounded-full tw-bg-transparent hover:tw-bg-ink/[0.06] tw-border-0 tw-text-secondary hover:tw-text-ink tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors"><span class="material-symbols-rounded tw-text-[16px]">close</span></button>`}
          </div>
        </div>
      `;
      return;
    }

    slot.innerHTML = '';
    slot.classList.add('tw-hidden');
  });
}

async function reloadAndSyncAuthIdentity(silent = true) {
  if (!auth || !auth.currentUser) return false;
  try {
    const prevVerified = !!auth.currentUser.emailVerified;
    const prevEmail = (auth.currentUser.email || '').trim().toLowerCase();
    await auth.currentUser.reload();
    currentUser = auth.currentUser;
    const nowVerified = !!currentUser.emailVerified;
    const nowEmail = (currentUser.email || '').trim().toLowerCase();

    if (nowVerified !== prevVerified || (nowEmail && nowEmail !== prevEmail)) {
      const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
      const updates = { email_verified: nowVerified };
      if (nowEmail) {
        updates.email = nowEmail;
      }
      await setDoc(userDocRef, updates, { merge: true });
      await handleAuthStateChanged(currentUser);
      if (nowVerified && !prevVerified && window.BFWT_Toast) {
        window.BFWT_Toast.success('Email verified! Your account & alerts are now active.');
      }
      return true;
    } else if (!silent && window.BFWT_Toast) {
      if (nowVerified) {
        window.BFWT_Toast.success('Your email address is verified.');
      } else {
        window.BFWT_Toast.info('Not verified yet — please click the verification link in your inbox.');
      }
    }
    return nowVerified;
  } catch (err) {
    console.warn('[Firebase] reloadAndSyncAuthIdentity warning:', err);
    return false;
  }
}

window.addEventListener('focus', () => {
  if (currentUser && !currentUser.emailVerified) {
    reloadAndSyncAuthIdentity(true);
  }
});

document.addEventListener('click', async (e) => {
  const pillBtn = e.target.closest('[data-bfwt-channel-pill]');
  if (pillBtn) {
    e.preventDefault();
    const channelKey = pillBtn.getAttribute('data-bfwt-channel-pill');
    if (!currentUser) {
      if (channelKey === 'deal-push') {
        openAuthModal('register', { trigger: 'notification_matrix_brand_push' });
        return;
      }
      if (channelKey.endsWith('-email')) {
        openAuthModal('register', { trigger: 'notification_matrix_email' });
        return;
      }
      // Public push channels (coffee-push / editorial-push) for guests:
      const currentlyPressed = pillBtn.getAttribute('aria-pressed') === 'true';
      const targetVal = !currentlyPressed;
      if (targetVal) {
        const granted = await _ensureWebPushPermissionAndToken();
        if (!granted && ('Notification' in window) && Notification.permission !== 'granted') {
          if (window.BFWT_Toast) {
            window.BFWT_Toast.error('Please allow browser notifications to enable Push alerts.');
          }
          return;
        }
      }
      updateChannelPillButtonsUI({ [channelKey]: targetVal });
      if (window.BFWT_Toast) {
        window.BFWT_Toast.success(targetVal ? 'Push channel enabled.' : 'Push channel muted.');
      }
      return;
    }
    const currentlyPressed = pillBtn.getAttribute('aria-pressed') === 'true';
    const targetVal = !currentlyPressed;
    pillBtn.disabled = true;
    try {
      if (channelKey.endsWith('-push') && targetVal) {
        const granted = await _ensureWebPushPermissionAndToken();
        if (!granted && ('Notification' in window) && Notification.permission !== 'granted') {
          if (window.BFWT_Toast) {
            window.BFWT_Toast.error('Please allow browser notifications to enable Push alerts.');
          }
          return;
        }
      }
      const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
      const prefsUpdate = { last_updated: new Date().toISOString() };
      const topUpdate = { notification_preferences_v2: prefsUpdate };

      if (channelKey === 'deal-push') {
        prefsUpdate.deal_push_enabled = targetVal;
        if (targetVal) { prefsUpdate.web_enabled = true; prefsUpdate.enabled = true; }
      } else if (channelKey === 'coffee-push') {
        prefsUpdate.coffee_push_enabled = targetVal;
        if (targetVal) { prefsUpdate.web_enabled = true; prefsUpdate.enabled = true; }
      } else if (channelKey === 'editorial-push') {
        prefsUpdate.editorial_push_enabled = targetVal;
        if (targetVal) { prefsUpdate.web_enabled = true; prefsUpdate.enabled = true; }
      } else if (channelKey === 'deal-email') {
        prefsUpdate.deal_email_enabled = targetVal;
      } else if (channelKey === 'coffee-email') {
        prefsUpdate.coffee_email_enabled = targetVal;
      } else if (channelKey === 'editorial-email') {
        topUpdate.newsletter_subscribed = targetVal;
        prefsUpdate.newsletter_enabled = targetVal;
      }

      updateChannelPillButtonsUI({ [channelKey]: targetVal });
      await setDoc(userDocRef, topUpdate, { merge: true });
      if (window.BFWT_Toast) {
        window.BFWT_Toast.success(targetVal ? 'Channel enabled.' : 'Channel muted.');
      }
    } catch (err) {
      console.error('Failed to toggle channel pill:', err);
      updateChannelPillButtonsUI({ [channelKey]: currentlyPressed });
    } finally {
      pillBtn.disabled = false;
    }
    return;
  }

  const actionBtn = e.target.closest('[data-bfwt-nudge-action]');
  if (!actionBtn) return;
  e.preventDefault();
  const action = actionBtn.getAttribute('data-bfwt-nudge-action');

  if (action === 'dismiss-push') {
    try { sessionStorage.setItem('bfwt_nudge_push_dismissed', '1'); } catch (_) {}
    syncCommunicationNudges(currentUser, latestUserFirestoreData);
  } else if (action === 'dismiss-email') {
    try { sessionStorage.setItem('bfwt_nudge_email_dismissed', '1'); } catch (_) {}
    syncCommunicationNudges(currentUser, latestUserFirestoreData);
  } else if (action === 'check-verify') {
    actionBtn.disabled = true;
    await reloadAndSyncAuthIdentity(false);
    actionBtn.disabled = false;
  } else if (action === 'resend-verify') {
    const resendBtn = document.getElementById('bfwt-resend-verification-btn');
    if (resendBtn) {
      resendBtn.click();
    }
  } else if (action === 'enable-push') {
    if (!currentUser) return;
    actionBtn.disabled = true;
    try {
      await _ensureWebPushPermissionAndToken();
      const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
      await setDoc(
        userDocRef,
        {
          notification_preferences_v2: {
            web_enabled: true,
            enabled: true,
            deal_push_enabled: true,
            coffee_push_enabled: true,
            editorial_push_enabled: true,
            last_updated: new Date().toISOString(),
          },
        },
        { merge: true }
      );
      if (window.BFWT_Toast) {
        window.BFWT_Toast.success('All push notification channels enabled!');
      }
    } finally {
      actionBtn.disabled = false;
    }
  } else if (action === 'enable-email') {
    if (!currentUser) return;
    actionBtn.disabled = true;
    try {
      const userDocRef = doc(db, USERS_COLLECTION, currentUser.uid);
      await setDoc(
        userDocRef,
        {
          newsletter_subscribed: true,
          notification_preferences_v2: {
            newsletter_enabled: true,
            deal_email_enabled: true,
            coffee_email_enabled: true,
            last_updated: new Date().toISOString(),
          },
        },
        { merge: true }
      );
      if (window.BFWT_Toast) {
        window.BFWT_Toast.success('All email channels enabled!');
      }
    } finally {
      actionBtn.disabled = false;
    }
  }
});
